import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@env/environment';
import { AuthService, emitSubscribeEntities, EntityService, getNotificationSocketConnection, SharedService } from '@app/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';
import { PurchaseManagerAnalyticsService } from '../../analytics/services/purchase-manager-analytics.service';
import { AddFavProductPayload, buildFavProductKey, FavProductKey, PmFavProduct, RemoveFavProductPayload } from '../../models/pm-fav-product.model';
import { BulkPrice, CartUpdateEvent, MarketBestPrice, SearchItem, SupplierProduct, SupplierSection } from '../../models/supplier.models';
import { priceDiscoveryTagsNavigationState, readTrustedPriceDiscoveryTags, rememberPriceDiscoveryTags } from '../../helpers/price-discovery-tags';
import { decodePmSearchTag, encodePmSearchTag, HyperpureSheetSearchRow, NAME_MATCH_THRESHOLD, nameSimilarity, normalizeUnit, readCartQuantities } from '../../services/hyperpure-sheet-cart.service';
import { applyHyperpureInclusiveTaxToItems, areAllExternalSuppliersDisabled, DEMO_DEFAULT_SEARCH_ITEMS, getHyperpureTaxInclusiveRate, PmConnectFormCityOption, PmConnectLeadPayload, PriceDiscoveryService } from '../../services/price-discovery.service';
import { PmDemoModeService } from '../../services/pm-demo-mode.service';
import { PurchaseOrderService } from '../../services/purchase-order.service';
import { SupplierRegistryService } from '../../services/supplier-registry.service';
import { ConnectLeadModalComponent } from '../connect-lead-modal/connect-lead-modal.component';
import { DeliveritSupplierComponent } from '../deliverit-supplier/deliverit-supplier.component';
import { DmartSupplierComponent } from '../dmart-supplier/dmart-supplier.component';
import { HyperpureSupplierComponent } from '../hyperpure-supplier/hyperpure-supplier.component';
import { LocalSupplierPurchaseOrderModalComponent } from '../local-supplier-create-po/local-supplier-purchase-order-modal.component';
import { SwiggyAssureSupplierComponent } from '../swiggy-assure-supplier/swiggy-assure-supplier.component';
import { UdaanSupplierComponent } from '../udaan-supplier/udaan-supplier.component';

// Interfaces are now imported from '../../models/supplier.models'

interface BestMatchCandidate {
    searchItemName: string;
    platform: string;
    platformLabel: string;
    item: SupplierProduct;
    isSelected: boolean;
    isAlreadyAdded: boolean;
    isAlreadyInCart: boolean;
    existingCartQty: number;
    minimumOrderQty: number;
    quantity: number;
}

/**
 * Cached search state per entity — preserves search items and supplier results
 * so they can be restored when the user switches back to a previously visited entity.
 */
interface EntitySearchCache {
    searchItems: SearchItem[];
    activeSearchItem: string;
    supplierSections: SupplierSection[];
    marketBestPrice: MarketBestPrice | null;
    marketBestItemSkus: Set<string>;
    marketBestItemsSnapshot: { platform: string; skuId: string; previousQty: number }[];
    hasMarketBestItems: boolean;
    hasSupplierPreferrence: boolean;
}

@Component({
    selector: 'app-pm-price-discovery',
    templateUrl: './pm-price-discovery.component.html',
    styleUrls: ['./pm-price-discovery.component.scss']
})
export class PmPriceDiscoveryComponent implements OnInit, OnDestroy, AfterViewInit {

    // Entity
    selectedEntityId = '';
    private previousEntity: any = null; // Stores the current entity object for revert on cancel
    private entitySearchCache = new Map<string, EntitySearchCache>(); // Per-entity search state cache

    // Search Items (left sidebar)
    searchItems: SearchItem[] = [];
    sheetCartNote = '';
    private sheetNoteRun = 0;
    sheetAgentOpen = false;
    sheetAgentRun = 0;
    private sheetCartApplyInFlight = false;
    activeSearchItem: string = ''; // Currently selected search item tab
    newItemSearchText: string = ''; // Model for the new search input box

    // Quick Add
    quickAddCategories: { label: string; icon: string; key: string; active: boolean; addedItems: string[] }[] = [
        // { label: 'Recent Purchased', icon: 'schedule', key: 'recentPurchase', active: false, addedItems: [] },
        // { label: 'Most Purchased', icon: 'shopping_cart', key: 'mostPurchased', active: false, addedItems: [] },
        { label: 'Recent Searched', icon: 'history', key: 'recentSearched', active: false, addedItems: [] },
        { label: 'Most Searched', icon: 'search', key: 'mostSearched', active: false, addedItems: [] },
        // { label: 'Top 10 Ordered', icon: 'trending_up', key: 'topOrdered', active: false, addedItems: [] }
    ];
    isQuickAddLoading = false;
    isQuickAddOpen = false;

    // Supplier sections
    supplierSections: SupplierSection[] = [];

    // Selected card tracking
    selectedCard: SupplierProduct | null = null;
    allSuppliers: any[] = []; // Stores full supplier list for PO mapping

    // Market best price (top banner)
    marketBestPrice: MarketBestPrice | null = null;

    // Loading states
    isLoading = false;
    isInitializing = true;

    /**
     * Checks if the currently active search item is still loading its results.
     */
    get isActiveItemLoading(): boolean {
        if (!this.activeSearchItem) return this.isInitializing;
        const activeItem = this.searchItems.find(item => item.name === this.activeSearchItem);
        return activeItem ? activeItem.isLoading || false : false;
    }

    /**
     * Returns true if any price discovery search is currently in progress.
     * Checks socket listeners, individual item loading states, and general loading flag.
     */
    get isSearchInProgress(): boolean {
        return this.isLoading
            || this.socketListeners.size > 0
            || this.searchItems.some(item => item.isLoading);
    }

    // Drawer state
    isDrawerOpen = false;
    selectedSection: SupplierSection | null = null;
    drawerSearchText = '';
    drawerSortOption = 'relevance';

    // Checkout Drawer state
    isCheckoutDrawerOpen = false;
    selectedCheckoutCart: any = null;

    showSwiggyIframe = false;
    swiggyIframeUrlSafe: SafeResourceUrl | null = null;
    private readonly swiggyCartUrl = environment.swiggyAssureIframeUrl;
    private readonly swiggyAssureOrigin = environment.swiggyAssureOrigin;
    private readonly swiggyChildCloseTopic = 'swiggy_frame_close';
    private swiggyFrameLoadPayload: {
        Topic: string;
        Data: {
            tid: string;
            rid: string;
            storeId: string;
            email: string;
            phone: string;
            sid: string;
            token: string;
            source: string;
        };
    } | null = null;
    swiggyCheckoutLoading = false;
    private swiggyIframeLoadCount = 0;
    private swiggyInboundCount = 0;
    private swiggyOriginInboundCount = 0;
    private swiggyReplyTimer: ReturnType<typeof setTimeout> | null = null;
    private swiggyHandshakeTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly onSwiggyWindowMessage = (event: MessageEvent) => this.logSwiggyInboundMessage(event);
    private readonly onSwiggySecurityViolation = (event: SecurityPolicyViolationEvent) => this.logSwiggySecurityViolation(event);

    // HP Recent Purchases
    showHpRecentPurchasesModal = false;
    hpRecentPurchaseLiveItems: any[] = [];
    hpRecentPurchaseMissingItems: any[] = [];
    hpRecentPurchaseTotalCount = 0;
    hpRecentPurchaseLiveCount = 0;
    hpRecentPurchaseMissingCount = 0;
    isHpRecentPurchasesLoading = false;
    searchHpRecentPurchaseTerm = '';

    // Inventory PR
    showInventoryPrModal = false;

    /**
     * Returns a list of supplier products filtered by search text and sorted based on the selected drawer sort option.
     */
    get drawerFilteredItems(): SupplierProduct[] {
        if (!this.selectedSection) return [];

        let items = this.getItemsForActiveSearch(this.selectedSection);

        // Filter by search text
        if (this.drawerSearchText) {
            const query = this.drawerSearchText.toLowerCase();
            items = items.filter(item =>
                item.item_name.toLowerCase().includes(query) ||
                (item.sku_id && item.sku_id.toLowerCase().includes(query))
            );
        }

        // Sort items
        return items.sort((a, b) => {
            if (this.drawerSortOption === 'price-low') return a.rate - b.rate;
            if (this.drawerSortOption === 'price-high') return b.rate - a.rate;
            // relevance/default: bestMatch first, then in-stock
            if (a.isBestMatch && !b.isBestMatch) return -1;
            if (!a.isBestMatch && b.isBestMatch) return 1;
            if (a.is_in_stock && !b.is_in_stock) return -1;
            if (!a.is_in_stock && b.is_in_stock) return 1;
            return 0;
        });
    }

    // Integration status
    isHyperpureEnabled = false;
    isDmartEnabled = false;
    isUdaanEnabled = false;
    isDeliveritEnabled = false;
    isSwiggyAssureEnabled = false;
    enableLocalSupplier = false;
    /** True when the user has no external supplier integrations and search uses the demo account. */
    isDemoSearchMode = false;
    hyperpureStoreUuid = '';
    hyperpureSessionId = '';
    hyperpureLoginData: any = null;
    udaanStoreUuid = '';      // Internal store ID (MD5 hash) - used for update cart, etc.
    udaanApiStoreId = '';     // Actual Udaan store ID from udaanResponse.stores[0].storeId - used for get cart API only
    dmartStoreId = '';
    deliveritStoreData: any = null;

    // Child component references
    @ViewChild('hyperpureSupplier') hyperpureSupplier!: HyperpureSupplierComponent;
    @ViewChild('dmartSupplier') dmartSupplier!: DmartSupplierComponent;
    @ViewChild('udaanSupplier') udaanSupplier!: UdaanSupplierComponent;
    @ViewChild('deliveritSupplier') deliveritSupplier!: DeliveritSupplierComponent;
    @ViewChild('swiggyAssureSupplier') swiggyAssureSupplier!: SwiggyAssureSupplierComponent;
    private elRef!: ElementRef;

    @ViewChild('localSupplierPOModal') localSupplierPOModal!: LocalSupplierPurchaseOrderModalComponent;
    @ViewChild(ConnectLeadModalComponent) connectLeadModal?: ConnectLeadModalComponent;
    private demoFallbackCarts: any[] = [];
    demoLocalCarts: any[] = [];
    isDemoMobileCartOpen = false;
    isDemoMobileViewAllOpen = false;
    demoViewAllSection: SupplierSection | null = null;
    @ViewChild('entitySwitchConfirmModal') entitySwitchConfirmModal: any;
    @ViewChild('searchInput') searchInput!: ElementRef;
    @ViewChild('resultsPane') resultsPane?: ElementRef<HTMLElement>;
    @ViewChild('swiggyCheckoutFrame') swiggyCheckoutFrame?: ElementRef<HTMLIFrameElement>;
    @ViewChild('demoComposeInput') demoComposeInput?: ElementRef<HTMLInputElement>;
    @ViewChild('demoTagsRow') demoTagsRow?: ElementRef<HTMLElement>;

    // Pending entity switch (stored when search is in progress and modal is shown)
    private pendingEntitySwitch: any = null;

    // Suppliers map for resolving names
    suppliersMap: Map<string, string> = new Map();

    // Favourite products state
    favProductKeys: Set<FavProductKey> = new Set();
    favProductsList: PmFavProduct[] = [];
    isLoadingFavProducts = false;
    savingFavProduct: Set<FavProductKey> = new Set();

    // Sidebar accordion state
    activeSidebarSection: 'recent' | 'favourites' = 'recent';
    favSidebarSearchTerm = '';

    // Fav view mode - when user is viewing/searching fav items, hide market best price banner
    isFavViewMode = false;
    isLoadingAllFav = false;
    activeFavItem: string = '';

    // Dynamic card display
    maxVisibleCards = 4; // Default to 4 cards, will be updated based on screen size

    // Mobile responsive properties
    isMobileView = typeof window !== 'undefined' && window.innerWidth <= 768;
    isDemoMobileViewport = typeof window !== 'undefined' && window.innerWidth <= 767;
    isSidebarOpen = false;

    // Socket
    private socket: Socket;
    private socketListeners = new Set<string>();

    private destroy$ = new Subject<void>();

    // Auto Fill Cart: supplier IDs where autoFill=true (loaded from purchasemanagerConfig)
    autoFillSupplierIds: string[] = [];
    hasSupplierPreferrence = false;

    // Market Best / Best Match state
    isMarketBestItemsCollapsed = false;
    bestMatchCandidates: BestMatchCandidate[] = [];
    marketBestItemSkus: Set<string> = new Set();

    // Settings Popup State
    isSettingsPopupOpen = false;
    tempSupplierOrder: any[] = [];
    marketBestItemsSnapshot: { platform: string; skuId: string; altSkuId?: string; previousQty: number }[] = [];
    hasMarketBestItems = false;
    isAddingBestMatches = false;
    isBestMatchPopupOpen = false;
    selectAllBestMatches = false;

    // Pending search tags (populated from quick add, searched on demand)
    pendingSearchTags: string[] = [];
    /** Blocks in-page router.navigate calls from cancelling /integrations. */
    private isLeavingPage = false;

    // Cart highlight effect state
    highlightedCartPlatform: string | null = null;
    cartHighlightAction: 'add' | 'remove' | null = null;
    private highlightTimeout: any = null;
    private readonly cartQtyDebounceMs = 400;
    private cartQtyTimers = new Map<string, ReturnType<typeof setTimeout>>();
    private cartQtyBaseline = new Map<string, number>();

    /** Terms from in-app navigation only. Never taken from the forgeable `tags` query param. */
    private readonly trustedSearchTags: string;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private priceDiscoveryService: PriceDiscoveryService,
        private entityService: EntityService,
        private authService: AuthService,
        private sharedService: SharedService,
        private supplierRegistry: SupplierRegistryService,
        private purchaseOrderService: PurchaseOrderService,
        private pmAnalytics: PurchaseManagerAnalyticsService,
        private pmDemoMode: PmDemoModeService,
        private host: ElementRef<HTMLElement>,
        private sanitizer: DomSanitizer
    ) {
        this.trustedSearchTags = readTrustedPriceDiscoveryTags(this.router);
    }

    get isPublicDemo(): boolean {
        return this.pmDemoMode.isActive;
    }

    get canCheckout(): boolean {
        return this.isPublicDemo || this.authService.canCheckout();
    }

    get canCreateOrder(): boolean {
        return this.isPublicDemo || this.authService.canUseOrders();
    }

    get isDemoMobileView(): boolean {
        return this.isPublicDemo && this.isDemoMobileViewport;
    }

    private hasAuthorizedEntity(): boolean {
        return this.isPublicDemo || /^[0-9a-fA-F]{24}$/.test(this.selectedEntityId || '');
    }

    hideMarketBestBanner = false;
    private drawerNodesOnBody: HTMLElement[] = [];

    /**
     * Lifecycle hook for component initialization. Sets up entity and processes initial search tags.
     */
    ngOnInit(): void {
        // Calculate max visible cards based on screen size
        this.calculateMaxVisibleCards();
        window.addEventListener('resize', () => this.calculateMaxVisibleCards());

        // Detect mobile view
        this.checkMobileView();
        window.addEventListener('resize', () => this.checkMobileView());

        // Get entity
        const selectedEntity = this.entityService.getSingleSelectedEntity();
        if (this.pmDemoMode.isActive) {
            this.selectedEntityId = this.pmDemoMode.demoEntityId;
            this.loadDemoSupplierCarts();
        } else if (selectedEntity) {
            this.selectedEntityId = selectedEntity.id;
            this.previousEntity = selectedEntity;
        }

        if (!this.pmDemoMode.isActive) {
            // Initialize analytics
            const user = this.authService.user;
            const entity = this.entityService.getSingleSelectedEntity();
            this.pmAnalytics.initialize({
                userId: user?.id || '',
                userName: user?.name || '',
                userEmail: user?.email || '',
                entityId: this.selectedEntityId,
                companyId: user?.company?.id || entity?.parent || entity?.parentId || '',
                companyName: user?.company?.name || entity?.group || '',
            });
            this.pmAnalytics.trackPageView('/purchase-manager/price-discovery', 'PriceDiscovery');
        }
        // Restore market best items from localStorage
        this.loadMarketBestItems();

        if (!this.pmDemoMode.isActive) {
            // Listen for entity changes (e.g. user switches outlet from navbar)
            this.entityService.singleSelectedEntity$
                .pipe(takeUntil(this.destroy$))
                .subscribe(entity => {
                    if (entity && entity.id && this.selectedEntityId && entity.id !== this.selectedEntityId) {
                        // If price discovery is in progress, show confirmation modal before switching
                        if (this.isSearchInProgress) {
                            this.pendingEntitySwitch = entity;
                            if (this.entitySwitchConfirmModal) {
                                this.entitySwitchConfirmModal.show();
                            }
                            return;
                        }
                        // Store new entity as previous before resetting
                        this.previousEntity = entity;
                        this.resetForEntityChange(entity.id);
                    }
                });
        }

        // Load suppliers for name mapping
        this.loadSuppliers();

        // Load favourite products
        this.loadFavouriteProducts();

        // Search terms come from in-app navigation state, not from `?tags=`.
        // That query value was encrypted with the public storage key, so a link
        // could forge it and write the decrypted text into recent/trending searches.
        this.route.queryParams.pipe(take(1), takeUntil(this.destroy$)).subscribe(params => {
            const tagsParam = this.trustedSearchTags;
            if (tagsParam) {
                const tags = tagsParam.split('$$,$$').map((t: string) => t.trim()).filter((t: string) => t.length > 0);
                const decodedTags = tags.map((tag: string) => decodePmSearchTag(tag));
                const hasSheetRows = decodedTags.some(tag => tag.fromSheet);
                if (hasSheetRows) {
                    this.sheetAgentRun += 1;
                    this.sheetAgentOpen = true;
                }
                this.searchItems = [];
                decodedTags.forEach(decoded => {
                    let name = decoded.name;
                    if (this.searchItems.some(item => item.name.toLowerCase() === name.toLowerCase())) {
                        name = decoded.unit ? `${decoded.name} (${decoded.unit})` : decoded.name;
                    }
                    this.searchItems.push({
                        name,
                        unit: decoded.unit,
                        targetQuantity: decoded.targetQuantity,
                        sheetItemName: decoded.fromSheet ? decoded.name : undefined,
                        sheetRun: decoded.fromSheet ? this.sheetAgentRun : undefined,
                        sheetState: decoded.fromSheet ? 'searching' : undefined,
                        isLoading: true
                    });
                });
                // Set the first search item as active by default
                if (this.searchItems.length > 0) {
                    this.activeSearchItem = this.searchItems[0].name;
                }

                // Restore active Quick Add pill state from localStorage
                this.restoreQuickAddState();
            }

            if (params['tags']) {
                this.router.navigate([], {
                    relativeTo: this.route,
                    queryParams: { tags: null },
                    queryParamsHandling: 'merge',
                    replaceUrl: true,
                    state: priceDiscoveryTagsNavigationState(tagsParam)
                });
            }

            if (this.selectedEntityId) {
                this.initializeAndSearch();

                // Auto-load Recent Purchased items if landing with no search tags (skip in demo mode)
                if (!tagsParam) {
                    const recentCat = this.quickAddCategories.find(c => c.key === 'recentPurchase');
                    if (recentCat) {
                        // Defer until integration check & sections are initialized
                        setTimeout(() => {
                            if (!this.isDemoSearchMode && !this.pmDemoMode.isActive && this.searchItems.length === 0) {
                                this.onQuickAddClick(recentCat);
                            }
                        }, 1500);
                    }
                }
            } else {
                this.sharedService.errorAlert('No entity selected');
                this.isInitializing = false;
            }
        });
    }

    ngAfterViewInit(): void {
        this.searchInput?.nativeElement?.focus({ preventScroll: true });
    }

    /**
     * Lifecycle hook for cleanup. Disconnects socket listeners and resets body scroll.
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        // Unsubscribe all socket listeners
        this.socketListeners.forEach((requestId) => {
            this.unsubscribeFromSocket(requestId);
        });
        if (this.socket) {
            this.socket.disconnect();
        }

        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        this.cartQtyTimers.forEach(timer => clearTimeout(timer));
        this.cartQtyTimers.clear();
        this.stopSwiggyIframeLogging();
        this.restoreDemoDrawer();

        // Remove resize listeners
        window.removeEventListener('resize', () => this.calculateMaxVisibleCards());
        window.removeEventListener('resize', () => this.checkMobileView());
    }

    /**
     * Saves the current entity's search state into the cache so it can be restored later.
     * Items still loading (in-progress searches) are saved with isLoading=true so they
     * can be re-searched when the entity is restored.
     */
    private saveEntitySearchState(): void {
        if (!this.selectedEntityId || this.searchItems.length === 0) return;

        // Deep-clone search items, preserving isLoading flag as-is
        const clonedSearchItems: SearchItem[] = this.searchItems.map(item => ({
            ...JSON.parse(JSON.stringify(item)),
            isLoading: item.isLoading || false,
        }));

        this.entitySearchCache.set(this.selectedEntityId, {
            searchItems: clonedSearchItems,
            activeSearchItem: this.activeSearchItem,
            supplierSections: JSON.parse(JSON.stringify(this.supplierSections)),
            marketBestPrice: this.marketBestPrice ? JSON.parse(JSON.stringify(this.marketBestPrice)) : null,
            marketBestItemSkus: new Set(this.marketBestItemSkus),
            marketBestItemsSnapshot: [...this.marketBestItemsSnapshot],
            hasMarketBestItems: this.hasMarketBestItems,
            hasSupplierPreferrence: this.hasSupplierPreferrence,
        });
    }

    /**
     * Restores a previously cached search state for the given entity.
     * - Items that had completed results are restored as-is (isLoading = false).
     * - Items that were still loading when cached are kept with isLoading = true
     *   so they can be re-searched after integration setup.
     * Returns true if cached state was found and restored, false otherwise.
     */
    private restoreEntitySearchState(entityId: string): boolean {
        const cached = this.entitySearchCache.get(entityId);
        if (!cached || cached.searchItems.length === 0) return false;

        this.searchItems = cached.searchItems;
        this.activeSearchItem = cached.activeSearchItem;
        this.supplierSections = cached.supplierSections;
        this.marketBestPrice = cached.marketBestPrice;
        this.marketBestItemSkus = cached.marketBestItemSkus;
        this.marketBestItemsSnapshot = cached.marketBestItemsSnapshot;
        this.hasMarketBestItems = cached.hasMarketBestItems;
        this.hasSupplierPreferrence = cached.hasSupplierPreferrence || false;

        // Mark completed items as not loading; keep pending items as loading
        const hasPendingItems = this.searchItems.some(item => item.isLoading);

        this.isLoading = hasPendingItems;
        this.isInitializing = false;

        // Remove from cache after restore (will be re-saved on next switch)
        this.entitySearchCache.delete(entityId);

        return true;
    }

    /**
     * Resets all component state when the entity changes.
     * Saves current entity's search state, clears localStorage for the previous entity,
     * disconnects socket listeners, and reinitializes (or restores from cache).
     */
    private resetForEntityChange(newEntityId: string): void {
        // Unsubscribe all socket listeners
        this.socketListeners.forEach((requestId) => {
            this.unsubscribeFromSocket(requestId);
        });
        this.socketListeners.clear();
        // Save the current entity's search state before resetting
        this.saveEntitySearchState();

        // Clear all PM-related localStorage data (belongs to old entity)
        localStorage.removeItem('hyperpureCart');
        localStorage.removeItem('dmartCart');
        localStorage.removeItem('hyperpureSessionItems');
        localStorage.removeItem('dmartSessionItems');
        localStorage.removeItem('deliveritSessionItems');
        this.clearMarketBestItems();

        // Reset search state
        this.searchItems = [];
        this.activeSearchItem = '';
        this.newItemSearchText = '';
        this.supplierSections = [];
        this.marketBestPrice = null;
        this.selectedCard = null;
        this.allSuppliers = [];
        this.suppliersMap = new Map();

        // Reset favourite products state
        this.favProductKeys = new Set();
        this.favProductsList = [];
        this.savingFavProduct = new Set();

        // Reset loading states
        this.isLoading = false;
        this.isInitializing = true;

        // Reset drawer states
        this.isDrawerOpen = false;
        this.selectedSection = null;
        this.drawerSearchText = '';
        this.drawerSortOption = 'relevance';
        this.isCheckoutDrawerOpen = false;
        this.selectedCheckoutCart = null;

        // Reset supplier integration states
        this.isHyperpureEnabled = false;
        this.hyperpureStoreUuid = '';
        this.hyperpureSessionId = '';
        this.hyperpureLoginData = null;
        this.isDmartEnabled = false;
        this.dmartStoreId = '';
        this.isUdaanEnabled = false;
        this.udaanStoreUuid = '';
        this.udaanApiStoreId = '';
        this.isDeliveritEnabled = false;
        this.deliveritStoreData = null;
        this.isSwiggyAssureEnabled = false;
        this.enableLocalSupplier = false;
        this.isDemoSearchMode = false;

        // Delegate reset to child components
        if (this.hyperpureSupplier) this.hyperpureSupplier.resetState();
        if (this.dmartSupplier) this.dmartSupplier.resetState();
        if (this.udaanSupplier) this.udaanSupplier.resetState();
        if (this.deliveritSupplier) this.deliveritSupplier.resetState();
        if (this.swiggyAssureSupplier) this.swiggyAssureSupplier.resetState();

        // Set new entity and reinitialize
        this.selectedEntityId = newEntityId;

        // Reload suppliers and favourites for new entity
        this.loadSuppliers();
        this.loadFavouriteProducts();

        // Check if we have cached search state for the new entity
        if (this.restoreEntitySearchState(newEntityId)) {
            // Cached state restored — only initialize supplier integrations (no re-search)
            this.initializeIntegrationsOnly();
            return;
        }

        // No cached state — start fresh with empty search (don't re-read URL query params,
        // those belong to the original entity from page load)
        this.initializeAndSearch();
    }

    /**
     * Fetches the list of all suppliers to build an ID-to-Name mapping for local suppliers.
     */
    private loadSuppliers(): void {
        if (!this.selectedEntityId) return;

        this.priceDiscoveryService.getSuppliers(this.selectedEntityId, true)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (suppliers: any[]) => {
                    this.allSuppliers = suppliers || [];
                    (suppliers || []).forEach(s => {
                        const id = s.id || s._id;
                        if (id) {
                            this.suppliersMap.set(id, s.name || s.alias || s.supplierAlias || 'Unknown Supplier');
                        }
                    });
                },
                (error: any) => {
                    console.warn('Failed to load supplier names for mapping', error);
                }
            );
    }

    /**
     * Resolves a supplier ID to its human-readable name using the suppliers map.
     * @param supplierRef The supplier ID or name.
     */
    resolveSupplierName(supplierRef: string | undefined): string {
        if (!supplierRef) return '';
        return this.suppliersMap.get(supplierRef) || supplierRef;
    }

    formatSupplierFromLabel(supplierRef: string | undefined): string {
        const name = this.resolveSupplierName(supplierRef);
        if (!name) {
            return '';
        }
        return name.toLowerCase().startsWith('from') ? name : `From ${name}`;
    }

    /**
     * Checks if a string looks like an ID (long alphanumeric/hex string) rather than a readable name.
     * Used to avoid displaying raw IDs in the UI.
     */
    private looksLikeId(value: string | undefined): boolean {
        if (!value) return false;
        // Looks like an ID if it's 20+ chars and contains only hex chars or alphanumeric with no spaces
        return value.length >= 20 && /^[a-f0-9]+$/i.test(value);
    }

    // ── Initialization ──

    /**
     * Called when user confirms the entity switch from the confirmation modal.
     * Proceeds with the entity change.
     */
    confirmEntitySwitch(): void {
        if (this.entitySwitchConfirmModal) {
            this.entitySwitchConfirmModal.hide();
        }
        if (this.pendingEntitySwitch) {
            this.previousEntity = this.pendingEntitySwitch;
            this.resetForEntityChange(this.pendingEntitySwitch.id);
            this.pendingEntitySwitch = null;
        }
    }

    /**
     * Called when user cancels the entity switch from the confirmation modal.
     * Reverts the entity dropdown back to the current entity.
     */
    cancelEntitySwitch(): void {
        if (this.entitySwitchConfirmModal) {
            this.entitySwitchConfirmModal.hide();
        }
        // Revert entity back to the previous one
        if (this.previousEntity) {
            this.entityService.setSingleSelectedEntity(this.previousEntity);
        }
        this.pendingEntitySwitch = null;
    }

    /**
     * Checks integration status with various suppliers and starts price discovery searches.
     */
    private initializeAndSearch(): void {
        this.isInitializing = true;

        this.priceDiscoveryService.checkIntegrationStatus(this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    this.applyUserIntegrationStatus(response);
                    this.isDemoSearchMode = areAllExternalSuppliersDisabled(response);
                    if (this.isDemoSearchMode) {
                        this.ensureDemoDefaultSearchItems();
                    }
                    this.initSupplierSections(response?.supplierPreferrence || []);

                    if (this.pmDemoMode.isActive) {
                        this.isInitializing = false;
                        this.startSearches();
                        return;
                    }

                    this.initializeEnabledSupplierChildren();

                    if (this.isHyperpureEnabled && this.hyperpureStoreUuid) {
                        this.fetchHyperpureLogin().then(() => {
                            this.isInitializing = false;
                            this.startSearches();
                        });
                    } else {
                        this.isInitializing = false;
                        this.startSearches();
                    }
                },
                (error: any) => {
                    console.error('Failed to check integration status', error);
                    this.isInitializing = false;
                    this.initSupplierSections();
                    this.startSearches();
                }
            );
    }

    /**
     * Initializes supplier integrations (Hyperpure login, DMart, Udaan) without
     * reinitializing supplier sections or re-running completed searches.
     * After integration setup, re-triggers searches for any items that were
     * still loading when the entity was cached (e.g. Ghee was in-progress).
     */
    private initializeIntegrationsOnly(): void {
        this.priceDiscoveryService.checkIntegrationStatus(this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    this.applyUserIntegrationStatus(response);
                    this.isDemoSearchMode = areAllExternalSuppliersDisabled(response);
                    if (this.isDemoSearchMode) {
                        this.ensureDemoDefaultSearchItems();
                    }
                    this.initializeEnabledSupplierChildren();

                    if (this.isHyperpureEnabled && this.hyperpureStoreUuid) {
                        this.fetchHyperpureLogin().then(() => {
                            this.reSearchPendingItems();
                        });
                    } else {
                        this.reSearchPendingItems();
                    }
                },
                (error: any) => {
                    console.error('Failed to check integration status for cached entity', error);
                    this.reSearchPendingItems();
                }
            );
    }

    /**
     * Applies the current entity's real integration flags. Demo store data is kept
     * separate so carts/add-to-cart still require a real integration.
     */
    private applyUserIntegrationStatus(response: any): void {
        this.isHyperpureEnabled = response?.isHyperpureEnabled || response?.is_hyperpure_enabled || false;
        this.isDmartEnabled = response?.isDmartEnabled || response?.is_dmart_enabled || false;
        this.isUdaanEnabled = response?.isUdaanEnabled || response?.is_udaan_enabled || false;
        this.isDeliveritEnabled = response?.isDeliveritEnabled || response?.is_deliverit_enabled || false;
        this.deliveritStoreData = response?.deliveritStoreData || response?.deliverit_store_data || null;
        this.isSwiggyAssureEnabled = response?.isSwiggyAssureEnabled || response?.is_swiggy_assure_enabled || false;
        this.enableLocalSupplier = response?.enableLocalSupplier || response?.enable_local_supplier || false;

        if (this.isHyperpureEnabled) {
            this.hyperpureStoreUuid = response?.hyperpureStoreUuid || response?.hyperpure_store_uuid || '';
            this.hyperpureLoginData = {
                storeId: this.hyperpureStoreUuid,
                email: response?.hyperpureEmail,
                phone: response?.hyperpurePhone,
                storeName: response?.hyperpureStoreData?.storeName,
                storeGst: response?.hyperpureStoreData?.storeGst,
                storeAddress: response?.hyperpureStoreData?.storeAddress,
                storePincode: response?.hyperpureStoreData?.storePincode
            };
        }

        if (this.isUdaanEnabled) {
            this.udaanStoreUuid = response?.udaanStoreData?.storeId || response?.udaanId || '';
            this.udaanApiStoreId = response?.udaanStoreData?.udaanResponse?.stores?.[0]?.storeId ||
                response?.udaanStoreData?.udaanResponse?.stores?.[0]?.store_id || '';
        }

        if (this.isDmartEnabled) {
            this.dmartStoreId = response?.dmartStoreData?.storeId || response?.dmartId || '';
        }
    }

    /**
     * Initializes cart child components only for suppliers the user has actually integrated.
     */
    private initializeEnabledSupplierChildren(): void {
        if (this.isDmartEnabled && this.dmartStoreId) {
            setTimeout(() => {
                if (this.dmartSupplier) {
                    const dmartSection = this.supplierSections.find(s => s.id === 'dmart');
                    this.dmartSupplier.entityId = this.selectedEntityId;
                    this.dmartSupplier.storeId = this.dmartStoreId;
                    this.dmartSupplier.supplierSection = dmartSection || null;
                    this.dmartSupplier.initialize();
                }
            });
        }

        if (this.isUdaanEnabled && this.udaanStoreUuid) {
            setTimeout(() => {
                if (this.udaanSupplier) {
                    const udaanSection = this.supplierSections.find(s => s.id === 'udaan');
                    this.udaanSupplier.entityId = this.selectedEntityId;
                    this.udaanSupplier.storeUuid = this.udaanStoreUuid;
                    this.udaanSupplier.apiStoreId = this.udaanApiStoreId;
                    this.udaanSupplier.supplierSection = udaanSection || null;
                }
            });
        }

        if (this.isDeliveritEnabled && this.deliveritStoreData) {
            setTimeout(() => {
                if (this.deliveritSupplier) {
                    const deliveritSection = this.supplierSections.find(s => s.id === 'deliverit');
                    this.deliveritSupplier.entityId = this.selectedEntityId;
                    this.deliveritSupplier.storeData = this.deliveritStoreData;
                    this.deliveritSupplier.supplierSection = deliveritSection || null;
                    this.deliveritSupplier.initialize();
                }
            });
        }

        if (this.isSwiggyAssureEnabled) {
            setTimeout(() => {
                if (this.swiggyAssureSupplier) {
                    const swiggySection = this.supplierSections.find(s => s.id === 'swiggy');
                    this.swiggyAssureSupplier.entityId = this.selectedEntityId;
                    this.swiggyAssureSupplier.supplierSection = swiggySection || null;
                    this.swiggyAssureSupplier.initialize();
                }
            });
        }
    }

    /**
     * True when this platform is an external supplier shown from demo search data
     * (user has not integrated it, so add-to-cart must stay blocked).
     */
    isDemoPreviewSupplier(platform?: string): boolean {
        if (!this.isDemoSearchMode || !platform) {
            return false;
        }
        const id = platform.toLowerCase();
        return id === 'hyperpure' || id === 'udaan' || id === 'dmart' || id === 'deliverit' || id === 'swiggy';
    }

    get demoDisabledActionTitle(): string {
        return 'Connect this supplier to use cart and favourites';
    }

    isDemoProductUnavailable(item: any): boolean {
        return item?.is_in_stock === false || item?.isInStock === false;
    }

    private ensureDemoDefaultSearchItems(): void {
        if (this.searchItems.length > 0) {
            return;
        }

        this.searchItems = DEMO_DEFAULT_SEARCH_ITEMS.map(name => ({
            name,
            isLoading: true
        }));
        this.activeSearchItem = this.searchItems[0]?.name || '';
        this.persistSearchTags();
    }

    openConnectLeadPopup(): void {
        this.connectLeadModal?.open();
    }

    private loadDemoSupplierCarts(): void {
        this.priceDiscoveryService.getDemoSupplierCarts()
            .pipe(takeUntil(this.destroy$))
            .subscribe((carts: any[]) => {
                this.demoFallbackCarts = Array.isArray(carts) ? carts : [];
                this.demoLocalCarts = this.cloneDemoCarts(this.demoFallbackCarts);
            });
    }

    goToIntegrations(event?: Event): void {
        event?.preventDefault();
        event?.stopImmediatePropagation();
        this.isLeavingPage = true;
        window.location.assign('/integrations');
    }

    /**
     * Re-triggers search for any items that were still loading when the entity was cached.
     * Items that already have results are left untouched.
     */
    private reSearchPendingItems(): void {
        const pendingItems = this.searchItems.filter(item => item.isLoading);
        if (pendingItems.length === 0) {
            this.isLoading = false;
            return;
        }

        this.isLoading = true;
        pendingItems.forEach(searchItem => {
            this.compareItem(searchItem);
        });
    }

    /**
     * Initializes the available supplier sections based on enabled integrations.
     */
    private initSupplierSections(savedOrder: string[] = []): void {
        this.supplierSections = [];

        // Pre-initialize standard sections to maintain fixed ordering
        const standardSections = [
            { id: 'hyperpure', name: 'Hyperpure', logo: 'hyperpure', color: '#fad2d1' },
            { id: 'swiggy', name: 'Assure', logo: 'swiggy', color: '#fff1e6' },
            { id: 'deliverit', name: 'DeliverIt', logo: 'deliverit', color: '#EAF7EB' },
            { id: 'udaan', name: 'Udaan', logo: 'udaan', color: '#cce5ff' },
            { id: 'dmart', name: 'DMart', logo: 'dmart', color: '#d4edda' },
            { id: 'local', name: 'Local Suppliers', logo: 'local', color: '#fff3cd', isLocalSupplier: true }
        ];

        standardSections.forEach(config => {
            const isExternal = config.id === 'hyperpure' || config.id === 'udaan' || config.id === 'dmart' || config.id === 'deliverit' || config.id === 'swiggy';
            // Check if enabled from company config
            const isEnabled = (config.id === 'hyperpure' && this.isHyperpureEnabled) ||
                (config.id === 'udaan' && this.isUdaanEnabled) ||
                (config.id === 'dmart' && this.isDmartEnabled) ||
                (config.id === 'deliverit' && this.isDeliveritEnabled) ||
                (config.id === 'swiggy' && this.isSwiggyAssureEnabled) ||
                (config.id === 'local' && this.enableLocalSupplier);

            if (isEnabled || (this.isDemoSearchMode && isExternal)) {
                this.supplierSections.push({
                    id: config.id,
                    name: config.name,
                    logo: config.logo,
                    color: config.color,
                    items: [],
                    totalItems: 0,
                    isVisible: true,
                    isLocalSupplier: config.isLocalSupplier
                });
            }
        });

        // Reorder list based on user's saved sequence
        try {
            const orderIds = savedOrder && savedOrder.length > 0 ? savedOrder : [];
            if (orderIds.length > 0) {
                this.supplierSections.sort((a, b) => {
                    const indexA = orderIds.indexOf(a.id);
                    const indexB = orderIds.indexOf(b.id);
                    if (indexA === -1 && indexB === -1) return 0;
                    if (indexA === -1) return 1;
                    if (indexB === -1) return -1;
                    return indexA - indexB;
                });
            }
        } catch (e) {
            console.warn('Failed to parse supplier sequence:', e);
        }
    }

    /**
     * Fetches specific login and session data required for Hyperpure integration.
     */
    private fetchHyperpureLogin(): Promise<void> {
        return new Promise((resolve) => {
            // Initialize Hyperpure child component
            setTimeout(() => {
                if (this.hyperpureSupplier) {
                    const hpSection = this.supplierSections.find(s => s.id === 'hyperpure');
                    this.hyperpureSupplier.entityId = this.selectedEntityId;
                    this.hyperpureSupplier.storeUuid = this.hyperpureStoreUuid;
                    this.hyperpureSupplier.supplierSection = hpSection || null;
                    this.hyperpureSupplier.initialize();
                }
            });
            resolve();
        });
    }

    // ── Search & Compare ──

    /**
     * Starts the price comparison process for all search items.
     */
    private startSearches(): void {
        if (this.searchItems.length === 0) return;

        this.isLoading = true;

        // Sequentially fire comparePurchaseItems for each search tag
        this.searchItems.forEach(searchItem => {
            this.compareItem(searchItem);
        });
    }

    /**
     * Triggers the price comparison API for a specific search item.
     * @param searchItem The item to search and compare prices for.
     */
    private compareItem(searchItem: SearchItem): void {
        const selectedEntity = this.entityService.getEntities()?.find(
            (e: any) => e.id === this.selectedEntityId
        );
        const location = selectedEntity?.city || selectedEntity?.address || 'Ahmedabad';

        const params: any = {
            item_name: (searchItem.sheetItemName || searchItem.name).trim(),
            unit: searchItem.unit || '',
            quantity: 1,
            location: location,
            entity_id: this.selectedEntityId,
            hyperpure_store_uuid: this.hyperpureLoginData?.store_uuid || this.hyperpureStoreUuid || '',
            hyperpure_session_id: this.hyperpureSessionId || ''
        };

        if (this.isDemoSearchMode) {
            params.is_demo = true;
        }

        searchItem.isLoading = true;
        const searchStartTime = Date.now();

        this.priceDiscoveryService.comparePurchaseItems(params, this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    const requestId = response?.requestId || response?.request_id;
                    if (requestId) {
                        searchItem.requestId = requestId;
                        (searchItem as any)._searchStartTime = searchStartTime;
                        if (this.pmDemoMode.isActive) {
                            this.loadDemoPriceDiscoveryStatus(requestId, searchItem);
                        } else {
                            this.subscribeToSocketUpdates(requestId, searchItem);
                        }
                    } else {
                        searchItem.isLoading = false;
                        console.error('No requestId returned for', searchItem.name);
                        this.checkAllSearchesComplete();
                    }
                },
                (error: any) => {
                    console.error('Compare failed for', searchItem.name, error);
                    searchItem.isLoading = false;

                    // Track failed search
                    this.pmAnalytics.trackSearch('all', {
                        keyword: searchItem.name,
                        resultCount: 0,
                        searchSource: 'typed',
                        responseTimeMs: Date.now() - searchStartTime,
                    });

                    this.checkAllSearchesComplete();
                }
            );
    }

    private loadDemoPriceDiscoveryStatus(requestId: string, searchItem: SearchItem): void {
        this.priceDiscoveryService.checkPriceDiscoveryStatus(requestId, this.selectedEntityId)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    searchItem.isLoading = false;
                    if (response?.status === 'completed' && response?.response) {
                        const productImages = response?.product_images || response?.productImages || {};
                        this.processResults(response.response, searchItem, productImages);
                    }
                    this.checkAllSearchesComplete();
                },
                () => {
                    searchItem.isLoading = false;
                    this.checkAllSearchesComplete();
                }
            );
    }

    // ── Socket Subscriptions ──

    /**
     * Subscribes to socket updates for a price discovery search request.
     * @param requestId The ID of the search request.
     * @param searchItem The search item associated with the request.
     */
    private subscribeToSocketUpdates(requestId: string, searchItem: SearchItem): void {
        if (!this.socket) {
            const connection = getNotificationSocketConnection(this.authService.token, [this.selectedEntityId]);
            if (!connection) {
                return;
            }
            this.socket = io(connection.url, connection.options);
        }
        emitSubscribeEntities(this.socket, [this.selectedEntityId]);

        this.socketListeners.add(requestId);

        this.socket.on(requestId, (data: any) => {
            const fetchStatus = (retries = 3) => {
                this.priceDiscoveryService.checkPriceDiscoveryStatus(requestId, this.selectedEntityId)
                    .pipe(take(1), takeUntil(this.destroy$))
                    .subscribe(
                        (response: any) => {
                            if (response?.status === 'completed' && response?.response) {
                                // Unsubscribe from socket
                                this.unsubscribeFromSocket(requestId);
                                searchItem.isLoading = false;

                                // Process results
                                const productImages = response?.product_images || {};
                                this.processResults(response.response, searchItem, productImages);

                                // Track successful search in analytics DB
                                const searchStartTime = (searchItem as any)._searchStartTime || Date.now();
                                const resultCountBySupplier: Record<string, number> = {};
                                this.supplierSections.forEach(s => {
                                    const count = s.items.filter(i => i.searchItemName === searchItem.name).length;
                                    if (count > 0) resultCountBySupplier[s.id] = count;
                                });
                                const totalResults = Object.values(resultCountBySupplier).reduce((a, b) => a + b, 0);
                                this.pmAnalytics.trackSearch('all', {
                                    keyword: searchItem.name,
                                    resultCount: totalResults,
                                    resultCountBySupplier,
                                    searchSource: 'typed',
                                    responseTimeMs: Date.now() - searchStartTime,
                                });

                                this.checkAllSearchesComplete();
                            } else if (retries > 0) {
                                // If we hit a race condition, retry after a short delay
                                setTimeout(() => fetchStatus(retries - 1), 500);
                            }
                        },
                        (error: any) => {
                            console.error('API error after socket event for', searchItem.name, error);
                            this.unsubscribeFromSocket(requestId);
                            searchItem.isLoading = false;
                            this.checkAllSearchesComplete();
                        }
                    );
            };

            // Call immediately without 1-second delay
            fetchStatus();
        });
    }

    /**
     * Unsubscribes from socket updates for a specific request ID.
     * @param requestId The request ID to unsubscribe from.
     */
    private unsubscribeFromSocket(requestId: string): void {
        if (this.socketListeners.has(requestId) && this.socket) {
            this.socket.off(requestId);
            this.socketListeners.delete(requestId);
        }
    }

    /**
     * Checks if all active search items have finished loading their results.
     */
    private checkAllSearchesComplete(): void {
        this.searchItems.forEach(item => {
            if (item.sheetRun === this.sheetAgentRun && !item.isLoading && item.sheetState === 'searching') {
                this.noteSheetSearchResult(item);
            }
        });
        const allDone = this.searchItems.every(item => !item.isLoading);
        if (allDone) {
            this.isLoading = false;
            this.applySheetQuantitiesToHyperpureCart();
        }
    }

    private noteSheetSearchResult(searchItem: SearchItem): void {
        const match = this.findHyperpureMatch(searchItem);
        if (!match) {
            searchItem.sheetState = 'missed';
            searchItem.sheetMatchName = undefined;
            searchItem.sheetMatchSku = undefined;
            searchItem.sheetMatchRate = undefined;
            return;
        }
        searchItem.sheetMatchName = match.item_name;
        searchItem.sheetMatchSku = String(match.sku_id || '');
        searchItem.sheetMatchRate = match.rate;
        searchItem.sheetState = searchItem.targetQuantity ? 'matched' : 'found';
    }

    // ── Process Results ──

    /**
     * Processes the price discovery results and organizes them into supplier sections.
     * @param response The raw results from the price discovery API.
     * @param searchItem The search item these results belong to.
     * @param productImages Optional mapping of product images.
     */
    private processResults(response: any, searchItem: SearchItem, productImages: any = {}): void {
        // Detect response structure
        const hasNewStructure = response.hyperpure || response.dmart || response.udaan || response.deliverit || response.swiggy;
        const supplierResults: { [key: string]: { bestMatch: any; similarItems: any[] } } = {};

        if (hasNewStructure) {
            // New structure: { hyperpure: { best_match, similar_items }, ... }
            // Helper to map productImages/product_images -> image_url on raw items
            /**
             * Internal helper to map product images to a consistent image_url field.
             */
            const mapProductImages = (item: any) => {
                if (item && !item.image_url && !item.imageUrl) {
                    // Check both camelCase and snake_case variants
                    const imgUrl = item.productImages || item.product_images;
                    if (imgUrl) {
                        if (typeof imgUrl === 'string') {
                            item.image_url = imgUrl;
                            item.imageUrl = imgUrl;
                        } else if (Array.isArray(imgUrl) && imgUrl.length > 0) {
                            const first = imgUrl[0];
                            item.image_url = typeof first === 'string' ? first : (first?.path || first?.url || first?.image_url || '');
                            item.imageUrl = item.image_url;
                        }
                    }
                }
            };

            ['hyperpure', 'dmart', 'udaan', 'deliverit', 'swiggy'].forEach(supplierId => {
                const data = response[supplierId];
                if (data && Object.keys(data).length > 0) {
                    // Pre-map images on all items so they survive normalization
                    if (data.preferred_best_match) mapProductImages(data.preferred_best_match);
                    if (data.preferredBestMatch) mapProductImages(data.preferredBestMatch);
                    if (data.best_match) mapProductImages(data.best_match);
                    if (data.bestMatch) mapProductImages(data.bestMatch);
                    if (Array.isArray(data.similar_items)) data.similar_items.forEach(mapProductImages);
                    if (Array.isArray(data.similarItems)) data.similarItems.forEach(mapProductImages);

                    supplierResults[supplierId] = {
                        bestMatch: (data.best_match && Object.keys(data.best_match).length > 0) ? data.best_match : ((data.bestMatch && Object.keys(data.bestMatch).length > 0) ? data.bestMatch : null),
                        similarItems: data.similar_items || data.similarItems || []
                    };
                }
            });
        } else {
            // Old structure: { best_match, similar_items: { hyperpure: [], dmart: [] } }
            const similarItemsRaw = response.similar_items || response.similarItems;
            const bestMatchData = response.preferred_best_match || response.preferredBestMatch || response.best_match || response.bestMatch;

            if (similarItemsRaw && typeof similarItemsRaw === 'object') {
                Object.keys(similarItemsRaw).forEach(supplierId => {
                    const items = similarItemsRaw[supplierId];
                    const bestMatchPlatform = (bestMatchData?.platform || '').toLowerCase();
                    supplierResults[supplierId] = {
                        bestMatch: bestMatchPlatform === supplierId.toLowerCase() ? bestMatchData : null,
                        similarItems: Array.isArray(items) ? items : []
                    };
                });
            }
        }

        // Process supplierWithRateCard (local suppliers)
        const supplierRateCardObj = response.supplierWithRateCard || response.supplier_with_rate_card;
        if (supplierRateCardObj && typeof supplierRateCardObj === 'object') {
            // Extract root best_match if present
            let rootBestMatch = supplierRateCardObj.best_match || supplierRateCardObj.bestMatch;
            if (rootBestMatch && typeof rootBestMatch === 'object' && Object.keys(rootBestMatch).length === 0) {
                rootBestMatch = null;
            }

            let localBestMatchToSet = null;
            if (rootBestMatch) {
                const supplierName = rootBestMatch.supplier_name || rootBestMatch.supplierName || this.resolveSupplierName(rootBestMatch.supplier_id || rootBestMatch.supplierId) || 'Local Supplier';
                localBestMatchToSet = { ...rootBestMatch };
                localBestMatchToSet.supplierName = supplierName;
                localBestMatchToSet.platform = 'local';
                localBestMatchToSet.supplierId = rootBestMatch.supplier_id || rootBestMatch.supplierId;
            }

            Object.keys(supplierRateCardObj).forEach(rateCardKey => {
                if (rateCardKey === 'best_match' || rateCardKey === 'bestMatch') return;

                const supplierData = supplierRateCardObj[rateCardKey];
                const similarItems = supplierData.similar_items || supplierData.similarItems || [];

                const supplierName = this.resolveSupplierName(supplierData.supplier_id || supplierData.supplierId)
                    || this.resolveSupplierName(rateCardKey)
                    || supplierData.supplier_name || supplierData.supplierName
                    || rateCardKey;

                if (Array.isArray(similarItems) && similarItems.length > 0) {
                    // Add to local supplier section
                    const allItems = [...similarItems];

                    allItems.forEach((item: any) => {
                        item.supplierName = supplierName;
                        item.platform = 'local';
                        item.supplierId = supplierData.supplier_id || supplierData.supplierId || rateCardKey;
                    });

                    const existingLocal = supplierResults['local'];
                    if (existingLocal) {
                        existingLocal.similarItems.push(...allItems);
                    } else {
                        supplierResults['local'] = {
                            bestMatch: null, // Default null, will be overridden by root best_match later
                            similarItems: allItems
                        };
                    }
                }
            });

            // Set the root best match for local section if exists
            if (localBestMatchToSet) {
                if (!supplierResults['local']) {
                    supplierResults['local'] = { bestMatch: localBestMatchToSet, similarItems: [] };
                } else {
                    supplierResults['local'].bestMatch = localBestMatchToSet;
                }
            }
        }

        // Store active best match from the main response
        let mainBestMatch = response.best_match || response.bestMatch;
        if (mainBestMatch && typeof mainBestMatch === 'object' && Object.keys(mainBestMatch).length === 0) {
            mainBestMatch = null;
        }

        if (mainBestMatch) {
            const platform = (mainBestMatch.platform || '').toLowerCase();
            const isEnabled = (platform === 'hyperpure' && this.isHyperpureEnabled) ||
                (platform === 'udaan' && this.isUdaanEnabled) ||
                (platform === 'dmart' && this.isDmartEnabled) ||
                (platform === 'deliverit' && this.isDeliveritEnabled) ||
                (platform === 'swiggy' && this.isSwiggyAssureEnabled) ||
                ((platform === 'local' || platform === 'supplier_rate_card') && this.enableLocalSupplier) ||
                this.isDemoPreviewSupplier(platform) ||
                !this.isStandardSection(platform);
            const hasPrice = (mainBestMatch.rate || mainBestMatch.price || 0) > 0;
            if (isEnabled && hasPrice) {
                searchItem.bestMatch = this.normalizeItem(mainBestMatch, mainBestMatch.platform || 'api', productImages);
            }
        }

        // Map results into supplier sections
        Object.keys(supplierResults).forEach(supplierId => {
            const { bestMatch, similarItems } = supplierResults[supplierId];
            const section = this.supplierSections.find(s => s.id === supplierId);

            if (!section) {
                // Skip creating section for standard suppliers that are not enabled
                if (this.isStandardSection(supplierId)) {
                    return;
                }

                // Create section dynamically for unknown suppliers
                if (similarItems.length > 0 || bestMatch) {
                    const newSection: SupplierSection = {
                        id: supplierId,
                        name: supplierId.charAt(0).toUpperCase() + supplierId.slice(1),
                        color: '#f0f0f0',
                        items: [],
                        totalItems: 0,
                        isVisible: true,
                        isLocalSupplier: supplierId === 'local'
                    };
                    this.supplierSections.push(newSection);
                    this.addItemsToSection(newSection, bestMatch, similarItems, searchItem, productImages);
                }
                return;
            }

            this.addItemsToSection(section, bestMatch, similarItems, searchItem, productImages);
        });

        // Update market best price
        this.updateMarketBestPrice();

        // Update search item price range
        this.updateSearchItemPrices(searchItem);
    }

    /**
     * Adds normalized items to a specific supplier section.
     * @param section The section to add items to.
     * @param bestMatch The best matching product for this supplier.
     * @param similarItems Array of similar products for this supplier.
     * @param searchItem The origin search item.
     * @param productImages Optional product image mapping.
     */
    private addItemsToSection(section: SupplierSection, bestMatch: any, similarItems: any[], searchItem: SearchItem, productImages: any = {}): void {
        const items: SupplierProduct[] = [];
        let normalizedBestMatchSkuId: string | null = null;

        // Normalize and add best match first
        if (bestMatch && (bestMatch.rate || bestMatch.price || 0) > 0) {
            const normalized = this.normalizeItem(bestMatch, section.id, productImages);
            normalized.isBestMatch = true;
            normalized.searchItemName = searchItem.name;
            normalizedBestMatchSkuId = normalized.sku_id;
            items.push(normalized);
        } else if (bestMatch) {
            // Treat zero-price best match as a regular item (not a Best Match)
            const normalized = this.normalizeItem(bestMatch, section.id, productImages);
            normalized.searchItemName = searchItem.name;
            items.push(normalized);
        }

        // Add similar items
        if (Array.isArray(similarItems)) {
            similarItems.forEach(item => {
                const normalized = this.normalizeItem(item, section.id, productImages);
                normalized.searchItemName = searchItem.name;
                // Don't duplicate best match — compare normalized sku_ids
                if (normalizedBestMatchSkuId && normalized.sku_id === normalizedBestMatchSkuId) {
                    return;
                }
                items.push(normalized);
            });
        }

        // Filter valid items
        const validItems = items.filter(item =>
            item.item_name && item.rate && Number(item.rate) > 0
        );

        // For Hyperpure items, sync selectedQuantity from the HP API cart
        // so search results reflect items already in the cart (show qty controls instead of "+")
        const hpCartItems = this.hyperpureSupplier?.apiCartItems || [];
        if (section.id === 'hyperpure' && hpCartItems.length > 0) {
            const cartMap = new Map<string, number>();
            hpCartItems.forEach(ci => {
                cartMap.set(String(ci.sku_id), ci.selectedQuantity || ci.quantity || 0);
            });
            validItems.forEach(item => {
                const cartQty = cartMap.get(String(item.sku_id));
                if (cartQty && cartQty > 0) {
                    item.selectedQuantity = cartQty;
                    this.applyBulkRate(item);
                }
            });
        }

        // For DMart items, sync selectedQuantity from the DMart API cart
        // Uses multi-key map (productId, skuUniqueID, articleNumber) to match any ID format
        const dmartQtyMap = this.dmartSupplier?.cartQuantityMap;
        if (section.id === 'dmart' && dmartQtyMap && dmartQtyMap.size > 0) {
            validItems.forEach(item => {
                const cartQty = dmartQtyMap.get(String(item.sku_id));
                if (cartQty && cartQty > 0) {
                    item.selectedQuantity = cartQty;
                    this.applyBulkRate(item);
                }
            });
        }

        // For Udaan items, sync selectedQuantity from its cart map
        const udaanQtyMap = this.udaanSupplier?.cartQuantityMap;
        if (section.id === 'udaan' && udaanQtyMap && udaanQtyMap.size > 0) {
            validItems.forEach(item => {
                const cartQty = udaanQtyMap.get(String(item.sku_id));
                if (cartQty && cartQty > 0) {
                    item.selectedQuantity = cartQty;
                    this.applyBulkRate(item);
                }
            });
        }

        // For DeliverIt items, sync selectedQuantity from its cart map
        const deliveritQtyMap = this.deliveritSupplier?.cartQuantityMap;
        if (section.id === 'deliverit' && deliveritQtyMap && deliveritQtyMap.size > 0) {
            validItems.forEach(item => {
                const cartQty = deliveritQtyMap.get(String(item.sku_id));
                if (cartQty && cartQty > 0) {
                    item.selectedQuantity = cartQty;
                    this.applyBulkRate(item);
                }
            });
        }

        const swiggyQtyMap = this.swiggyAssureSupplier?.cartQuantityMap;
        const swiggyCartItems = this.swiggyAssureSupplier?.apiCartItems || [];
        if (section.id === 'swiggy' && swiggyQtyMap && swiggyQtyMap.size > 0) {
            validItems.forEach(item => {
                const keys = [item.swiggyItemId, item.swiggyProductId, item.swiggySpin, item.sku_id]
                    .filter(Boolean)
                    .map(key => String(key));
                const cartQty = swiggyQtyMap.get(String(item.swiggyItemId || item.sku_id))
                    || swiggyQtyMap.get(String(item.sku_id));
                const cartItem = swiggyCartItems.find(row =>
                    [row.swiggyItemId, row.swiggyProductId, row.swiggySpin, row.sku_id]
                        .some(key => key && keys.includes(String(key)))
                );
                if (cartQty && cartQty > 0) {
                    item.selectedQuantity = cartQty;
                    this.applyBulkRate(item);
                }
                if (cartItem?.maximumOrderQty) {
                    item.maximumOrderQty = cartItem.maximumOrderQty;
                }
                if (cartItem?.minimumOrderQty) {
                    item.minimumOrderQty = cartItem.minimumOrderQty;
                }
            });
        }

        section.items.push(...validItems);
        section.totalItems = section.items.length;
    }

    /**
     * Normalizes a raw product item into a consistent SupplierProduct object.
     * @param item The raw product data.
     * @param supplierId The supplier identifier.
     * @param productImages Optional product images mapping.
     */
    private normalizeItem(item: any, supplierId: string, productImages: any = {}): SupplierProduct {
        const sId = (supplierId || item.platform || '').toLowerCase();
        const isLocal = sId === 'local' || sId === 'supplier_rate_card';
        // For local/rate-card items, always use item_name as sku_id so bestMatch and
        // section.items share the same identifier (itemCode is inconsistent across API responses)
        const rawItemId = isLocal
            ? (item.item_name || item.itemName || item.name || item.sku_id || item.skuId || item.itemCode || '')
            : (item.sku_id || item.skuId || item.itemCode || item.item_code || item.item_name || item.itemName || '');
        const itemId = String(rawItemId).trim();

        // Resolve image URL — check both camelCase and snake_case variants
        let imageUrl = '';
        const itemProductImages = item.productImages || item.product_images;

        if (itemProductImages && typeof itemProductImages === 'string') {
            imageUrl = itemProductImages;
        } else if (Array.isArray(itemProductImages) && itemProductImages.length > 0) {
            const firstImage = itemProductImages[0];
            imageUrl = typeof firstImage === 'string'
                ? firstImage
                : (firstImage?.path || firstImage?.url || firstImage?.image_url || '');
        } else if (productImages && sId && productImages[sId] && productImages[sId][itemId]) {
            imageUrl = productImages[sId][itemId];
        } else if (productImages && productImages[itemId]) {
            imageUrl = productImages[itemId];
        }

        if (!imageUrl) {
            imageUrl = item.image_url || item.imageUrl || item.image || '';
        }

        const exclusiveRate = Math.round(Number(item.rate || item.price || 0) * 100) / 100;
        const isHyperpure = sId === 'hyperpure';
        const taxDetails = item.taxDetails || item.tax_details;
        // Hyperpure rates are exclusive of tax; convert so they compare fairly with other platforms
        const baseRate = isHyperpure
            ? getHyperpureTaxInclusiveRate(exclusiveRate, taxDetails)
            : exclusiveRate;

        // Parse bulk prices — support both Hyperpure format (qty/price) and Udaan format (quantity/pricePerUnit)
        const rawBulkPrices: BulkPrice[] = (item.bulkPrices || item.bulk_prices || [])
            .map((bp: any) => {
                const exclusivePrice = Number(bp.price || bp.pricePerUnit || 0);
                return {
                    qty: Number(bp.qty || bp.quantity || 0),
                    price: isHyperpure
                        ? getHyperpureTaxInclusiveRate(exclusivePrice, taxDetails)
                        : exclusivePrice,
                    type: bp.type || ''
                };
            })
            .filter((bp: BulkPrice) => bp.qty > 1 && bp.price > 0)
            .sort((a: BulkPrice, b: BulkPrice) => a.qty - b.qty);

        // Parse available order quantity — empty string or undefined means infinite (no limit)
        const rawAvlOrderQty = item.avlOrderQty;
        const parsedAvlOrderQty = (rawAvlOrderQty !== undefined && rawAvlOrderQty !== null && rawAvlOrderQty !== '' && !isNaN(Number(rawAvlOrderQty)))
            ? Number(rawAvlOrderQty)
            : undefined;

        // Parse max order qty — treat very large values (e.g. 2147483647) as no limit
        const rawMaxOrderQty = item.maximumOrderQty;
        const parsedMaxOrderQty = (rawMaxOrderQty && rawMaxOrderQty < 2147483647) ? rawMaxOrderQty : undefined;

        // Effective max is the lower of maximumOrderQty and avlOrderQty (if both are present)
        let effectiveMaxQty: number | undefined = parsedMaxOrderQty;
        if (parsedAvlOrderQty !== undefined) {
            effectiveMaxQty = effectiveMaxQty ? Math.min(effectiveMaxQty, parsedAvlOrderQty) : parsedAvlOrderQty;
        }

        return {
            item_name: item.item_name || item.itemName || item.name || '',
            sku_id: itemId,
            sku: item.sku || item.productNumber || item.product_number || '',
            rate: baseRate,
            baseRate: baseRate,
            mrp: Number(item.mrp || item.original_price || item.originalPrice || baseRate || 0),
            price: baseRate,
            unit: item.unit || item.uom || item.subUom || item.sub_uom || 'kg',
            quantity: Number(item.quantity || item.qty || 1),
            image_url: imageUrl,
            product_images: typeof itemProductImages === 'string' ? itemProductImages : '',
            platform: String(item.platform || supplierId || '').toLowerCase(),
            isBestMatch: false,
            is_in_stock: item.isInStock !== undefined ? item.isInStock : true,
            bulkPrices: rawBulkPrices,
            supplierName: item.supplierName || item.supplier_name || '',
            supplierId: item.supplier_id || item.supplierId || '',
            category_id: item.categoryId || '',
            vertical: item.vertical || '',
            minimumOrderQty: this.readMinimumOrderQty(item),
            maximumOrderQty: effectiveMaxQty,
            avlOrderQty: parsedAvlOrderQty,
            product_number: item.product_number || item.productNumber || item.sku || '',
            article_number: item.article_number || item.articleNumber || item.dmartArticleNumber || '',
            dmartArticleNumber: item.dmartArticleNumber || item.article_number || item.articleNumber || '',
            swiggyItemId: sId === 'swiggy' ? String(item.itemId || item.item_id || itemId || '').trim() : undefined,
            swiggyProductId: sId === 'swiggy' ? String(item.productId || item.product_id || item.productNumber || item.product_number || item.itemId || item.item_id || itemId || '').trim() : undefined,
            swiggySpin: sId === 'swiggy' ? String(item.spin || item.itemId || item.item_id || itemId || '').trim() : undefined
        };
    }

    /**
     * Updates the market best price display.
     */
    private updateMarketBestPrice(): void {
        this.refreshMarketBestPriceForActiveSearch();
    }

    /**
     * Recalculates the best market price based on currently active search results across all suppliers.
     */
    refreshMarketBestPriceForActiveSearch(): void {

        const activeItem = this.searchItems.find(item => item.name === this.activeSearchItem);

        // Prioritize API's recommended best match
        if (activeItem?.bestMatch) {
            const best = activeItem.bestMatch;

            // Skip if best match has zero price (invalid data)
            if (!best.rate || best.rate <= 0) {
                this.marketBestPrice = null;
                return;
            }

            // Find which section it belongs to for the label
            const section = this.supplierSections.find(s => s.id.toLowerCase() === (best.platform || '').toLowerCase())
                || this.supplierSections.find(s => s.id === 'local');

            // Calculate price analytics for API's best match
            const allPrices: number[] = [];
            let nextCheapest: SupplierProduct | null = null;
            this.supplierSections.forEach((sec: SupplierSection) => {
                (this.getItemsForActiveSearch(sec) as SupplierProduct[]).forEach((item: SupplierProduct) => {
                    if (item.rate > 0) {
                        allPrices.push(item.rate);
                        // Find next cheapest (excluding the best match)
                        if (item.rate !== best.rate) {
                            if (!nextCheapest || item.rate < nextCheapest.rate) {
                                nextCheapest = item;
                            }
                        }
                    }
                });
            });
            const savingsFromNextBest = nextCheapest ? nextCheapest.rate - best.rate : 0;

            // Calculate average market rate (excluding the best price)
            const otherPrices = allPrices.filter(price => price !== best.rate);
            const avgMarketRate = otherPrices.length > 0
                ? otherPrices.reduce((sum, price) => sum + price, 0) / otherPrices.length
                : 0;

            // Calculate percentage lower than average
            const savingsPercent = avgMarketRate > 0
                ? Math.round(((avgMarketRate - best.rate) / avgMarketRate) * 100)
                : 0;

            this.marketBestPrice = {
                item_name: best.item_name,
                supplier: section ? `${section.name}` : (best.platform === 'supplier_rate_card' ? 'Local Suppliers' : ''),
                supplierId: section?.id || (best.platform === 'supplier_rate_card' ? 'local' : undefined),
                rate: best.rate,
                mrp: best.mrp,
                unit: best.unit,
                image_url: best.image_url,
                product_images: best.product_images || '',
                savings: savingsFromNextBest > 0 ? `₹${savingsFromNextBest.toFixed(0)} cheaper than next best` : undefined,
                savingsPercent: savingsPercent > 0 ? savingsPercent : undefined,
                originalItem: best,
                originalSection: section
            };
            return;
        }

        this.marketBestPrice = null;
    }

    /**
     * Updates the calculated price range for a search item based on all findings.
     * @param searchItem The search item to update.
     */
    private updateSearchItemPrices(searchItem: SearchItem): void {
        let minPrice = Infinity;
        let maxPrice = 0;

        this.supplierSections.forEach(section => {
            section.items
                .filter(item => item.searchItemName === searchItem.name)
                .forEach(item => {
                    if (item.rate > 0) {
                        minPrice = Math.min(minPrice, item.rate);
                        maxPrice = Math.max(maxPrice, item.rate);
                    }
                });
        });

        if (minPrice !== Infinity) {
            searchItem.minPrice = minPrice;
            searchItem.maxPrice = maxPrice;
            searchItem.priceRange = `₹${minPrice.toFixed(0)} - ₹${maxPrice.toFixed(0)}`;
        }
    }

    /**
    * Toggles card selection state on click.
    * @param item The product card to select/deselect.
    * @param event The click event.
    */
    toggleCardSelection(item: SupplierProduct, event?: Event): void {
        // Prevent event bubbling if needed
        if (event) {
            event.stopPropagation();
        }

        // Deselect previously selected card
        if (this.selectedCard && this.selectedCard !== item) {
            this.selectedCard.isSelected = false;
        }

        // Toggle current card selection
        item.isSelected = !item.isSelected;
        this.selectedCard = item.isSelected ? item : null;
    }

    /**
     * Opens the item selection drawer for a specific supplier section.
     * @param section The supplier section.
     * @param item Optional product to increment quantity for upon opening.
     */
    openDrawer(section: SupplierSection, item?: SupplierProduct): void {
        this.selectedSection = section;
        this.isDrawerOpen = true;
        this.drawerSearchText = '';
        this.drawerSortOption = 'relevance';
        document.body.style.overflow = 'hidden';

        // Close mobile sidebar when opening drawer
        if (this.isMobileView && this.isSidebarOpen) {
            this.isSidebarOpen = false;
        }

        // Add to cart if opening via Add button
        if (item) {
            if (section.id === 'dmart' || section.id === 'hyperpure' || section.id === 'udaan' || section.id === 'deliverit' || section.id === 'swiggy') {
                this.addToCart(item, section);
            } else {
                // For local, just increment locally (existing behavior)
                let targetItem = section.items.find(i => i === item);
                if (!targetItem && item.sku_id) {
                    targetItem = section.items.find(i => i.sku_id === item.sku_id);
                }
                const finalItem = targetItem || item;
                finalItem.selectedQuantity = (finalItem.selectedQuantity || 0) + 1;
                this.sharedService.successAlert(`Added "${finalItem.item_name}" to cart`);

                // Track cart add in analytics DB
                this.pmAnalytics.trackCartAdd(section.id || 'unknown', {
                    itemId: String(finalItem.sku_id || ''),
                    itemName: finalItem.item_name || '',
                    quantity: finalItem.selectedQuantity || 1,
                    unitPrice: finalItem.rate || 0,
                    itemUnit: finalItem.unit || '',
                    cartValueAfter: 0,
                    cartItemCountAfter: 0,
                });
            }
        }

        if (this.isDemoMobileView) {
            setTimeout(() => this.attachDemoDrawerToBody(), 0);
        }
    }

    /**
     * Returns the effective per-unit rate for a given quantity based on bulk pricing tiers.
     * Falls back to the item's base rate if no bulk tier applies.
     */
    getEffectiveRate(item: SupplierProduct, qty?: number): number {
        const quantity = qty !== undefined ? qty : (item.selectedQuantity || 1);
        const baseRate = item.baseRate || item.rate;
        if (!item.bulkPrices || item.bulkPrices.length === 0) {
            return baseRate;
        }
        // bulkPrices is sorted ascending by qty
        let effectivePrice = baseRate;
        for (const bp of item.bulkPrices) {
            if (quantity >= bp.qty) {
                effectivePrice = bp.price;
            } else {
                break;
            }
        }
        return effectivePrice;
    }

    /**
     * Updates item.rate to the effective bulk rate for the current selectedQuantity.
     */
    private applyBulkRate(item: SupplierProduct): void {
        if (item.bulkPrices && item.bulkPrices.length > 0) {
            item.rate = this.getEffectiveRate(item);
        }
    }

    /**
     * True when the current quantity has reached a bulk tier.
     * Checkout lines that came from the cart API borrow tiers from the matching search result.
     */
    showsBulkOffer(item: SupplierProduct): boolean {
        const priced = this.itemWithBulkPrices(item);
        const quantity = Number(item.selectedQuantity) || 0;
        if (priced.bulkPrices && priced.bulkPrices.length > 0 && quantity > 0) {
            return priced.bulkPrices.some(tier => quantity >= Number(tier.qty) && Number(tier.qty) > 1);
        }
        const base = Number(item.baseRate) || Number(priced.baseRate) || 0;
        const current = this.getCartDisplayRate(item);
        return base > 0 && current > 0 && current < base - 0.009;
    }

    private itemWithBulkPrices(item: SupplierProduct): SupplierProduct {
        if (item.bulkPrices && item.bulkPrices.length > 0) {
            return item;
        }
        const keys = new Set(this.cartIdentityKeys(item));
        if (keys.size === 0) {
            return item;
        }
        for (const section of this.supplierSections) {
            if (item.platform && section.id !== item.platform) {
                continue;
            }
            const match = (section.items || []).find(candidate =>
                !!candidate.bulkPrices?.length &&
                this.cartIdentityKeys(candidate).some(key => keys.has(key))
            );
            if (match) {
                return match;
            }
        }
        return item;
    }

    /**
     * Returns the effective rate for a cart item.
     * For Hyperpure items, uses the tax-inclusive rate from the cart API if available.
     * For other suppliers, uses the effective bulk rate.
     */
    getCartDisplayRate(item: SupplierProduct): number {
        if (item.platform === 'hyperpure' && this.hyperpureSupplier?.apiRateMap) {
            const apiRate = this.hyperpureSupplier.apiRateMap.get(String(item.sku_id));
            if (apiRate !== undefined && apiRate > 0) {
                return apiRate;
            }
        }
        return this.getEffectiveRate(item);
    }

    /**
     * Returns the cart_price_per_unit string for a cart item.
     * For Hyperpure items, looks up from the API cart data.
     */
    getCartPricePerUnit(item: SupplierProduct): string {
        if (item.platform === 'hyperpure' && this.hyperpureSupplier?.apiCartPricePerUnitMap) {
            return this.hyperpureSupplier.apiCartPricePerUnitMap.get(String(item.sku_id)) || item.cart_price_per_unit || '';
        }
        return item.cart_price_per_unit || '';
    }

    /**
     * Returns the next bulk price tier the user can unlock by ordering more.
     * Returns null if no further tier is available.
     */
    getNextBulkTier(item: SupplierProduct): BulkPrice | null {
        if (!item.bulkPrices || item.bulkPrices.length === 0) return null;
        const qty = item.selectedQuantity || 0;
        for (const bp of item.bulkPrices) {
            if (qty < bp.qty) {
                return bp;
            }
        }
        return null;
    }

    /**
     * Checks if a specific bulk tier is the one currently being applied.
     * Only the highest qualifying tier should be marked active.
     */
    isActiveBulkTier(item: SupplierProduct, tier: BulkPrice): boolean {
        if (!item.bulkPrices || !item.selectedQuantity || item.selectedQuantity < tier.qty) {
            return false;
        }
        // Find the highest qualifying tier
        let activeTier: BulkPrice | null = null;
        for (const bp of item.bulkPrices) {
            if (item.selectedQuantity >= bp.qty) {
                activeTier = bp;
            } else {
                break;
            }
        }
        return activeTier === tier;
    }

    /**
     * Closes the item selection drawer.
     */
    closeDrawer(): void {
        this.restoreDemoDrawer();
        this.isDrawerOpen = false;
        // Only reset body overflow if mobile sidebar is also closed
        if (!this.isSidebarOpen || !this.isMobileView) {
            document.body.style.overflow = '';
        }
        setTimeout(() => {
            this.selectedSection = null;
        }, 300); // Wait for transition
    }

    private itemPlatform(item: any, section?: any): string {
        return String(item?.platform || section?.id || '').toLowerCase();
    }

    /**
     * Updates the selected quantity of a product.
     * @param item The product item.
     * @param change The change amount (positive or negative).
     */
    updateQuantity(item: SupplierProduct, change: number, section?: any): void {
        if (this.isDemoMobileView) {
            if (!item.selectedQuantity) {
                item.selectedQuantity = 0;
            }
            item.selectedQuantity = Math.max(0, item.selectedQuantity + change);
            this.upsertDemoLocalCartItem(item, section);
            return;
        }
        if (this.isDemoPreviewSupplier(item.platform)) {
            return;
        }

        if (!item.selectedQuantity) item.selectedQuantity = 0;
        const previousQuantity = item.selectedQuantity;
        const minQty = this.resolveMinimumOrderQty(item);

        // Enforce maximum order quantity
        if (change > 0 && item.maximumOrderQty && (item.selectedQuantity + change) > item.maximumOrderQty) {
            this.sharedService.warningAlert(`Maximum order quantity is ${item.maximumOrderQty}`);
            return;
        }

        // At the minimum, minus removes the line instead of stepping below it
        if (change < 0 && (previousQuantity + change < 1 || (minQty > 1 && previousQuantity <= minQty))) {
            item.selectedQuantity = 0;
        } else {
            item.selectedQuantity += change;
            if (item.selectedQuantity < 0) item.selectedQuantity = 0;
        }
        this.syncDuplicateCartQuantity(item);

        // Apply bulk pricing based on new quantity
        this.applyBulkRate(item);

        item.cartError = undefined; // Clear any previous errors

        // Track cart add in analytics DB
        if (change > 0) {
            this.pmAnalytics.trackCartAdd(item.platform || 'unknown', {
                itemId: String(item.sku_id || ''),
                itemName: item.item_name || '',
                quantity: item.selectedQuantity || 0,
                unitPrice: item.rate || 0,
                itemUnit: item.unit || '',
                cartValueAfter: 0,
                cartItemCountAfter: 0,
            });
        }

        // Delegate to supplier-specific child component after the user pauses
        const action = change > 0 ? 'add' : 'remove';
        const platform = this.itemPlatform(item, section);
        if (platform === 'hyperpure' && item.sku_id && this.hyperpureSupplier) {
            this.hyperpureSupplier.updateQuantity(item, change, previousQuantity);
            this.highlightCart('hyperpure', action);
        } else if (platform === 'deliverit' && item.sku_id && this.deliveritSupplier) {
            this.deliveritSupplier.updateQuantity(item, change, previousQuantity);
            this.highlightCart('deliverit', action);
        } else if (platform === 'dmart' && item.sku_id && this.dmartSupplier) {
            this.dmartSupplier.updateQuantity(item, change, previousQuantity);
            this.highlightCart('dmart', action);
        } else if (platform === 'udaan' && item.sku_id && this.udaanSupplier) {
            this.udaanSupplier.updateQuantity(item, change, previousQuantity);
            this.highlightCart('udaan', action);
        } else if (platform === 'swiggy' && (item.sku_id || item.swiggyItemId) && this.swiggyAssureSupplier) {
            this.swiggyAssureSupplier.updateQuantity(item, change, previousQuantity);
            this.highlightCart('swiggy', action);
        } else {
            // For non-Hyperpure/non-DMart items (local/udaan)
            if (change > 0 && item.selectedQuantity === 1) {
                this.sharedService.successAlert(`Added "${item.item_name}" to cart`);
            }
            this.refreshCheckoutCart();
            this.highlightCart(item.platform || 'local', action);
        }
    }

    /**
     * Adds an item to the cart and shows a success alert.
     * @param item The item to add.
     * @param section The supplier section the item belongs to.
     */
    addToCart(item: any, section: any): void {
        if (this.isDemoMobileView) {
            const minQty = item.minimumOrderQty && item.minimumOrderQty > 1 ? item.minimumOrderQty : 1;
            item.selectedQuantity = Math.max(Number(item.selectedQuantity) || 0, minQty);
            if ((Number(item.selectedQuantity) || 0) < 1) {
                item.selectedQuantity = 1;
            }
            this.upsertDemoLocalCartItem(item, section);
            return;
        }
        if (this.isDemoPreviewSupplier(section?.id || item?.platform)) {
            return;
        }

        // Reuse drawer logic for consistency
        const itemName = item?.item_name || 'Item';
        const supplierName = section?.name || item?.supplier || 'Supplier';

        if (item.rate) {
            const previousQuantity = item.selectedQuantity || 0;

            // Enforce maximum order quantity
            if (item.maximumOrderQty && (previousQuantity + 1) > item.maximumOrderQty) {
                this.sharedService.warningAlert(`Maximum order quantity is ${item.maximumOrderQty}`);
                return;
            }

            // Use minimumOrderQty as initial quantity when first adding
            if (previousQuantity === 0 && item.minimumOrderQty && item.minimumOrderQty > 1) {
                item.selectedQuantity = item.minimumOrderQty;
            } else {
                item.selectedQuantity = previousQuantity + 1;
            }
            // Apply bulk pricing based on new quantity
            this.applyBulkRate(item);
            item.cartError = undefined; // Clear any previous errors

            // Track cart add in analytics DB
            this.pmAnalytics.trackCartAdd(item.platform || section?.id || 'unknown', {
                itemId: String(item.sku_id || ''),
                itemName: item.item_name || itemName,
                quantity: item.selectedQuantity || 1,
                unitPrice: item.rate || 0,
                itemUnit: item.unit || '',
                cartValueAfter: 0,
                cartItemCountAfter: 0,
            });

            // Delegate to supplier-specific child component
            const platform = this.itemPlatform(item, section);
            if (platform === 'hyperpure' && item.sku_id && this.hyperpureSupplier) {
                this.hyperpureSupplier.addToCart(item, supplierName, previousQuantity);
                this.highlightCart('hyperpure', 'add');
            } else if (platform === 'deliverit' && item.sku_id && this.deliveritSupplier) {
                this.deliveritSupplier.addToCart(item, supplierName, previousQuantity);
                this.highlightCart('deliverit', 'add');
            } else if (platform === 'dmart' && item.sku_id && this.dmartSupplier) {
                this.dmartSupplier.addToCart(item, supplierName, previousQuantity);
                this.highlightCart('dmart', 'add');
            } else if (platform === 'udaan' && item.sku_id && this.udaanSupplier) {
                this.udaanSupplier.addToCart(item, supplierName, previousQuantity);
                this.highlightCart('udaan', 'add');
            } else if (platform === 'swiggy' && (item.sku_id || item.swiggyItemId) && this.swiggyAssureSupplier) {
                this.swiggyAssureSupplier.addToCart(item, supplierName, previousQuantity);
                this.highlightCart('swiggy', 'add');
            } else {
                // For non-Hyperpure/non-DMart items (local/udaan), open drawer
                this.sharedService.successAlert(`Added "${itemName}" from ${supplierName} to cart`);
                this.highlightCart(section?.id || 'local', 'add');
            }
        }
    }

    /**
     * Highlights the cart section for a specific platform with a brief animation.
     * @param platform The platform identifier (hyperpure, dmart, udaan, local, deliverit)
     * @param action The action type ('add' for filling effect, 'remove' for pop out effect)
     */
    highlightCart(platform: string, action: 'add' | 'remove' = 'add'): void {
        // Clear any existing timeout
        if (this.highlightTimeout) {
            clearTimeout(this.highlightTimeout);
        }

        // Set the highlighted platform and action
        this.highlightedCartPlatform = platform;
        this.cartHighlightAction = action;

        // Remove highlight after animation completes
        this.highlightTimeout = setTimeout(() => {
            this.highlightedCartPlatform = null;
            this.cartHighlightAction = null;
        }, 1200);
    }

    /**
     * Opens the item selection drawer for the product identified as the market best price.
     */
    openBestPriceDrawer(): void {
        if (this.isDemoMobileView) {
            this.addMarketBestToCart();
            return;
        }
        if (this.marketBestPrice?.originalSection && this.marketBestPrice?.originalItem) {
            this.openDrawer(this.marketBestPrice.originalSection, this.marketBestPrice.originalItem);
        } else if (this.marketBestPrice?.supplierId) {
            const section = this.supplierSections.find(s => s.id === this.marketBestPrice?.supplierId);
            if (section) {
                this.openDrawer(section);
            }
        }
    }

    addMarketBestToCart(): void {
        const item = this.marketBestPrice?.originalItem || this.buildMarketBestCartItem();
        const section = this.marketBestPrice?.originalSection
            || this.supplierSections.find(row => row.id === this.marketBestPrice?.supplierId)
            || this.buildMarketBestSection();
        if (!item) {
            return;
        }
        this.addToCart(item, section);
    }

    updateMarketBestQuantity(change: number): void {
        const item = this.marketBestPrice?.originalItem || this.buildMarketBestCartItem();
        const section = this.marketBestPrice?.originalSection
            || this.supplierSections.find(row => row.id === this.marketBestPrice?.supplierId)
            || this.buildMarketBestSection();
        if (!item) {
            return;
        }
        this.updateQuantity(item, change, section);
    }

    get marketBestCartQuantity(): number {
        const item = this.marketBestPrice?.originalItem;
        if (item) {
            return Number(item.selectedQuantity) || 0;
        }
        if (!this.marketBestPrice) {
            return 0;
        }
        const sku = this.demoItemSku(this.marketBestPrice);
        for (const cart of this.demoLocalCarts) {
            const match = (cart.items || []).find((row: any) => this.demoItemSku(row) === sku);
            if (match) {
                return Number(match.selectedQuantity) || 0;
            }
        }
        return 0;
    }

    private buildMarketBestCartItem(): any | null {
        const best = this.marketBestPrice;
        if (!best) {
            return null;
        }
        const original = best.originalItem as any;
        return {
            item_name: best.item_name,
            itemName: best.item_name,
            rate: best.rate,
            unit: best.unit,
            sku_id: original?.sku_id || original?.skuId || best.item_name,
            skuId: original?.sku_id || original?.skuId || best.item_name,
            platform: best.supplierId,
            image_url: best.image_url,
            selectedQuantity: 0
        };
    }

    private buildMarketBestSection(): any {
        return {
            id: this.marketBestPrice?.supplierId || 'local',
            name: (this.marketBestPrice?.supplier || 'Supplier').replace(/^From\s+/i, '')
        };
    }

    private attachDemoDrawerToBody(): void {
        if (!this.isDemoMobileView || this.drawerNodesOnBody.length) {
            return;
        }
        const overlay = this.host.nativeElement.querySelector('.drawer-overlay') as HTMLElement | null;
        const drawer = this.host.nativeElement.querySelector('.sidebar-drawer') as HTMLElement | null;
        [overlay, drawer].forEach(node => {
            if (!node || node.parentElement === document.body) {
                return;
            }
            this.drawerNodesOnBody.push(node);
            document.body.appendChild(node);
        });
    }

    private restoreDemoDrawer(): void {
        this.drawerNodesOnBody.forEach(node => {
            if (node.parentElement === document.body) {
                this.host.nativeElement.appendChild(node);
            }
        });
        this.drawerNodesOnBody = [];
    }

    /**
     * Handles clicking a Quick Add category — fetches items from API and adds them as search tags.
     */
    toggleQuickAddMenu(): void {
        this.isQuickAddOpen = !this.isQuickAddOpen;
    }

    onQuickAddClickFromMenu(category: { label: string; icon: string; key: string; active: boolean; addedItems: string[] }): void {
        this.isQuickAddOpen = false;
        this.onQuickAddClick(category);
    }

    onQuickAddClick(category: { label: string; icon: string; key: string; active: boolean; addedItems: string[] }): void {
        // Toggle off if already active — remove its items
        if (category.active) {
            this.removeQuickAddItems(category);
            return;
        }

        // Deactivate all others is no longer needed — allow multiple active tags
        category.active = true;
        this.saveQuickAddState();

        this.isQuickAddLoading = true;

        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
        const params = {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            dateField: 'invoiceDate'
        };

        let obs$;
        switch (category.key) {
            case 'mostPurchased': obs$ = this.priceDiscoveryService.getMostPurchasedItems(params, this.selectedEntityId); break;
            case 'recentPurchase': obs$ = this.priceDiscoveryService.getRecentPurchaseItems(params, this.selectedEntityId); break;
            case 'recentSearched': {
                const fastTagParams = {
                    start_date: startDate.toISOString(),
                    end_date: endDate.toISOString(),
                    date_field: 'invoiceDate'
                };
                obs$ = this.priceDiscoveryService.getRecentSearchedItems(fastTagParams, this.selectedEntityId);
                break;
            }
            case 'mostSearched': obs$ = this.priceDiscoveryService.getMostSearchedItems(params, this.selectedEntityId); break;
            case 'topOrdered': obs$ = this.priceDiscoveryService.getTopOrderedItems(params, this.selectedEntityId); break;
            default: this.isQuickAddLoading = false; return;
        }

        obs$.pipe(takeUntil(this.destroy$)).subscribe(
            (items: any[]) => {
                this.isQuickAddLoading = false;
                const names: string[] = (items || []).slice(0, 10)
                    .map((item: any) => (item.itemName || item.item_name || item.name || '').trim())
                    .filter((n: string) => !!n);

                if (names.length === 0) {
                    this.sharedService.warningAlert(`No items found for "${category.label}"`);
                    category.active = false;
                    this.saveQuickAddState();
                    return;
                }

                // Populate pending tags (deduplicating against searchItems and existing pending)
                const addedNames: string[] = [];
                names.forEach(name => {
                    const inSearch = this.searchItems.find(i => i.name.toLowerCase() === name.toLowerCase());
                    const inPending = this.pendingSearchTags.find(n => n.toLowerCase() === name.toLowerCase());
                    if (!inSearch && !inPending) {
                        this.pendingSearchTags.push(name);
                        addedNames.push(name);
                    }
                });

                // Track which items this category contributed (used for removal)
                category.addedItems = addedNames;
                this.saveQuickAddState();
            },
            (error: any) => {
                console.error(`Quick Add failed for ${category.key}`, error);
                this.isQuickAddLoading = false;
                category.active = false;
                this.saveQuickAddState();
                this.sharedService.errorAlert(`Failed to load ${category.label}`);
            }
        );
    }

    /** Returns true if any search item is still loading. */
    get isAnyItemLoading(): boolean {
        return this.searchItems.some(item => item.isLoading);
    }

    /** Removes a single item from the pending search tags. */
    removePendingTag(name: string): void {
        this.pendingSearchTags = this.pendingSearchTags.filter(n => n !== name);
        // Also clear the category's addedItems reference if it was the last pending item from that category
        this.quickAddCategories.forEach(cat => {
            if (cat.addedItems) {
                cat.addedItems = cat.addedItems.filter(n => n !== name);
                if (cat.addedItems.length === 0) {
                    cat.active = false;
                }
            }
        });
        this.saveQuickAddState();
    }

    /** Runs compareItem on all pending search tags and clears the pending list. */
    searchPendingItems(): void {
        if (this.pendingSearchTags.length === 0) return;

        const toSearch = [...this.pendingSearchTags];
        this.pendingSearchTags = [];

        toSearch.forEach(name => {
            const exists = this.searchItems.find(i => i.name.toLowerCase() === name.toLowerCase());
            if (!exists) {
                const newItem: SearchItem = { name, isLoading: true };
                this.searchItems.push(newItem);
                this.compareItem(newItem);
            }
        });

        if (this.searchItems.length > 0) {
            this.selectSearchItem(this.searchItems[0]);
        }

        this.persistSearchTags();

        this.sharedService.successAlert(`${toSearch.length} item${toSearch.length > 1 ? 's' : ''} added to search`);
    }

    /** Returns quick-add categories that are currently active (have items added). */
    get activeQuickAdds(): { label: string; icon: string; key: string; active: boolean; addedItems: string[] }[] {
        return this.quickAddCategories.filter(c => c.active && c.addedItems && c.addedItems.length > 0);
    }

    /** Persists all active quick-add states to localStorage. */
    private saveQuickAddState(): void {
        const state = this.quickAddCategories
            .filter(c => c.active && c.addedItems && c.addedItems.length > 0)
            .map(c => ({ key: c.key, addedItems: c.addedItems }));
        if (state.length > 0) {
            localStorage.setItem(`pm_quickAddState_${this.selectedEntityId}`, JSON.stringify(state));
        } else {
            localStorage.removeItem(`pm_quickAddState_${this.selectedEntityId}`);
        }
    }

    /** Restores quick-add states from localStorage. */
    private restoreQuickAddState(): void {
        const raw = localStorage.getItem(`pm_quickAddState_${this.selectedEntityId}`);
        if (!raw) return;
        try {
            const state: { key: string; addedItems: string[] }[] = JSON.parse(raw);
            state.forEach(s => {
                const cat = this.quickAddCategories.find(c => c.key === s.key);
                if (cat) {
                    // Only mark active if some of the saved items still exist in search
                    const remaining = s.addedItems.filter(n =>
                        this.searchItems.some(i => i.name.toLowerCase() === n.toLowerCase())
                    );
                    if (remaining.length > 0) {
                        cat.active = true;
                        cat.addedItems = remaining;
                    }
                }
            });
        } catch (e) {
            localStorage.removeItem(`pm_quickAddState_${this.selectedEntityId}`);
        }
    }

    addNewSearchItem(): void {
        const searchTerm = (this.newItemSearchText || '').trim();
        if (!searchTerm) return;

        // Check if already in list (case insensitive)
        const existingItem = this.searchItems.find(
            i => i.name.toLowerCase() === searchTerm.toLowerCase()
        );

        if (existingItem) {
            this.selectSearchItem(existingItem);
            this.newItemSearchText = '';
            return;
        }

        // Add new search item
        const newItem: SearchItem = { name: searchTerm, isLoading: true };
        this.searchItems.push(newItem);

        // Update URL to include the new tag so a reload doesn't lose it
        this.persistSearchTags();

        this.selectSearchItem(newItem);
        this.compareItem(newItem);
        this.newItemSearchText = '';
    }

    /**
     * Removes an item from the search list and cleans up associated results and socket subscriptions.
     * @param searchItem The search item to remove.
     */
    removeSearchItem(searchItem: SearchItem): void {
        // Stop socket subscription if still active
        if (searchItem.requestId) {
            this.unsubscribeFromSocket(searchItem.requestId);
        }

        // Remove items belonging to this search from all sections
        this.supplierSections.forEach(section => {
            section.items = section.items.filter(item => item.searchItemName !== searchItem.name);
            section.totalItems = section.items.length;
        });

        this.searchItems = this.searchItems.filter(i => i.name !== searchItem.name);

        // Deactivate any Quick Add pill that no longer has any of its items remaining
        this.quickAddCategories.forEach(cat => {
            if (cat.active && cat.addedItems && cat.addedItems.length > 0) {
                const anyRemaining = cat.addedItems.some(
                    n => this.searchItems.some(i => i.name.toLowerCase() === n.toLowerCase())
                );
                if (!anyRemaining) {
                    cat.active = false;
                    cat.addedItems = [];
                    this.saveQuickAddState();
                }
            }
        });

        if (this.searchItems.length === 0) {
            this.goBack();
        } else {
            // If the removed item was active, switch to the first remaining search item
            if (this.activeSearchItem === searchItem.name) {
                this.selectSearchItem(this.searchItems[0]);
            } else {
                this.refreshMarketBestPriceForActiveSearch();
            }
        }
    }

    /**
     * Removes all search items added by a specific Quick Add category.
     * @param category The Quick Add category to remove items for.
     */
    removeQuickAddItems(category: { label: string; icon: string; key: string; active: boolean; addedItems: string[] }): void {
        if (!category.addedItems || category.addedItems.length === 0) {
            category.active = false;
            return;
        }

        // Remove each item that was added by this category
        const namesToRemove = new Set(category.addedItems.map(n => n.toLowerCase()));

        // Remove from pending tags if not yet searched
        this.pendingSearchTags = this.pendingSearchTags.filter(n => !namesToRemove.has(n.toLowerCase()));

        const itemsToRemove = this.searchItems.filter(i => namesToRemove.has(i.name.toLowerCase()));

        itemsToRemove.forEach(searchItem => {
            // Stop socket subscription if still active
            if (searchItem.requestId) {
                this.unsubscribeFromSocket(searchItem.requestId);
            }

            // Remove items belonging to this search from all sections
            this.supplierSections.forEach(section => {
                section.items = section.items.filter(item => item.searchItemName !== searchItem.name);
                section.totalItems = section.items.length;
            });
        });

        this.searchItems = this.searchItems.filter(i => !namesToRemove.has(i.name.toLowerCase()));

        // Reset category state
        category.active = false;
        category.addedItems = [];
        this.saveQuickAddState();

        if (this.searchItems.length === 0) {
            // Reset market best price and stay on page
            this.marketBestPrice = null;
            this.activeSearchItem = '';
        } else {
            // If the active item was removed, switch to the first remaining
            if (!this.searchItems.find(i => i.name === this.activeSearchItem)) {
                this.selectSearchItem(this.searchItems[0]);
            } else {
                this.refreshMarketBestPriceForActiveSearch();
            }
        }

        this.persistSearchTags();
    }

    /**
     * @param sectionId The identifier of the section.
     */
    isStandardSection(sectionId: string): boolean {
        return ['hyperpure', 'dmart', 'udaan', 'deliverit', 'swiggy', 'local'].includes(sectionId);
    }

    /**
     * Sets a search item as active and refreshes the market best price display.
     * @param searchItem The search item to select.
     */
    selectSearchItem(searchItem: SearchItem): void {
        this.activeSearchItem = searchItem.name;
        this.sheetAgentOpen = false;
        this.hideMarketBestBanner = false;
        this.selectedCard = null;
        if (this.resultsPane?.nativeElement) {
            this.resultsPane.nativeElement.scrollTop = 0;
        }

        // Exit fav view mode when selecting a regular search item
        if (searchItem.name !== '__fav__') {
            this.isFavViewMode = false;
        }

        this.refreshMarketBestPriceForActiveSearch();

        // Close mobile sidebar when selecting a search item
        if (this.isMobileView && this.isSidebarOpen) {
            this.isSidebarOpen = false;
            document.body.style.overflow = '';
        }
    }

    trackBySearchGroup(_index: number, group: { label: string }): string {
        return group.label;
    }

    trackBySearchItem(_index: number, item: SearchItem): string {
        return item.name;
    }

    get sheetAgentItems(): SearchItem[] {
        return this.searchItems.filter(item => item.sheetRun === this.sheetAgentRun);
    }

    get searchItemGroups(): { label: string; items: SearchItem[] }[] {
        const sheet = this.searchItems.filter(item => item.sheetRun != null);
        const manual = this.searchItems.filter(item => item.sheetRun == null);
        const groups: { label: string; items: SearchItem[] }[] = [];
        if (sheet.length) {
            groups.push({ label: 'From your sheet', items: sheet });
        }
        if (manual.length) {
            groups.push({ label: 'Searched by you', items: manual });
        }
        return groups;
    }

    get sheetAgentChecked(): number {
        return this.sheetAgentItems.filter(item => item.sheetState && item.sheetState !== 'searching').length;
    }

    get sheetAgentSettled(): boolean {
        const items = this.sheetAgentItems;
        return items.length > 0
            && !this.sheetCartApplyInFlight
            && items.every(item => item.sheetState && item.sheetState !== 'searching' && item.sheetState !== 'matched');
    }

    get sheetAgentTitle(): string {
        if (!this.sheetAgentSettled) {
            return this.sheetCartApplyInFlight ? 'Updating the Hyperpure cart' : 'Checking your sheet';
        }
        const added = this.sheetAgentItems.filter(item => this.sheetGapTone(item) === 'added').length;
        return added > 0 ? `${added} in the Hyperpure cart` : 'Sheet check finished';
    }

    /**
     * Live cart status for a sheet row. Quantity changes replace "Added to cart"
     * with how many are extra or still needed. Removing the line clears the tag.
     */
    sheetGapTone(item: SearchItem): 'searching' | 'adding' | 'missed' | 'found' | 'added' | 'short' | 'excess' | 'clear' | null {
        if (item.sheetRun == null && !item.sheetState) {
            return null;
        }
        if (!item.sheetState || item.sheetState === 'searching') {
            return 'searching';
        }
        if (item.sheetState === 'matched') {
            return 'adding';
        }
        if (item.sheetState === 'missed' || !item.sheetMatchName) {
            return 'missed';
        }
        if (!item.targetQuantity) {
            return 'found';
        }
        const cartQty = this.sheetLineCartQuantity(item);
        const target = item.targetQuantity;
        if (cartQty <= 0) {
            return 'clear';
        }
        if (cartQty === target) {
            return 'added';
        }
        return cartQty > target ? 'excess' : 'short';
    }

    sheetAgentLabel(item: SearchItem): string {
        const tone = this.sheetGapTone(item);
        if (!tone) {
            return '';
        }
        const gap = Math.abs(this.sheetLineCartQuantity(item) - (item.targetQuantity || 0));
        switch (tone) {
            case 'searching':
                return 'Searching';
            case 'adding':
                return 'Adding to cart';
            case 'missed':
                return 'No close match';
            case 'found':
                return 'Found';
            case 'added':
                return 'Added to cart';
            case 'excess':
                return `${gap} excess`;
            case 'short':
                return `${gap} more required`;
            default:
                return '';
        }
    }

    private sheetLineCartQuantity(item: SearchItem): number {
        const sku = String(item.sheetMatchSku || '').trim();
        const name = (item.sheetMatchName || '').replace(/\s+/g, ' ').trim().toLowerCase();
        const section = this.supplierSections.find(entry => entry.id === 'hyperpure');
        const live = (section?.items || []).filter(candidate => this.sameSheetProduct(candidate, sku, name));
        if (live.length > 0) {
            return live.reduce((max, candidate) => Math.max(max, Number(candidate.selectedQuantity) || 0), 0);
        }
        const saved = (this.hyperpureSupplier?.apiCartItems || []).filter(candidate => this.sameSheetProduct(candidate, sku, name));
        return saved.reduce((max, candidate) => Math.max(max, Number(candidate.selectedQuantity) || 0), 0);
    }

    private sameSheetProduct(candidate: SupplierProduct, sku: string, name: string): boolean {
        const candidateSku = String(candidate.sku_id || '').trim();
        const candidateName = (candidate.item_name || '').replace(/\s+/g, ' ').trim().toLowerCase();
        return (!!sku && candidateSku === sku) || (!!name && candidateName === name);
    }

    openSheetAgentItem(item: SearchItem): void {
        if (item.sheetState === 'searching') {
            return;
        }
        this.selectSearchItem(item);
        if (this.sheetAgentSettled) {
            this.sheetAgentOpen = false;
        }
    }

    closeSheetAgent(): void {
        this.sheetAgentOpen = false;
    }

    reopenSheetAgent(): void {
        if (this.sheetAgentItems.length > 0) {
            this.sheetAgentOpen = true;
        }
    }

    refreshHyperpureCart(): void {
        if (this.hyperpureSupplier) {
            this.hyperpureSupplier.fetchCart();
        }
    }

    /**
     * Adds uploaded rows to the current Purchase Manager search.
     * Cart quantities are applied after those searches finish.
     */
    searchUploadedSheet(rows: HyperpureSheetSearchRow[]): void {
        if (!rows || rows.length === 0) {
            return;
        }
        this.sheetAgentRun += 1;
        this.sheetAgentOpen = true;
        this.sheetCartNote = '';
        rows.forEach(row => this.addSheetSearchRow(row, this.sheetAgentRun));
        if (this.searchItems.length > 0 && !this.searchItems.some(item => item.name === this.activeSearchItem)) {
            this.selectSearchItem(this.searchItems[0]);
        }
        this.persistSearchTags();
        this.checkAllSearchesComplete();
    }

    private addSheetSearchRow(row: HyperpureSheetSearchRow, run: number): void {
        const unit = (row.unit || '').trim();
        const quantity = row.quantity && row.quantity > 0 ? row.quantity : undefined;
        const sameName = this.searchItems.filter(item =>
            (item.sheetItemName || item.name).toLowerCase() === row.item.toLowerCase()
        );
        const sameUnit = sameName.find(item => normalizeUnit(item.unit || '') === normalizeUnit(unit));
        if (sameUnit) {
            sameUnit.unit = unit || undefined;
            sameUnit.targetQuantity = quantity;
            sameUnit.sheetItemName = row.item;
            sameUnit.sheetRun = run;
            sameUnit.sheetState = 'searching';
            sameUnit.sheetMatchName = undefined;
            sameUnit.sheetMatchSku = undefined;
            sameUnit.sheetMatchRate = undefined;
            sameUnit.sheetCartHandled = false;
            if (!sameUnit.isLoading) {
                this.clearSearchResults(sameUnit);
                sameUnit.isLoading = true;
                this.compareItem(sameUnit);
            }
            return;
        }

        const name = sameName.length > 0 && unit ? `${row.item} (${unit})` : row.item;
        const searchItem: SearchItem = {
            name,
            sheetItemName: row.item,
            sheetRun: run,
            sheetState: 'searching',
            unit: unit || undefined,
            targetQuantity: quantity,
            isLoading: true
        };
        this.searchItems.push(searchItem);
        this.compareItem(searchItem);
    }

    private clearSearchResults(searchItem: SearchItem): void {
        this.supplierSections.forEach(section => {
            section.items = section.items.filter(item => item.searchItemName !== searchItem.name);
            section.totalItems = section.items.length;
        });
    }

    /**
     * After Purchase Manager search finishes, set Hyperpure cart quantities
     * when the result name is at least an 80% match. Unit is required only when the sheet has one.
     * Rows with no quantity are searched and then left out of the cart.
     */
    private applySheetQuantitiesToHyperpureCart(): void {
        const pending = this.searchItems.filter(item => item.targetQuantity && !item.sheetCartHandled);
        if (this.sheetCartApplyInFlight) {
            return;
        }
        if (pending.length === 0 || !this.isHyperpureEnabled) {
            this.publishSheetCartNote(0, 0, 0, 0, 0);
            return;
        }
        this.sheetCartApplyInFlight = true;
        pending.forEach(item => item.sheetCartHandled = true);

        this.priceDiscoveryService.getCart('', 'DELIVERY', this.selectedEntityId)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(
                (cart) => this.writeSheetQuantities(pending, readCartQuantities(cart)),
                () => {
                    this.sheetCartNote = 'Could not read the Hyperpure cart, so quantities were left as they are.';
                    this.sheetAgentItems.forEach(item => {
                        if (item.sheetState === 'matched') {
                            item.sheetState = 'found';
                        }
                    });
                    this.sheetCartApplyInFlight = false;
                }
            );
    }

    private writeSheetQuantities(pending: SearchItem[], cartQuantities: Map<string, number>): void {
        const run = this.sheetAgentRun;
        let already = 0;
        let notFound = 0;
        const queued = new Map<string, { skuId: number | string; quantity: number; match: SupplierProduct; previous: number; item: string; owners: SearchItem[] }>();

        pending.forEach(searchItem => {
            const match = this.findHyperpureMatch(searchItem);
            if (!match || !match.sku_id) {
                searchItem.sheetState = 'missed';
                notFound += 1;
                return;
            }
            const current = this.sheetCartQuantity(cartQuantities, match);
            const target = Number(searchItem.targetQuantity) || 0;
            const key = String(match.sku_id);
            const existing = queued.get(key);
            if (existing) {
                existing.owners.push(searchItem);
                if (target > existing.quantity) {
                    existing.quantity = target;
                }
                return;
            }
            if (current === target) {
                searchItem.sheetState = 'already';
                searchItem.sheetMatchName = match.item_name;
                searchItem.sheetMatchSku = String(match.sku_id || '');
                searchItem.sheetMatchRate = match.rate;
                match.selectedQuantity = current;
                already += 1;
                return;
            }
            queued.set(key, {
                skuId: /^\d+$/.test(key) ? Number(match.sku_id) : match.sku_id,
                quantity: target,
                match,
                previous: current,
                item: match.item_name,
                owners: [searchItem]
            });
        });
        const writes = Array.from(queued.values());

        if (writes.length === 0) {
            this.publishSheetCartNote(0, 0, already, notFound, 0, run);
            this.sheetCartApplyInFlight = false;
            return;
        }

        this.priceDiscoveryService.addMultipleItemsToCart(
            writes.map(write => ({
                skuId: write.skuId,
                quantity: write.quantity,
                deliveryMode: 'DELIVERY'
            })),
            this.selectedEntityId
        ).pipe(take(1), takeUntil(this.destroy$)).subscribe(
            (response) => {
                const failures = new Map<string, string>();
                (response?.errors || []).forEach((error: any) => {
                    const id = error?.skuId ?? error?.productId;
                    if (id != null) {
                        failures.set(String(id), error?.error?.message || error?.message || 'Could not update the cart');
                    }
                });
                let added = 0;
                let updated = 0;
                let failed = 0;
                writes.forEach(write => {
                    if (failures.has(String(write.skuId))) {
                        failed += 1;
                        write.match.cartError = failures.get(String(write.skuId));
                        write.owners.forEach(owner => owner.sheetState = 'found');
                        return;
                    }
                    write.match.selectedQuantity = write.quantity;
                    write.owners.forEach(owner => {
                        owner.sheetState = 'added';
                        owner.sheetMatchName = write.match.item_name;
                        owner.sheetMatchSku = String(write.match.sku_id || '');
                        owner.sheetMatchRate = write.match.rate;
                    });
                    if (write.previous > 0) {
                        updated += write.owners.length;
                    } else {
                        added += write.owners.length;
                    }
                });
                this.hyperpureSupplier?.fetchCart();
                this.publishSheetCartNote(added, updated, already, notFound, failed, run);
                this.sheetCartApplyInFlight = false;
            },
            () => {
                this.sheetCartNote = 'The Hyperpure cart could not be updated.';
                this.sheetAgentItems.forEach(item => {
                    if (item.sheetState === 'matched') {
                        item.sheetState = 'found';
                    }
                });
                this.sheetCartApplyInFlight = false;
            }
        );
    }

    private findHyperpureMatch(searchItem: SearchItem): SupplierProduct | null {
        const wantedName = searchItem.sheetItemName || searchItem.name;
        const wantedUnit = (searchItem.unit || '').trim();
        const section = this.supplierSections.find(item => item.id === 'hyperpure');
        const candidates = (section?.items || []).filter(item => item.searchItemName === searchItem.name && item.item_name);
        let best: { item: SupplierProduct; score: number } | null = null;

        candidates.forEach(item => {
            if (wantedUnit && normalizeUnit(item.unit) !== normalizeUnit(wantedUnit)) {
                return;
            }
            const score = nameSimilarity(wantedName, item.item_name);
            if (score < NAME_MATCH_THRESHOLD) {
                return;
            }
            const inStock = item.is_in_stock !== false;
            const bestInStock = best ? best.item.is_in_stock !== false : false;
            if (!best || score > best.score || (score === best.score && inStock && !bestInStock)) {
                best = { item, score };
            }
        });

        return best ? best.item : null;
    }

    private sheetCartQuantity(cartQuantities: Map<string, number>, item: SupplierProduct): number {
        const keys = [item.sku_id, item.product_number].filter(key => key != null && String(key).trim() !== '').map(key => String(key));
        for (const key of keys) {
            if (cartQuantities.has(key)) {
                return cartQuantities.get(key) || 0;
            }
        }
        return Number(item.selectedQuantity) || 0;
    }

    /**
     * Rows with no sheet quantity are still searched, but they never enter the cart write.
     * Count them here so the note covers every uploaded row.
     */
    private sheetRowsOutsideCartWrite(): { missed: number; searchedOnly: number } {
        const rows = this.sheetAgentItems.filter(item => !item.targetQuantity && item.sheetState && item.sheetState !== 'searching' && item.sheetState !== 'matched');
        return {
            missed: rows.filter(item => item.sheetState === 'missed' || !item.sheetMatchName).length,
            searchedOnly: rows.filter(item => item.sheetState !== 'missed' && !!item.sheetMatchName).length
        };
    }

    private publishSheetCartNote(added: number, updated: number, already: number, notFound: number, failed: number, run = this.sheetAgentRun): void {
        if (!this.sheetAgentItems.length || run !== this.sheetAgentRun || this.sheetNoteRun === run) {
            return;
        }
        const extra = this.sheetRowsOutsideCartWrite();
        const note = this.sheetCartSummary(added, updated, already, notFound + extra.missed, failed, extra.searchedOnly);
        if (!note) {
            return;
        }
        this.sheetCartNote = note;
        this.sheetNoteRun = this.sheetAgentRun;
    }

    private sheetCartSummary(added: number, updated: number, already: number, notFound: number, failed: number, searchedOnly = 0): string {
        const parts: string[] = [];
        if (added > 0) {
            parts.push(`${added} added to the Hyperpure cart`);
        }
        if (updated > 0) {
            parts.push(`${updated} ${updated === 1 ? 'quantity' : 'quantities'} updated`);
        }
        if (already > 0) {
            parts.push(`${already} already in the cart`);
        }
        if (searchedOnly > 0) {
            parts.push(`${searchedOnly} searched with no quantity`);
        }
        if (notFound > 0) {
            parts.push(`${notFound} had no close Hyperpure name match`);
        }
        if (failed > 0) {
            parts.push(`${failed} could not be added`);
        }
        return parts.join('. ') + (parts.length ? '.' : '');
    }

    /**
     * Navigates back to the purchase manager landing page.
     */
    goBack(): void {
        if (this.isLeavingPage || !this.router.url.includes('/price-discovery')) {
            return;
        }
        this.isLeavingPage = true;
        this.sheetAgentOpen = false;
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        this.router.navigate([this.pmDemoMode.basePath], { queryParams: {} });
    }

    private persistSearchTags(): void {
        if (this.isLeavingPage || !this.router.url.includes('/price-discovery')) {
            return;
        }
        const tags = this.searchItems.map(item => encodePmSearchTag(item)).join('$$,$$');
        rememberPriceDiscoveryTags(tags);
        const currentState = history.state && typeof history.state === 'object' ? history.state : {};
        history.replaceState({ ...currentState, ...priceDiscoveryTagsNavigationState(tags) }, '');
        if (this.route.snapshot.queryParamMap.has('tags')) {
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { tags: null },
                queryParamsHandling: 'merge',
                replaceUrl: true,
                state: priceDiscoveryTagsNavigationState(tags)
            });
        }
    }

    // ── Cart Bottom Bar ──

    /**
     * Aggregates items from all sections into grouped cart sections based on supplier.
     */
    get cartSections(): any[] {
        const carts: any[] = [];
        let hasHpCartFromSearch = false;
        let hasDmartCartFromSearch = false;
        let hasUdaanCartFromSearch = false;
        let hasDeliveritCartFromSearch = false;

        this.supplierSections.forEach(section => {
            const cartItems = section.items.filter(item => item.selectedQuantity && item.selectedQuantity > 0);
            if (cartItems.length > 0) {
                if (section.id === 'local') {
                    // Group by supplierName
                    const localGroups = new Map<string, any[]>();
                    cartItems.forEach(item => {
                        const sName = item.supplierName || 'LOCAL SUPPLIERS';
                        if (!localGroups.has(sName)) localGroups.set(sName, []);
                        localGroups.get(sName)!.push(item);
                    });

                    localGroups.forEach((items, sName) => {
                        const totalValue = items.reduce((sum, item) => sum + (item.rate * (item.selectedQuantity || 0)), 0);
                        carts.push({
                            section: section,
                            platform: 'local',
                            supplierName: sName,
                            totalValue: totalValue,
                            totalCount: items.length,
                            items: items
                        });
                    });
                } else if (section.id === 'hyperpure') {
                    // For Hyperpure, use child component's cart count
                    const uniqueItems = this.uniqueCartLines(cartItems);
                    const hpCartCount = this.hyperpureSupplier?.apiCartCount || 0;
                    const totalValue = uniqueItems.reduce((sum, item) => sum + (this.getCartDisplayRate(item) * (item.selectedQuantity || 0)), 0);
                    carts.push({
                        section: section,
                        platform: section.id,
                        supplierName: section.name,
                        totalValue: totalValue,
                        totalCount: hpCartCount > 0 ? hpCartCount : uniqueItems.length,
                        items: uniqueItems
                    });
                    hasHpCartFromSearch = true;
                } else if (section.id === 'dmart') {
                    // For DMart, use child component's cart count
                    const dmartCartCount = this.dmartSupplier?.apiCartCount || 0;
                    const totalValue = cartItems.reduce((sum, item) => sum + (item.rate * (item.selectedQuantity || 0)), 0);
                    carts.push({
                        section: section,
                        platform: section.id,
                        supplierName: section.name,
                        totalValue: totalValue,
                        totalCount: dmartCartCount > 0 ? dmartCartCount : cartItems.length,
                        items: cartItems
                    });
                    hasDmartCartFromSearch = true;
                } else if (section.id === 'deliverit') {
                    // For DeliverIt, use child component's cart count
                    const deliveritCartCount = this.deliveritSupplier?.apiCartCount || 0;
                    const totalValue = cartItems.reduce((sum, item) => sum + (item.rate * (item.selectedQuantity || 0)), 0);
                    carts.push({
                        section: section,
                        platform: section.id,
                        supplierName: section.name,
                        totalValue: totalValue,
                        totalCount: deliveritCartCount > 0 ? deliveritCartCount : cartItems.length,
                        items: cartItems
                    });
                    hasDeliveritCartFromSearch = true;
                } else if (section.id === 'udaan') {
                    // For Udaan, use child component's cart count
                    const udaanCartCount = this.udaanSupplier?.apiCartCount || 0;
                    const totalValue = cartItems.reduce((sum, item) => sum + (item.rate * (item.selectedQuantity || 0)), 0);
                    carts.push({
                        section: section,
                        platform: section.id,
                        supplierName: section.name,
                        totalValue: totalValue,
                        totalCount: udaanCartCount > 0 ? udaanCartCount : cartItems.length,
                        items: cartItems
                    });
                    hasUdaanCartFromSearch = true;
                } else if (section.id === 'swiggy') {
                    // Swiggy uses the live cart below. Search quantities must not create a second card.
                } else {
                    const totalValue = cartItems.reduce((sum, item) => sum + (item.rate * (item.selectedQuantity || 0)), 0);
                    carts.push({
                        section: section,
                        platform: section.id,
                        supplierName: section.name,
                        totalValue: totalValue,
                        totalCount: cartItems.length,
                        items: cartItems
                    });
                }
            }
        });

        // If HP API cart has items but no search-result items matched, still show the HP cart
        const hpApiCartCount = this.hyperpureSupplier?.apiCartCount || 0;
        const hpApiCartItems = this.hyperpureSupplier?.apiCartItems || [];
        if (!hasHpCartFromSearch && hpApiCartCount > 0) {
            const hpSection = this.supplierSections.find(s => s.id === 'hyperpure');
            const totalValue = hpApiCartItems.reduce((sum, item) => sum + (this.getCartDisplayRate(item) * (item.selectedQuantity || 0)), 0);
            carts.unshift({
                section: hpSection || { id: 'hyperpure', name: 'Hyperpure', items: [] },
                platform: 'hyperpure',
                supplierName: 'Hyperpure',
                totalValue: totalValue,
                totalCount: hpApiCartCount,
                items: hpApiCartItems
            });
        } else if (hasHpCartFromSearch && hpApiCartItems.length > 0) {
            const hpCart = carts.find(c => c.platform === 'hyperpure');
            if (hpCart) {
                const existingKeys = new Set<string>();
                hpCart.items.forEach((item: SupplierProduct) => this.cartIdentityKeys(item).forEach(key => existingKeys.add(key)));
                const missingApiItems = hpApiCartItems.filter(ai => !this.cartIdentityKeys(ai).some(key => existingKeys.has(key)));
                if (missingApiItems.length > 0) {
                    hpCart.items = [...hpCart.items, ...missingApiItems];
                    hpCart.totalCount = hpApiCartCount;
                    hpCart.totalValue = hpCart.items.reduce((sum: number, item: SupplierProduct) => sum + (this.getCartDisplayRate(item) * (item.selectedQuantity || 0)), 0);
                }
            }
        }

        // If DMart API cart has items but no search-result items matched, still show the DMart cart
        const dmartApiCartCount = this.dmartSupplier?.apiCartCount || 0;
        const dmartApiCartItems = this.dmartSupplier?.apiCartItems || [];
        const dmartIdMap = this.dmartSupplier?.idToCanonicalId || new Map();
        if (!hasDmartCartFromSearch && dmartApiCartCount > 0) {
            const dmartSection = this.supplierSections.find(s => s.id === 'dmart');
            const totalValue = dmartApiCartItems.reduce((sum, item) => sum + (item.rate * (item.selectedQuantity || 0)), 0);
            carts.push({
                section: dmartSection || { id: 'dmart', name: 'DMart', items: [] },
                platform: 'dmart',
                supplierName: 'DMart',
                totalValue: totalValue,
                totalCount: dmartApiCartCount,
                items: dmartApiCartItems
            });
        } else if (hasDmartCartFromSearch && dmartApiCartItems.length > 0) {
            const dmartCart = carts.find(c => c.platform === 'dmart');
            if (dmartCart) {
                const existingCanonicalIds = new Set(
                    dmartCart.items.map((i: SupplierProduct) =>
                        dmartIdMap.get(String(i.sku_id)) || String(i.sku_id)
                    )
                );
                const missingApiItems = dmartApiCartItems.filter(ai => {
                    const canonicalId = dmartIdMap.get(String(ai.sku_id)) || String(ai.sku_id);
                    return !existingCanonicalIds.has(canonicalId);
                });
                if (missingApiItems.length > 0) {
                    dmartCart.items = [...dmartCart.items, ...missingApiItems];
                    dmartCart.totalCount = dmartApiCartCount;
                    dmartCart.totalValue = dmartCart.items.reduce((sum: number, item: SupplierProduct) => sum + (item.rate * (item.selectedQuantity || 0)), 0);
                }
            }
        }

        // If Udaan API cart has items but no search-result items matched, still show the Udaan cart
        const udaanApiCartCount = this.udaanSupplier?.apiCartCount || 0;
        const udaanApiCartItems = this.udaanSupplier?.apiCartItems || [];
        if (!hasUdaanCartFromSearch && udaanApiCartCount > 0) {
            const udaanSection = this.supplierSections.find(s => s.id === 'udaan');
            const totalValue = udaanApiCartItems.reduce((sum, item) => sum + (item.rate * (item.selectedQuantity || 0)), 0);
            carts.push({
                section: udaanSection || { id: 'udaan', name: 'Udaan', items: [] },
                platform: 'udaan',
                supplierName: 'Udaan',
                totalValue: totalValue,
                totalCount: udaanApiCartCount,
                items: udaanApiCartItems
            });
        } else if (hasUdaanCartFromSearch && udaanApiCartItems.length > 0) {
            const udaanCart = carts.find(c => c.platform === 'udaan');
            if (udaanCart) {
                const existingSkuIds = new Set(udaanCart.items.map((i: SupplierProduct) => String(i.sku_id)));
                const missingApiItems = udaanApiCartItems.filter(ai => !existingSkuIds.has(String(ai.sku_id)));
                if (missingApiItems.length > 0) {
                    udaanCart.items = [...udaanCart.items, ...missingApiItems];
                    udaanCart.totalCount = udaanApiCartCount;
                    udaanCart.totalValue = udaanCart.items.reduce((sum: number, item: SupplierProduct) => sum + (item.rate * (item.selectedQuantity || 0)), 0);
                }
            }
        }

        // If DeliverIt API cart has items but no search-result items matched, still show the DeliverIt cart
        const deliveritApiCartCount = this.deliveritSupplier?.apiCartCount || 0;
        const deliveritApiCartItems = this.deliveritSupplier?.apiCartItems || [];
        if (!hasDeliveritCartFromSearch && deliveritApiCartCount > 0) {
            const deliveritSection = this.supplierSections.find(s => s.id === 'deliverit');
            const totalValue = deliveritApiCartItems.reduce((sum, item) => sum + (item.rate * (item.selectedQuantity || 0)), 0);
            carts.push({
                section: deliveritSection || { id: 'deliverit', name: 'DeliverIt', items: [] },
                platform: 'deliverit',
                supplierName: 'DeliverIt',
                totalValue: totalValue,
                totalCount: deliveritApiCartCount,
                items: deliveritApiCartItems
            });
        } else if (hasDeliveritCartFromSearch && deliveritApiCartItems.length > 0) {
            const deliveritCart = carts.find(c => c.platform === 'deliverit');
            if (deliveritCart) {
                const existingSkuIds = new Set(deliveritCart.items.map((i: SupplierProduct) => String(i.sku_id)));
                const missingApiItems = deliveritApiCartItems.filter(ai => !existingSkuIds.has(String(ai.sku_id)));
                if (missingApiItems.length > 0) {
                    deliveritCart.items = [...deliveritCart.items, ...missingApiItems];
                    deliveritCart.totalCount = deliveritApiCartCount;
                    deliveritCart.totalValue = deliveritCart.items.reduce((sum: number, item: SupplierProduct) => sum + (item.rate * (item.selectedQuantity || 0)), 0);
                }
            }
        }

        const swiggyApiCartCount = this.swiggyAssureSupplier?.apiCartCount || 0;
        const swiggyApiCartItems = this.swiggyAssureSupplier?.apiCartItems || [];
        if (swiggyApiCartCount > 0) {
            const swiggySection = this.supplierSections.find(s => s.id === 'swiggy');
            const totalValue = swiggyApiCartItems.reduce((sum, item) => sum + ((item.rate || 0) * (item.selectedQuantity || 0)), 0);
            carts.push({
                section: swiggySection || { id: 'swiggy', name: 'Assure', items: [] },
                platform: 'swiggy',
                supplierName: 'Assure',
                totalValue: totalValue,
                totalCount: swiggyApiCartCount,
                items: swiggyApiCartItems
            });
        }

        if (this.pmDemoMode.isActive && this.demoFallbackCarts.length) {
            const platforms = new Set(carts.map(c => c.platform));
            this.demoFallbackCarts.forEach(demoCart => {
                if (!platforms.has(demoCart.platform)) {
                    carts.push({
                        ...demoCart,
                        items: demoCart.items || []
                    });
                }
            });
        }

        return carts;
    }

    /**
     * Calculates the total monetary value of all items in all carts.
     */
    get totalCartValue(): number {
        return this.cartSections.reduce((sum, cart) => sum + cart.totalValue, 0);
    }

    /**
     * Cart item count for the public-demo mobile FAB.
     */
    get demoMobileCartCount(): number {
        if (this.demoLocalCarts.length) {
            return this.demoLocalCarts.reduce((sum, cart) => sum + this.demoCartQuantityCount(cart), 0);
        }
        const live = this.cartSections.reduce((sum, cart) => sum + (Number(cart.totalCount) || 0), 0);
        const seeded = this.demoFallbackCarts.reduce((sum, cart) => sum + this.demoCartQuantityCount(cart), 0);
        return Math.max(live, seeded);
    }

    get demoMobileCartGrandTotal(): number {
        return this.demoVisibleCarts.reduce((sum, cart) => sum + this.demoCartValue(cart), 0);
    }

    get demoMobileCheckoutCount(): number {
        return this.demoVisibleCarts.length;
    }

    get demoVisibleCarts(): any[] {
        return this.demoLocalCarts.filter(cart => this.demoCartQuantityCount(cart) > 0);
    }

    scrollDemoTags(direction: 'left' | 'right'): void {
        this.cycleDemoSearchItem(direction);
    }

    cycleDemoSearchItem(direction: 'left' | 'right'): void {
        if (this.searchItems.length < 2) {
            return;
        }
        const index = Math.max(0, this.searchItems.findIndex(item => item.name === this.activeSearchItem));
        const delta = direction === 'left' ? -1 : 1;
        const next = (index + delta + this.searchItems.length) % this.searchItems.length;
        this.selectSearchItem(this.searchItems[next]);
        setTimeout(() => {
            const active = this.demoTagsRow?.nativeElement?.querySelector('.pd-m-chip.active') as HTMLElement | null;
            active?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
        }, 0);
    }

    get hasCartItems(): boolean {
        if (this.pmDemoMode.isActive) {
            return this.cartSections.length > 0;
        }
        if (this.isDemoSearchMode) {
            return this.cartSections.some(cart => cart.platform === 'local');
        }
        const hpCartCount = this.hyperpureSupplier?.apiCartCount || 0;
        const dmartCartCount = this.dmartSupplier?.apiCartCount || 0;
        const udaanCartCount = this.udaanSupplier?.apiCartCount || 0;
        const deliveritCartCount = this.deliveritSupplier?.apiCartCount || 0;
        const swiggyCartCount = this.swiggyAssureSupplier?.apiCartCount || 0;
        return this.cartSections.length > 0 || hpCartCount > 0 || dmartCartCount > 0 || udaanCartCount > 0 || deliveritCartCount > 0 || swiggyCartCount > 0;
    }

    /**
     * Scrolls the supplier carts container horizontally.
     * @param direction The scroll direction.
     */
    scrollCarts(direction: 'left' | 'right'): void {
        const container = document.querySelector('.supplier-carts-container') as HTMLElement;
        if (container) {
            const scrollAmount = 300;
            if (direction === 'left') {
                container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
            } else {
                container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
            }
        }
    }

    /**
     * Opens the checkout drawer for a specific supplier's cart.
     * @param cart The cart object to checkout.
     */
    openCheckoutDrawer(cart: any): void {
        this.selectedCheckoutCart = cart;
        this.isCheckoutDrawerOpen = true;
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';

        // Refresh Udaan cart data when opening checkout drawer to ensure latest data
        if (cart.platform === 'udaan' && this.udaanSupplier) {
            this.udaanSupplier.fetchCart();
        }

        // Refresh DeliverIt cart data when opening checkout drawer to ensure latest data
        if (cart.platform === 'deliverit' && this.deliveritSupplier) {
            this.deliveritSupplier.fetchCart();
        }
        if (cart.platform === 'swiggy' && this.swiggyAssureSupplier) {
            this.swiggyAssureSupplier.fetchCart();
        }

        // Track checkout drawer open
        const cartItems = cart.items || [];
        const cartTotalValue = cartItems.reduce((sum: number, i: any) => sum + ((i.rate || 0) * (i.selectedQuantity || 1)), 0);
        this.pmAnalytics.trackCartCheckoutClick(cart.platform || 'unknown', {
            totalCartValue: cartTotalValue,
            totalItems: cartItems.length,
            uniqueItemCount: cartItems.length,
            supplierBreakdown: [{
                supplier: cart.supplierName || cart.platform || '',
                itemCount: cartItems.length,
                cartValue: cartTotalValue,
            }],
        });
    }

    /**
     * Closes the checkout drawer.
     */
    closeCheckoutDrawer(): void {
        this.isCheckoutDrawerOpen = false;
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        setTimeout(() => {
            this.selectedCheckoutCart = null;
        }, 300);
    }

    openSwiggyCheckout(): void {
        if (this.swiggyCheckoutLoading) {
            return;
        }
        this.swiggyAssureSupplier?.flushPendingQuantitySyncs();
        this.swiggyCheckoutLoading = true;
        this.priceDiscoveryService.refreshSwiggyAssureCart(this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.priceDiscoveryService.getSwiggyAssureCartSession(this.selectedEntityId)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe((session: any) => {
                        this.swiggyFrameLoadPayload = {
                            Topic: 'swiggy_frame_load_cart',
                            Data: {
                                tid: session?.tid || '',
                                rid: session?.rid || '',
                                storeId: session?.storeId || '',
                                email: session?.email || '',
                                phone: session?.phone || '',
                                sid: session?.sid || '',
                                token: session?.token || '',
                                source: 'petpooja-purchase'
                            }
                        };
                        this.closeCheckoutDrawer();
                        this.showSwiggyCheckoutFrame();
                        this.swiggyCheckoutLoading = false;
                    }, () => {
                        this.swiggyCheckoutLoading = false;
                        this.sharedService.errorAlert('Failed to open Swiggy Assure checkout');
                    });
            }, (error) => {
                this.swiggyCheckoutLoading = false;
                this.sharedService.errorAlert(error?.error?.error || 'Failed to refresh the Swiggy Assure cart');
            });
    }

    private showSwiggyCheckoutFrame(): void {
        this.swiggyIframeLoadCount = 0;
        this.swiggyInboundCount = 0;
        this.swiggyOriginInboundCount = 0;
        this.swiggyIframeUrlSafe = null;
        this.showSwiggyIframe = true;
        document.body.style.overflow = 'hidden';
        this.startSwiggyIframeLogging();
        console.log('[SWIGGY ASSURE IFRAME] Open', {
            parentOrigin: window.location.origin,
            iframeUrl: this.swiggyCartUrl,
            ...this.swiggyHandshakeSummary()
        });
        console.log('[SWIGGY ASSURE IFRAME] Handshake payload', this.swiggyFrameLoadPayload);
        setTimeout(() => {
            this.swiggyIframeUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(this.swiggyCartUrl);
            console.log('[SWIGGY ASSURE IFRAME] Iframe src assigned', this.swiggyCartUrl);
        }, 10);
    }

    closeSwiggyIframe(): void {
        console.log('[SWIGGY ASSURE IFRAME] Close', {
            loads: this.swiggyIframeLoadCount,
            inboundMessages: this.swiggyInboundCount,
            swiggyReplies: this.swiggyOriginInboundCount
        });
        this.showSwiggyIframe = false;
        this.swiggyIframeUrlSafe = null;
        this.swiggyFrameLoadPayload = null;
        document.body.style.overflow = '';
        this.stopSwiggyIframeLogging();
        if (this.swiggyAssureSupplier?.entityId) {
            this.swiggyAssureSupplier.fetchCart();
        }
    }

    reloadSwiggyIframe(): void {
        if (!this.swiggyFrameLoadPayload) {
            return;
        }
        console.log('[SWIGGY ASSURE IFRAME] Reload requested', this.swiggyCartUrl);
        this.swiggyIframeLoadCount = 0;
        this.swiggyInboundCount = 0;
        this.swiggyOriginInboundCount = 0;
        this.swiggyIframeUrlSafe = null;
        setTimeout(() => {
            this.swiggyIframeUrlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(this.swiggyCartUrl);
            console.log('[SWIGGY ASSURE IFRAME] Iframe src assigned', this.swiggyCartUrl);
        }, 10);
    }

    onSwiggyIframeLoaded(event: Event): void {
        if (!this.showSwiggyIframe) {
            console.log('[SWIGGY ASSURE IFRAME] Load ignored because overlay is closed');
            return;
        }
        this.swiggyIframeLoadCount += 1;
        const iframe = event.target as HTMLIFrameElement;
        console.log('[SWIGGY ASSURE IFRAME] Load', {
            loadNumber: this.swiggyIframeLoadCount,
            iframeSrc: iframe?.src || '',
            expectedUrl: this.swiggyCartUrl,
            hasContentWindow: Boolean(iframe?.contentWindow)
        });
        this.postSwiggyHandshake(iframe);
        if (this.swiggyHandshakeTimer) {
            clearTimeout(this.swiggyHandshakeTimer);
        }
        this.swiggyHandshakeTimer = setTimeout(() => this.postSwiggyHandshake(iframe), 1500);
    }

    private swiggyHandshakeSummary(): { topic: string; rid: string; storeId: string; hasTid: boolean; tidLength: number; hasSid: boolean; hasToken: boolean; hasEmail: boolean; hasPhone: boolean } {
        const data = this.swiggyFrameLoadPayload?.Data;
        return {
            topic: this.swiggyFrameLoadPayload?.Topic || '',
            rid: data?.rid || '',
            storeId: data?.storeId || '',
            hasTid: Boolean(data?.tid),
            tidLength: String(data?.tid || '').length,
            hasSid: Boolean(data?.sid),
            hasToken: Boolean(data?.token),
            hasEmail: Boolean(data?.email),
            hasPhone: Boolean(data?.phone)
        };
    }

    private postSwiggyHandshake(iframe?: HTMLIFrameElement): void {
        const payload = this.swiggyFrameLoadPayload;
        const win = iframe?.contentWindow || this.swiggyCheckoutFrame?.nativeElement?.contentWindow;
        if (!this.showSwiggyIframe || !win || !payload) {
            return;
        }
        try {
            win.postMessage(JSON.stringify(payload), this.swiggyCartUrl);
            console.log('[SWIGGY ASSURE IFRAME] Posted swiggy_frame_load_cart', {
                target: this.swiggyCartUrl,
                ...this.swiggyHandshakeSummary()
            });
            console.log('[SWIGGY ASSURE IFRAME] Handshake payload', payload);
            this.armSwiggyReplyWatch();
        } catch (error) {
            console.error('[SWIGGY ASSURE IFRAME] postMessage failed', error);
        }
    }

    private startSwiggyIframeLogging(): void {
        window.addEventListener('message', this.onSwiggyWindowMessage);
        document.addEventListener('securitypolicyviolation', this.onSwiggySecurityViolation);
    }

    private stopSwiggyIframeLogging(): void {
        window.removeEventListener('message', this.onSwiggyWindowMessage);
        document.removeEventListener('securitypolicyviolation', this.onSwiggySecurityViolation);
        if (this.swiggyReplyTimer) {
            clearTimeout(this.swiggyReplyTimer);
            this.swiggyReplyTimer = null;
        }
        if (this.swiggyHandshakeTimer) {
            clearTimeout(this.swiggyHandshakeTimer);
            this.swiggyHandshakeTimer = null;
        }
    }

    private armSwiggyReplyWatch(): void {
        if (this.swiggyReplyTimer) {
            clearTimeout(this.swiggyReplyTimer);
        }
        const loadNumber = this.swiggyIframeLoadCount;
        this.swiggyReplyTimer = setTimeout(() => {
            if (!this.showSwiggyIframe || this.swiggyOriginInboundCount > 0) {
                return;
            }
            console.warn('[SWIGGY ASSURE IFRAME] No message received from the iframe 8s after load', {
                loadNumber,
                iframeUrl: this.swiggyCartUrl,
                parentOrigin: window.location.origin
            });
        }, 8000);
    }

    private logSwiggyInboundMessage(event: MessageEvent): void {
        console.log(event, 'parent-event');
        if (event.origin !== this.swiggyAssureOrigin) {
            return;
        }
        this.swiggyInboundCount += 1;
        this.swiggyOriginInboundCount += 1;
        let data: any = event.data;
        if (typeof data === 'string') {
            try {
                data = JSON.parse(data);
            } catch {
                return;
            }
        }
        console.log('[SWIGGY ASSURE IFRAME] Inbound message', {
            origin: event.origin,
            data
        });
        if (data?.Topic === this.swiggyChildCloseTopic) {
            this.closeSwiggyIframe();
        }
    }

    private logSwiggySecurityViolation(event: SecurityPolicyViolationEvent): void {
        const blocked = `${event.blockedURI || ''} ${event.violatedDirective || ''}`;
        if (!/swiggy|frame-ancestors|frame-src/i.test(blocked) && event.violatedDirective !== 'frame-src') {
            return;
        }
        console.error('[SWIGGY ASSURE IFRAME] Security policy blocked the frame', {
            violatedDirective: event.violatedDirective,
            effectiveDirective: event.effectiveDirective,
            blockedURI: event.blockedURI,
            disposition: event.disposition
        });
    }

    /**
     * Initiates checkout for a specific supplier.
     * @param cart The supplier's cart.
     */
    checkoutSupplier(cart: any): void {
        if (this.pmDemoMode.isActive) {
            this.openConnectLeadPopup();
            return;
        }
        this.openCheckoutDrawer(cart);
    }

    /**
     * Opens checkout drawer for items of a specific platform.
     * @param platform The platform/supplier type (hyperpure, udaan, dmart, local).
     */
    openCheckoutDrawerForPlatform(platform: string): void {
        if (this.pmDemoMode.isActive) {
            this.openConnectLeadPopup();
            return;
        }
        const cart = this.cartSections.find(c => c.platform === platform);
        if (cart) {
            this.openCheckoutDrawer(cart);
        }
    }

    /**
     * Initiates the Purchase Order creation flow for a supplier's cart.
     * @param cart The cart to create a PO for.
     */
    createPO(cart: any): void {
        if (this.pmDemoMode.isActive) {
            this.openConnectLeadPopup();
            return;
        }
        console.log('Creating PO for supplier:', cart.supplierName);
        const cartItem = cart.items[0];
        const supplierId = cart.section?.items[0]?.supplierId || cartItem?.supplierId;

        // Enrich supplier with full details from allSuppliers
        // Try multiple ways to match the supplier
        let fullSupplier = this.allSuppliers.find(s => (s.id || s._id) === supplierId);
        if (!fullSupplier && supplierId) {
            // Try matching by name as fallback
            const resolvedName = this.resolveSupplierName(cart.supplierName);
            fullSupplier = this.allSuppliers.find(s =>
                s.name === resolvedName ||
                s.alias === resolvedName ||
                s.supplierAlias === resolvedName
            );
        }

        // Build enriched supplier - also check cart items for details as fallback
        const enrichedSupplier = {
            id: supplierId,
            name: this.resolveSupplierName(cart.supplierName),
            alias: fullSupplier?.alias || fullSupplier?.supplier_alias || cartItem?.supplierAlias || '',
            supplierAlias: fullSupplier?.supplierAlias || fullSupplier?.supplier_alias || cartItem?.supplierAlias || '',
            gstNo: fullSupplier?.gstNo || fullSupplier?.gst_no || fullSupplier?.gstNumber || cartItem?.gstNo || cartItem?.gst_no || '',
            mobile: fullSupplier?.mobile || fullSupplier?.mobile_number || fullSupplier?.mobileNumber || cartItem?.mobile || cartItem?.mobileNumber || '',
            email: fullSupplier?.email || cartItem?.email || ''
        };

        this.localSupplierPOModal.open(enrichedSupplier, cart.items);
    }

    /**
     * Handles the event when a PO is successfully generated.
     * @param poData The generated PO data.
     */
    onPOGenerated(poData: any): void {
        this.sharedService.successAlert(`Purchase Order ${poData.poNumber} created successfully!`);

        // Clear only the items for this local supplier from their section
        const supplierId = poData.supplierId;
        this.supplierSections.forEach(section => {
            if (section.id === 'local') {
                section.items.forEach(item => {
                    if (item.platform === 'local' && (this.suppliersMap.get(item.supplierName || '') === poData.supplierName || item.supplierName === poData.supplierName || item.supplierId === supplierId)) {
                        item.selectedQuantity = 0;
                    }
                });
            }
        });
    }

    /**
     * Finalizes the purchase by syncing with external supplier carts or creating a local PO.
     */
    proceedToBuy(): void {
        if (this.pmDemoMode.isActive) {
            this.closeCheckoutDrawer();
            this.openConnectLeadPopup();
            return;
        }

        if (!this.canCheckout) {
            return;
        }

        if (!this.selectedCheckoutCart || this.swiggyCheckoutLoading) return;

        const platform = this.selectedCheckoutCart.platform || 'unknown';
        const items = this.selectedCheckoutCart.items || [];
        const totalAmount = items.reduce((sum: number, i: any) => sum + ((i.rate || 0) * (i.selectedQuantity || 1)), 0);

        // Determine checkout method based on platform
        let checkoutMethod: string;
        switch (platform) {
            case 'hyperpure': checkoutMethod = 'iframe'; break;
            case 'dmart': case 'udaan': checkoutMethod = 'redirect'; break;
            case 'local': checkoutMethod = 'local_po'; break;
            default: checkoutMethod = 'other'; break;
        }

        // Track proceed-to-buy
        this.pmAnalytics.trackProceedToBuy(platform, {
            finalItems: items.map((i: any) => ({
                itemId: i.id || i._id || i.sku_id || '',
                itemName: i.item_name || i.itemName || '',
                quantity: i.selectedQuantity || 1,
                unitPrice: i.rate || 0,
                lineTotal: (i.rate || 0) * (i.selectedQuantity || 1),
            })),
            finalTotalValue: totalAmount,
            finalItemCount: items.length,
            checkoutMethod: checkoutMethod as any,
        });

        // const platform = this.selectedCheckoutCart.platform;

        // Clean up market best items for this platform after successful checkout
        this.cleanupMarketBestAfterCheckout(platform);

        if (platform === 'local') {
            this.sharedService.successAlert(`Proceeding to create PO for ${this.selectedCheckoutCart.supplierName}`);
            this.closeCheckoutDrawer();
        } else if (platform === 'hyperpure') {
            this.closeCheckoutDrawer();
            if (this.hyperpureSupplier) {
                this.hyperpureSupplier.proceedToBuy();
            }
        } else if (platform === 'deliverit') {
            this.closeCheckoutDrawer();
            if (this.deliveritSupplier) {
                this.deliveritSupplier.proceedToBuy();
            }
        } else if (platform === 'dmart') {
            this.closeCheckoutDrawer();
            if (this.dmartSupplier) {
                this.dmartSupplier.proceedToBuy(this.selectedCheckoutCart.items);
            }
        } else if (platform === 'swiggy') {
            this.openSwiggyCheckout();
        } else if (platform === 'udaan') {
            const items = this.selectedCheckoutCart.items;
            if (items.length === 0) return;
            this.closeCheckoutDrawer();
            if (this.udaanSupplier) {
                const udaanSection = this.supplierSections.find(s => s.id === 'udaan');
                this.udaanSupplier.entityId = this.selectedEntityId;
                this.udaanSupplier.storeUuid = this.udaanStoreUuid;
                this.udaanSupplier.apiStoreId = this.udaanApiStoreId; // For get cart API only
                this.udaanSupplier.supplierSection = udaanSection || null;
                this.udaanSupplier.proceedToBuy(items);
            }
        } else {
            this.sharedService.successAlert(`Proceeding to checkout for ${this.selectedCheckoutCart.supplierName}`);
            this.closeCheckoutDrawer();
        }
    }

    /**
     * Removes market best tracking for items belonging to a specific platform after checkout.
     */
    private cleanupMarketBestAfterCheckout(platform: string): void {
        const snapshotsForPlatform = this.marketBestItemsSnapshot.filter(s => s.platform === platform);
        if (snapshotsForPlatform.length === 0) return;

        snapshotsForPlatform.forEach(snapshot => {
            this.marketBestItemSkus.delete(snapshot.skuId);
        });
        this.marketBestItemsSnapshot = this.marketBestItemsSnapshot.filter(s => s.platform !== platform);
        this.hasMarketBestItems = this.marketBestItemSkus.size > 0;
        this.saveMarketBestItems();
    }

    viewFullCart(): void {
        if (this.isDemoMobileView) {
            this.isDemoMobileCartOpen = true;
            return;
        }
        this.sharedService.successAlert('Opening full cart view');
    }

    closeDemoMobileCart(): void {
        this.isDemoMobileCartOpen = false;
    }

    openDemoMobileViewAll(section: SupplierSection): void {
        this.demoViewAllSection = section;
        this.isDemoMobileViewAllOpen = true;
    }

    closeDemoMobileViewAll(): void {
        this.isDemoMobileViewAllOpen = false;
        this.demoViewAllSection = null;
    }

    demoProductName(item: any): string {
        return String(item?.item_name || item?.itemName || item?.name || '').trim();
    }

    demoItemPackLabel(item: any): string {
        const unit = String(item?.unit || item?.uom || item?.pack || item?.packSize || item?.pack_size || '').trim();
        if (!unit) {
            return '1 Pack';
        }
        if (/pack/i.test(unit)) {
            return unit.toLowerCase().startsWith('1 ') ? unit : `1 Pack of ${unit}`;
        }
        return `1 Pack of ${unit}`;
    }

    private demoItemSku(item: any): string {
        return String(item?.sku_id || item?.skuId || item?.id || this.demoProductName(item) || '');
    }

    trackBySearchProduct(_index: number, item: any): string {
        return this.demoItemSku(item);
    }

    onProductImageError(event: Event): void {
        const img = event.target as HTMLImageElement;
        if (img) {
            img.src = 'assets/images/placeholder-product.svg';
        }
    }

    demoCartLineCount(cart: any): number {
        return Array.isArray(cart?.items) ? cart.items.length : (Number(cart?.totalCount) || 0);
    }

    demoCartQuantityCount(cart: any): number {
        if (!Array.isArray(cart?.items) || cart.items.length === 0) {
            return Number(cart?.totalCount) || 0;
        }
        return cart.items.reduce(
            (sum: number, row: any) => sum + (Number(row.selectedQuantity) || 0),
            0
        );
    }

    demoCartValue(cart: any): number {
        if (!Array.isArray(cart?.items) || cart.items.length === 0) {
            return Number(cart?.totalValue) || 0;
        }
        return cart.items.reduce(
            (sum: number, row: any) => sum + (Number(row.rate) || 0) * (Number(row.selectedQuantity) || 0),
            0
        );
    }

    formatDemoCartAmount(value: number): string {
        const rounded = Math.round((Number(value) || 0) * 100) / 100;
        return rounded.toLocaleString('en-IN', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 0
        });
    }

    getDemoCartLogo(platform: string): string {
        switch ((platform || '').toLowerCase()) {
            case 'hyperpure':
                return 'assets/images/hyperpure_cart.png';
            case 'udaan':
                return 'assets/images/udaan_cart.png';
            case 'dmart':
                return 'assets/images/dmart_cart.png';
            case 'deliverit':
                return 'assets/images/deliverIt.png';
            case 'swiggy':
                return 'assets/images/swiggy-assure.png';
            default:
                return 'assets/images/placeholder-product.svg';
        }
    }

    updateDemoCartQuantity(cart: any, item: any, change: number): void {
        if (!cart || !item) {
            return;
        }
        const next = Math.max(0, (Number(item.selectedQuantity) || 0) + change);
        item.selectedQuantity = next;
        if (next === 0) {
            cart.items = (cart.items || []).filter((row: any) => row !== item);
        }
        this.recalculateDemoLocalCart(cart);
        if (this.demoCartQuantityCount(cart) === 0) {
            this.demoLocalCarts = this.demoLocalCarts.filter(row => row !== cart);
        } else {
            this.touchDemoLocalCarts();
        }
        this.syncSearchItemQuantity(item);
    }

    proceedDemoCheckout(_cart?: any): void {
        this.openConnectLeadPopup();
    }

    onConnectLeadSubmitted(): void {
        this.isDemoMobileCartOpen = false;
    }

    private cloneDemoCarts(carts: any[]): any[] {
        return (carts || []).map(cart => ({
            ...cart,
            items: (cart.items || []).map((item: any) => {
                const name = this.demoProductName(item);
                const sku = this.demoItemSku(item);
                return {
                    ...item,
                    sku_id: sku,
                    skuId: sku,
                    item_name: name,
                    itemName: name
                };
            })
        }));
    }

    private upsertDemoLocalCartItem(item: any, section?: any): void {
        const platform = (section?.id || item?.platform || 'local').toString().toLowerCase();
        const sku = this.demoItemSku(item);
        const qty = Number(item?.selectedQuantity) || 0;
        if (!sku) {
            return;
        }

        let cart = this.demoLocalCarts.find(row => row.platform === platform);
        if (!cart) {
            cart = {
                platform,
                supplierName: section?.name || item?.supplier || platform,
                totalValue: 0,
                totalCount: 0,
                items: []
            };
            this.demoLocalCarts = [...this.demoLocalCarts, cart];
        }

        if (qty <= 0) {
            cart.items = (cart.items || []).filter((row: any) => this.demoItemSku(row) !== sku);
            if (this.demoCartQuantityCount(cart) === 0) {
                this.demoLocalCarts = this.demoLocalCarts.filter(row => row !== cart);
            } else {
                this.recalculateDemoLocalCart(cart);
                this.touchDemoLocalCarts();
            }
            this.syncSearchItemQuantity({
                sku_id: sku,
                skuId: sku,
                selectedQuantity: 0
            });
            return;
        }

        const existing = (cart.items || []).find((row: any) => this.demoItemSku(row) === sku);
        if (existing) {
            existing.selectedQuantity = qty;
            existing.rate = Number(item.rate) || existing.rate;
            const name = this.demoProductName(item) || this.demoProductName(existing);
            existing.item_name = name;
            existing.itemName = name;
        } else {
            const name = this.demoProductName(item) || 'Item';
            cart.items = [
                ...(cart.items || []),
                {
                    sku_id: sku,
                    skuId: sku,
                    item_name: name,
                    itemName: name,
                    rate: Number(item.rate) || 0,
                    selectedQuantity: qty,
                    platform
                }
            ];
        }
        this.recalculateDemoLocalCart(cart);
        this.touchDemoLocalCarts();
        this.syncSearchItemQuantity({
            sku_id: sku,
            skuId: sku,
            item_name: this.demoProductName(item),
            selectedQuantity: qty
        });
    }

    private recalculateDemoLocalCart(cart: any): void {
        cart.totalCount = this.demoCartQuantityCount(cart);
        cart.totalValue = this.demoCartValue(cart);
    }

    private touchDemoLocalCarts(): void {
        this.demoLocalCarts = [...this.demoLocalCarts];
    }

    private syncSearchItemQuantity(cartItem: any): void {
        const sku = this.demoItemSku(cartItem);
        if (!sku) {
            return;
        }
        this.supplierSections.forEach(section => {
            (section.items || []).forEach(product => {
                if (this.demoItemSku(product) === sku) {
                    product.selectedQuantity = cartItem.selectedQuantity || 0;
                }
            });
        });
        const best = this.marketBestPrice?.originalItem;
        if (best && this.demoItemSku(best) === sku) {
            best.selectedQuantity = cartItem.selectedQuantity || 0;
        }
    }

    /**
     * Updates the quantity of an item specifically within the checkout drawer.
     * @param item The product item.
     * @param delta Change amount.
     */
    updateCheckoutQty(item: SupplierProduct, delta: number): void {
        const previousQty = item.selectedQuantity || 0;
        const minQty = this.resolveMinimumOrderQty(item);

        // Enforce maximum order quantity
        if (delta > 0 && item.maximumOrderQty && (previousQty + delta) > item.maximumOrderQty) {
            this.sharedService.warningAlert(`Maximum order quantity is ${item.maximumOrderQty}`);
            return;
        }

        const stepped = previousQty + delta;
        if (delta < 0 && (stepped < 1 || (minQty > 1 && previousQty <= minQty))) {
            item.selectedQuantity = 0;
        } else {
            item.selectedQuantity = Math.max(0, stepped);
        }
        this.syncDuplicateCartQuantity(item);
        // Apply bulk pricing based on new quantity
        this.applyBulkRate(item);
        item.cartError = undefined; // Clear any previous errors

        // Track cart add in analytics DB (only for quantity increases)
        if (delta > 0) {
            this.pmAnalytics.trackCartAdd(item.platform || 'unknown', {
                itemId: String(item.sku_id || ''),
                itemName: item.item_name || '',
                quantity: item.selectedQuantity || 0,
                unitPrice: item.rate || 0,
                itemUnit: item.unit || '',
                cartValueAfter: 0,
                cartItemCountAfter: 0,
            });
        }

        // Delegate API calls to child components (debounced per line item)
        this.syncCheckoutQtyWithSupplier(item, delta, previousQty);
    }

    /**
     * Handles manual quantity change in the checkout drawer input field.
     * @param item The product item.
     * @param event The input change event.
     */
    onCheckoutQtyChange(item: SupplierProduct, event: any): void {
        const previousQty = item.selectedQuantity || 1;
        let val = parseInt(event.target.value, 10);

        // Enforce maximum order quantity
        if (item.maximumOrderQty && val > item.maximumOrderQty) {
            val = item.maximumOrderQty;
            event.target.value = val;
            this.sharedService.warningAlert(`Maximum order quantity is ${item.maximumOrderQty}`);
        }

        // Enforce minimum order quantity
        if (item.minimumOrderQty && val > 0 && val < item.minimumOrderQty) {
            val = item.minimumOrderQty;
            event.target.value = val;
            this.sharedService.warningAlert(`Minimum order quantity is ${item.minimumOrderQty}`);
        }

        if (val && val > 0) {
            item.selectedQuantity = val;
        } else {
            item.selectedQuantity = 1;
            event.target.value = 1;
        }

        // Apply bulk pricing based on new quantity
        this.applyBulkRate(item);

        this.syncCheckoutQtyWithSupplier(item, 0, previousQty);
    }

    /**
     * Syncs checkout qty changes through the supplier (debounced per line item).
     */
    get isCheckoutCartSyncing(): boolean {
        const platform = this.selectedCheckoutCart?.platform;
        if (platform === 'swiggy') return !!this.swiggyAssureSupplier?.isCartSyncing;
        if (platform === 'hyperpure') return !!this.hyperpureSupplier?.isCartSyncing;
        if (platform === 'dmart') return !!this.dmartSupplier?.isCartSyncing;
        if (platform === 'deliverit') return !!this.deliveritSupplier?.isCartSyncing;
        if (platform === 'udaan') return !!this.udaanSupplier?.isCartSyncing;
        return false;
    }

    removeCheckoutItem(item: SupplierProduct): void {
        const previousQty = item.selectedQuantity || 0;
        if (previousQty <= 0 || this.isCheckoutCartSyncing) {
            return;
        }
        item.selectedQuantity = 0;
        item.cartError = undefined;
        this.syncCheckoutQtyWithSupplier(item, -previousQty, previousQty);
    }

    private syncCheckoutQtyWithSupplier(item: SupplierProduct, change: number, previousQty: number): void {
        if (item.platform === 'udaan' && item.sku_id && this.udaanSupplier) {
            this.udaanSupplier.updateQuantity(item, change, previousQty);
        } else if (item.platform === 'deliverit' && item.sku_id && this.deliveritSupplier) {
            this.deliveritSupplier.updateQuantity(item, change, previousQty);
        } else if (item.platform === 'hyperpure' && item.sku_id && this.hyperpureSupplier) {
            this.hyperpureSupplier.updateQuantity(item, change, previousQty);
        } else if (item.platform === 'dmart' && item.sku_id && this.dmartSupplier) {
            this.dmartSupplier.updateQuantity(item, change, previousQty);
        } else if (item.platform === 'swiggy' && item.sku_id && this.swiggyAssureSupplier) {
            this.swiggyAssureSupplier.updateQuantity(item, change, previousQty);
        }
        this.refreshCheckoutCart();
    }

    /**
     * Waits until quantity clicks pause, then runs one cart write with the final quantity.
     * A burst from 6 to 2 stays on screen immediately and sends 2 once.
     */
    private scheduleCartWrite(item: SupplierProduct, previousQuantity: number, write: (baseline: number) => void): void {
        const key = `${item.platform || ''}:${item.sku_id}`;
        if (!this.cartQtyBaseline.has(key)) {
            this.cartQtyBaseline.set(key, previousQuantity);
        }
        const pending = this.cartQtyTimers.get(key);
        if (pending) {
            clearTimeout(pending);
        }
        const timer = setTimeout(() => {
            this.cartQtyTimers.delete(key);
            const baseline = this.cartQtyBaseline.get(key) ?? previousQuantity;
            this.cartQtyBaseline.delete(key);
            write(baseline);
        }, this.cartQtyDebounceMs);
        this.cartQtyTimers.set(key, timer);
    }


    private readMinimumOrderQty(item: any): number | undefined {
        const raw = item?.minimumOrderQty ?? item?.minimum_order_qty ?? item?.minOrderQty ?? item?.min_order_qty ?? item?.minimumOrderQuantity ?? item?.minQty ?? item?.min_qty;
        const quantity = Number(raw);
        return Number.isFinite(quantity) && quantity > 1 ? quantity : undefined;
    }

    private resolveMinimumOrderQty(item: SupplierProduct): number {
        const own = this.readMinimumOrderQty(item) || 0;
        if (own > 1) {
            return own;
        }
        const keys = new Set(this.cartIdentityKeys(item));
        let found = 0;
        this.supplierSections.forEach(section => {
            if (item.platform && section.id !== item.platform) {
                return;
            }
            (section.items || []).forEach(candidate => {
                const min = this.readMinimumOrderQty(candidate) || 0;
                if (min > found && this.cartIdentityKeys(candidate).some(key => keys.has(key))) {
                    found = min;
                }
            });
        });
        return found > 1 ? found : 1;
    }

    private cartIdentityKeys(item: SupplierProduct): string[] {
        const keys: string[] = [];
        const sku = String(item?.sku_id || '').trim();
        const productNumber = String(item?.product_number || '').trim();
        const name = (item?.item_name || '').replace(/\s+/g, ' ').trim().toLowerCase();
        if (sku) {
            keys.push(`sku:${sku}`);
        }
        if (productNumber) {
            keys.push(`sku:${productNumber}`);
        }
        if (name) {
            keys.push(`name:${name}`);
        }
        return keys;
    }

    private uniqueCartLines(items: SupplierProduct[]): SupplierProduct[] {
        const seen = new Set<string>();
        const unique: SupplierProduct[] = [];
        (items || []).forEach(item => {
            const keys = this.cartIdentityKeys(item);
            if (keys.length === 0) {
                unique.push(item);
                return;
            }
            if (keys.some(key => seen.has(key))) {
                return;
            }
            keys.forEach(key => seen.add(key));
            unique.push(item);
        });
        return unique;
    }

    private syncDuplicateCartQuantity(item: SupplierProduct): void {
        const keys = new Set(this.cartIdentityKeys(item));
        if (keys.size === 0) {
            return;
        }
        const quantity = item.selectedQuantity || 0;
        const apply = (candidate: SupplierProduct) => {
            if (candidate !== item && this.cartIdentityKeys(candidate).some(key => keys.has(key))) {
                candidate.selectedQuantity = quantity;
            }
        };
        this.supplierSections.forEach(section => {
            if (item.platform && section.id !== item.platform) {
                return;
            }
            (section.items || []).forEach(apply);
        });
        (this.selectedCheckoutCart?.items || []).forEach(apply);
    }

    /**
     * Refreshes the selected checkout cart's totals and visibility.
     */
    private refreshCheckoutCart(): void {
        if (!this.selectedCheckoutCart) return;

        // Find removed items (qty = 0) and clean up market best tracking
        const allItems = this.selectedCheckoutCart.items || [];
        allItems.forEach((item: SupplierProduct) => {
            if ((!item.selectedQuantity || item.selectedQuantity <= 0) && this.marketBestItemSkus.has(String(item.sku_id))) {
                this.marketBestItemSkus.delete(String(item.sku_id));
                this.marketBestItemsSnapshot = this.marketBestItemsSnapshot.filter(
                    s => s.skuId !== String(item.sku_id)
                );
            }
        });

        // Recalculate totals. The same Hyperpure product can be returned by more than one search.
        const pricedItems = allItems.filter(
            (i: SupplierProduct) => i.selectedQuantity && i.selectedQuantity > 0
        );
        const items = this.selectedCheckoutCart.platform === 'hyperpure'
            ? this.uniqueCartLines(pricedItems)
            : pricedItems;
        this.selectedCheckoutCart.items = items;
        this.selectedCheckoutCart.totalCount = items.length;
        this.selectedCheckoutCart.totalValue = items.reduce(
            (sum: number, i: SupplierProduct) => sum + (this.getCartDisplayRate(i) * (i.selectedQuantity || 0)), 0
        );

        // Update market best state
        this.hasMarketBestItems = this.marketBestItemSkus.size > 0;
        this.saveMarketBestItems();

        // Close drawer if no items left
        if (items.length === 0 && !this.isCheckoutCartSyncing) {
            this.closeCheckoutDrawer();
        }
    }

    /**
     * Gets items for the active search item from a section.
     * @param section The supplier section.
     * 
     */
    getItemsForActiveSearch(section: SupplierSection): SupplierProduct[] {
        const active = this.searchItems.find(item => item.name === this.activeSearchItem);
        if (!active) {
            return this.activeSearchItem ? [] : section.items;
        }
        const keys = new Set(
            [active.name, active.sheetItemName]
                .filter((name): name is string => !!name && !!name.trim())
                .map(name => name.trim().toLowerCase())
        );
        return section.items.filter(item => keys.has((item.searchItemName || '').trim().toLowerCase()));
    }

    /**
     * Calculates max visible cards based on screen width.
     */
    calculateMaxVisibleCards(): void {
        const isMobile = window.innerWidth <= 768;
        const cardGap = isMobile ? 12 : 10;

        // Measure actual container width from the cards-row element (which has overflow: hidden)
        // to get the true available space for cards
        const cardsRow = document.querySelector('.cards-row') as HTMLElement;
        const pdMain = document.querySelector('.pd-main') as HTMLElement;
        let availableWidth: number;

        if (cardsRow) {
            availableWidth = cardsRow.clientWidth;
        } else if (pdMain) {
            const mainStyle = getComputedStyle(pdMain);
            const paddingLeft = parseFloat(mainStyle.paddingLeft) || 0;
            const paddingRight = parseFloat(mainStyle.paddingRight) || 0;
            // Subtract pd-main padding + supplier-section padding (16px left + 16px right)
            availableWidth = pdMain.clientWidth - paddingLeft - paddingRight - 32;
        } else {
            const sidebarWidth = isMobile ? 0 : 220;
            const containerPadding = isMobile ? 24 : 64;
            availableWidth = window.innerWidth - sidebarWidth - containerPadding - 32;
        }

        // Use base minimum card width for calculation (not rendered width, which may be
        // stretched by flex-grow and would cause a feedback loop on resize)
        const isTablet = window.innerWidth > 480 && window.innerWidth <= 768;
        const cardWidth = isMobile ? 160 : (isTablet ? 170 : 155);

        // Calculate total card slots that fully fit (no partial/clipped cards)
        // Subtract 1 to guarantee the last card is never clipped by overflow: hidden
        const totalSlots = Math.floor((availableWidth + cardGap) / (cardWidth + cardGap));
        this.maxVisibleCards = Math.max(1, totalSlots - 1);
    }

    /**
     * Check if the current view is mobile (screen width <= 768px)
     */
    checkMobileView(): void {
        this.isMobileView = window.innerWidth <= 768;
        this.isDemoMobileViewport = window.innerWidth <= 767;
        // Close sidebar when switching from mobile to desktop
        if (!this.isMobileView) {
            this.isSidebarOpen = false;
        }
    }

    /**
     * Toggle the mobile sidebar visibility
     */
    toggleMobileSidebar(): void {
        this.isSidebarOpen = !this.isSidebarOpen;

        // Close drawer when opening sidebar on mobile
        if (this.isSidebarOpen && this.isDrawerOpen) {
            this.isDrawerOpen = false;
            this.selectedSection = null;
        }

        // Prevent body scroll when sidebar is open on mobile
        if (this.isSidebarOpen && this.isMobileView) {
            document.body.style.overflow = 'hidden';
        } else if (!this.isDrawerOpen) {
            // Only restore scroll if drawer is also closed
            document.body.style.overflow = '';
        }
    }

    /**
     * Gets visible items for a section based on screen size.
     * Returns fewer items to make room for "View All" card.
     * @param section The supplier section.
     */
    getVisibleItems(section: SupplierSection): SupplierProduct[] {
        const allItems = this.getItemsForActiveSearch(section);
        allItems.forEach(item => {
            if (!item.image_url && item.product_images) {
                item.image_url = item.product_images;
            }
        });
        if (this.isDemoMobileView) {
            return allItems;
        }
        const totalItems = allItems.length;
        const itemsToShow = Math.min(totalItems, this.maxVisibleCards);
        return allItems.slice(0, itemsToShow);
    }

    /**
     * Check if "View All" card should be shown for a section.
     * @param section The supplier section.
     */
    shouldShowViewAll(section: SupplierSection): boolean {
        return this.getActiveTotalItems(section) > this.maxVisibleCards;
    }

    /**
     * Returns remaining item count not visible in the row (for View All label).
     */
    getRemainingItemCount(section: SupplierSection): number {
        const total = this.getActiveTotalItems(section);
        const visible = this.getVisibleItems(section).length;
        return total - visible;
    }

    /**
     * Total items count for active search item in a section.
     * @param section The supplier section.
     */
    getActiveTotalItems(section: SupplierSection): number {
        return this.getItemsForActiveSearch(section).length;
    }

    /**
     * Check if a section has items for the active search.
     * @param section The supplier section.
     */
    sectionHasActiveItems(section: SupplierSection): boolean {
        return this.getItemsForActiveSearch(section).length > 0;
    }

    /**
     * Shows a message indicating all items from a section are being viewed.
     * @param section The supplier section.
     */
    viewAllItems(section: SupplierSection): void {
        // TODO: Show all items modal or expand section
        this.sharedService.successAlert(`Viewing all ${this.getActiveTotalItems(section)} items from ${section.name}`);
    }


    /**
     * Checks if no results were found across any supplier for the active search.
     */
    hasNoResults(): boolean {
        return this.supplierSections.every(s => this.getItemsForActiveSearch(s).length === 0);
    }

    /**
     * Checks if no section is rendered at all for the active search.
     * Hides the global empty state if any section is rendered (even if empty layout).
     */
    hasGlobalNoResults(): boolean {
        return this.supplierSections.every(s =>
            !this.sectionHasActiveItems(s) && (!this.isStandardSection(s.id) || s.id === 'local')
        );
    }

    /**
     * Retrieves the logo URL for a specific supplier from the registry.
     * @param supplierId The supplier identifier.
     */
    getSupplierLogoUrl(supplierId: string): string {
        const supplier = this.supplierRegistry.get(supplierId);
        return supplier ? supplier.logoUrl : '';
    }

    /**
     * Handles cart update events from child components.
     */
    onChildCartUpdate(event: CartUpdateEvent): void {
        const section = this.supplierSections.find(entry => entry.id === event.platform);
        (section?.items || []).forEach(item => this.applyBulkRate(item));
        // Refresh checkout drawer if it's open for this supplier
        if (this.isCheckoutDrawerOpen && this.selectedCheckoutCart?.platform === event.platform) {
            // For Udaan or DeliverIt, update cart items from fresh cartSections data
            if (event.platform === 'udaan' || event.platform === 'deliverit' || event.platform === 'swiggy') {
                const freshCart = this.cartSections.find(c => c.platform === event.platform);
                if (freshCart) {
                    this.selectedCheckoutCart.items = freshCart.items;
                    this.selectedCheckoutCart.totalCount = freshCart.totalCount;
                    this.selectedCheckoutCart.totalValue = freshCart.totalValue;
                }
            }
            this.refreshCheckoutCart();
        }
    }

    /**
     * Handles iframe closed event from Hyperpure child component.
     */
    onIframeClosed(): void {
        // Refresh checkout cart after iframe interaction
        if (this.isCheckoutDrawerOpen) {
            this.refreshCheckoutCart();
        }
    }

    // ── Best Match Popup ──

    /**
     * Opens the Auto Fill Cart popup.
     * For each searched item, uses the API-recommended best match product.
     */
    openBestMatchPopup(): void {

        this.bestMatchCandidates = [];

        this.searchItems.forEach(searchItem => {
            if (searchItem.isLoading) return;

            // Use preferredBestMatch for autofill cart prefill, fallback to bestMatch only if no preferred suppliers are configured
            const autofillItem = this.hasSupplierPreferrence ? searchItem.preferredBestMatch : (searchItem.preferredBestMatch || searchItem.bestMatch);
            if (!autofillItem) return;

            const platform = (autofillItem.platform || '').toLowerCase();
            const targetSectionId = (platform === 'supplier_rate_card') ? 'local' : platform;

            const section = this.supplierSections.find(s => s.id === targetSectionId)
                || (targetSectionId === 'local' ? this.supplierSections.find(s => s.isLocalSupplier) : undefined);

            autofillItem.searchItemName = searchItem.name;

            const skuStr = String(autofillItem.sku_id);
            const alreadyAdded = this.marketBestItemSkus.has(skuStr);

            // Look up selectedQuantity from the actual section item (autofillItem is a
            // separate object and its selectedQuantity is never updated when user uses +/- buttons)
            const sectionItem = section?.items.find(i =>
                i.sku_id === autofillItem.sku_id && i.searchItemName === searchItem.name
            ) || section?.items.find(i => i.sku_id === autofillItem.sku_id);
            const liveItem = sectionItem || autofillItem;
            const existingQty = liveItem.selectedQuantity || autofillItem.selectedQuantity || 0;
            const isInCart = existingQty > 0 && !alreadyAdded;
            const minQty = Number(liveItem.minimumOrderQty || autofillItem.minimumOrderQty) || 1;
            const platformId = section ? section.id : platform;
            const platformLabel = section?.name || platform;

            this.bestMatchCandidates.push({
                searchItemName: searchItem.name,
                platform: platformId,
                platformLabel,
                item: liveItem,
                isSelected: !alreadyAdded && !this.isDemoPreviewSupplier(platformId),
                isAlreadyAdded: alreadyAdded,
                isAlreadyInCart: isInCart,
                existingCartQty: existingQty,
                minimumOrderQty: minQty,
                quantity: alreadyAdded ? (liveItem.selectedQuantity || minQty) : (isInCart ? 1 : minQty)
            });
        });

        if (this.bestMatchCandidates.length === 0) {
            this.sharedService.warningAlert('No items found from your auto-fill suppliers. Wait for all searches to complete.');
            return;
        }

        const pendingItems = this.bestMatchCandidates.filter(c => !c.isAlreadyAdded && !this.isDemoPreviewSupplier(c.platform));
        this.selectAllBestMatches = pendingItems.length > 0 && pendingItems.every(c => c.isSelected);
        this.isBestMatchPopupOpen = true;
        document.body.style.overflow = 'hidden';
    }

    /**
     * Closes the best match popup.
     */
    closeBestMatchPopup(): void {
        this.isBestMatchPopupOpen = false;
        document.body.style.overflow = '';
    }

    /**
     * Toggles selection of all best match candidates.
     */
    toggleSelectAllBestMatches(): void {
        this.selectAllBestMatches = !this.selectAllBestMatches;
        this.bestMatchCandidates.forEach(c => {
            if (!c.isAlreadyAdded && !this.isDemoPreviewSupplier(c.platform)) {
                c.isSelected = this.selectAllBestMatches;
            }
        });
    }

    /**
     * Updates the select-all checkbox state based on individual selections.
     */
    onBestMatchSelectionChange(): void {
        const pending = this.bestMatchCandidates.filter(c => !c.isAlreadyAdded && !this.isDemoPreviewSupplier(c.platform));
        this.selectAllBestMatches = pending.length > 0 && pending.every(c => c.isSelected);
    }

    /**
     * Updates the quantity for a best match candidate, respecting min/max constraints.
     */
    updateBestMatchQty(candidate: BestMatchCandidate, delta: number): void {
        if (this.isDemoPreviewSupplier(candidate.platform)) {
            return;
        }
        const newQty = candidate.quantity + delta;
        const effectiveMinQty = candidate.isAlreadyInCart ? 1 : candidate.minimumOrderQty;
        if (newQty < effectiveMinQty) {
            return;
        }
        const totalQty = candidate.existingCartQty + newQty;
        if (candidate.item.maximumOrderQty && totalQty > candidate.item.maximumOrderQty) {
            this.sharedService.warningAlert(`Maximum order quantity is ${candidate.item.maximumOrderQty}. You already have ${candidate.existingCartQty} in cart.`);
            return;
        }
        candidate.quantity = newQty;
    }

    /**
     * Returns the count of selected best match candidates.
     */
    get selectedBestMatchCount(): number {
        return this.bestMatchCandidates.filter(c => c.isSelected && !c.isAlreadyAdded).length;
    }

    /**
     * Returns the count of items already added as market best.
     */
    get alreadyAddedBestMatchCount(): number {
        return this.bestMatchCandidates.filter(c => c.isAlreadyAdded).length;
    }

    /**
     * Calculates the total value of selected (non-added) best match candidates.
     */
    get bestMatchTotalValue(): number {
        return this.bestMatchCandidates
            .filter(c => c.isSelected && !c.isAlreadyAdded)
            .reduce((sum, c) => sum + (c.item.rate * c.quantity), 0);
    }

    /**
     * Returns the platform logo image path for a given platform id.
     */
    getPlatformLogo(platform: string): string {
        switch (platform) {
            case 'hyperpure': return 'assets/images/hyperpure-logo.png';
            case 'swiggy': return 'assets/images/swiggy-assure.png';
            case 'dmart': return 'assets/images/d-mart-logo.png';
            case 'udaan': return 'assets/images/udaan-logo.png';
            // Local suppliers show their actual name as text so no generic logo is returned
            default: return '';
        }
    }

    /**
     * Adds all selected best match items to their respective carts.
     */
    addAllBestMatches(): void {
        const selected = this.bestMatchCandidates.filter(c => c.isSelected && !c.isAlreadyAdded);
        if (selected.length === 0) {
            this.sharedService.warningAlert('Please select at least one item to add');
            return;
        }

        const previewOnly = selected.filter(c => this.isDemoPreviewSupplier(c.platform));
        const orderable = selected.filter(c => !this.isDemoPreviewSupplier(c.platform));
        if (orderable.length === 0 && previewOnly.length > 0) {
            return;
        }

        this.isAddingBestMatches = true;
        let completedCount = 0;
        let errorCount = 0;

        const onComplete = () => {
            completedCount++;
            if (completedCount === orderable.length) {
                this.isAddingBestMatches = false;
                this.closeBestMatchPopup();

                // Mark as having market best items
                this.hasMarketBestItems = this.marketBestItemSkus.size > 0;
                this.saveMarketBestItems();

                if (errorCount > 0) {
                    this.sharedService.warningAlert(`${orderable.length - errorCount} of ${orderable.length} items added to cart. ${errorCount} failed.`);
                } else {
                    this.sharedService.successAlert(`${orderable.length} Market Best items added to cart!`);
                }
            }
        };

        orderable.forEach(candidate => {
            const item = candidate.item;
            const section = this.supplierSections.find(s => s.id === candidate.platform);
            if (!section) {
                errorCount++;
                onComplete();
                return;
            }

            // Find the actual item reference in the section to update its quantity
            let targetItem = section.items.find(i => i.sku_id === item.sku_id && i.searchItemName === candidate.searchItemName);
            if (!targetItem) {
                targetItem = section.items.find(i => i.sku_id === item.sku_id);
            }
            // For local suppliers, also try to match by item_name if sku_id match failed
            if (!targetItem && section.isLocalSupplier) {
                targetItem = section.items.find(i =>
                    i.searchItemName === candidate.searchItemName &&
                    i.item_name?.toLowerCase() === item.item_name?.toLowerCase()
                );
            }
            if (!targetItem) {
                // Item not yet in section.items (e.g. local supplier best_match sku_id mismatch).
                // Push it into the section so cartSections getter can pick it up.
                targetItem = item;
                targetItem.searchItemName = candidate.searchItemName;
                // Set supplierName so cartSections groups correctly by supplier
                if (!targetItem.supplierName || this.looksLikeId(targetItem.supplierName)) {
                    // Try to resolve from suppliersMap, then use platformLabel as fallback
                    const resolvedName = this.resolveSupplierName(targetItem.supplierId)
                        || this.resolveSupplierName(item.supplierId)
                        || (candidate.platformLabel && !this.looksLikeId(candidate.platformLabel) ? candidate.platformLabel : null)
                        || section.name;
                    targetItem.supplierName = resolvedName;
                }
                section.items.push(targetItem);
                section.totalItems = section.items.length;
            }

            // Record snapshot for undo (avoid duplicates)
            const previousQty = targetItem.selectedQuantity || 0;
            const altSkuId = String(item.sku_id) !== String(targetItem.sku_id) ? String(item.sku_id) : undefined;
            const snapshotExists = this.marketBestItemsSnapshot.some(s => s.skuId === String(targetItem.sku_id));
            if (!snapshotExists) {
                this.marketBestItemsSnapshot.push({
                    platform: candidate.platform,
                    skuId: String(targetItem.sku_id),
                    altSkuId,
                    previousQty: previousQty
                });
            }

            // Track as market best item
            this.marketBestItemSkus.add(String(targetItem.sku_id));
            // For local suppliers matched by item_name fallback, targetItem.sku_id may differ
            // from item.sku_id (searchItem.bestMatch.sku_id). Track both so the popup can detect
            // "already added" regardless of which sku_id is checked.
            if (altSkuId) {
                this.marketBestItemSkus.add(altSkuId);
            }

            // Set the quantity (existing cart qty + additional qty from popup)
            const newQty = previousQty + candidate.quantity;
            targetItem.selectedQuantity = newQty;
            this.applyBulkRate(targetItem);
            targetItem.cartError = undefined;

            // Call appropriate API via child components
            if (candidate.platform === 'hyperpure' && targetItem.sku_id && this.hyperpureSupplier) {
                this.hyperpureSupplier.syncCartLocally(String(targetItem.sku_id));
                targetItem.isAddingToCart = true;

                this.priceDiscoveryService.addToCart(
                    Number(targetItem.sku_id), newQty, 'DELIVERY', this.selectedEntityId
                ).pipe(takeUntil(this.destroy$)).subscribe({
                    next: () => {
                        targetItem!.isAddingToCart = false;
                        onComplete();
                    },
                    error: (error) => {
                        console.error('Failed to add Hyperpure best match:', error);
                        targetItem!.isAddingToCart = false;
                        targetItem!.selectedQuantity = previousQty;
                        this.hyperpureSupplier.syncCartLocally(String(targetItem!.sku_id));
                        this.marketBestItemSkus.delete(String(targetItem!.sku_id));
                        errorCount++;
                        onComplete();
                    }
                });
            } else if (candidate.platform === 'deliverit' && targetItem.sku_id && this.deliveritSupplier) {
                this.deliveritSupplier.syncCartLocally(String(targetItem.sku_id));
                targetItem.isAddingToCart = true;

                this.priceDiscoveryService.updateDeliverItCart(
                    String(targetItem.sku_id), newQty, this.selectedEntityId
                ).subscribe({
                    next: () => {
                        targetItem!.isAddingToCart = false;
                        onComplete();
                    },
                    error: (error) => {
                        console.error('Failed to add DeliverIt best match:', error);
                        targetItem!.isAddingToCart = false;
                        targetItem!.selectedQuantity = previousQty;
                        this.deliveritSupplier.syncCartLocally(String(targetItem!.sku_id));
                        this.marketBestItemSkus.delete(String(targetItem!.sku_id));
                        errorCount++;
                        onComplete();
                    }
                });
            } else if (candidate.platform === 'dmart' && targetItem.sku_id && this.dmartSupplier) {
                this.dmartSupplier.syncCartLocally(String(targetItem.sku_id));
                targetItem.isAddingToCart = true;

                this.priceDiscoveryService.addToDmartCart(
                    String(targetItem.sku_id), newQty, this.selectedEntityId
                ).pipe(takeUntil(this.destroy$)).subscribe({
                    next: () => {
                        targetItem!.isAddingToCart = false;
                        onComplete();
                    },
                    error: (error) => {
                        console.error('Failed to add DMart best match:', error);
                        targetItem!.isAddingToCart = false;
                        targetItem!.selectedQuantity = previousQty;
                        this.dmartSupplier.syncCartLocally(String(targetItem!.sku_id));
                        this.marketBestItemSkus.delete(String(targetItem!.sku_id));
                        errorCount++;
                        onComplete();
                    }
                });
            } else if (candidate.platform === 'swiggy' && targetItem.sku_id && this.swiggyAssureSupplier) {
                targetItem.selectedQuantity = newQty;
                this.swiggyAssureSupplier.syncQuantity(targetItem, previousQty);
                onComplete();
            } else if (candidate.platform === 'udaan' && targetItem.sku_id) {
                // Udaan items are added locally; they sync on checkout
                onComplete();
            } else {
                // Local suppliers or unknown - just update locally
                onComplete();
            }
        });
    }

    /**
     * Removes Market Best Items, restoring their previous quantities.
     * If a platform is specified, only removes items for that platform.
     * If no platform is specified, removes all Market Best Items.
     */
    removeAllMarketBestItems(platform?: string): void {
        const snapshotsToProcess = platform
            ? this.marketBestItemsSnapshot.filter(s => s.platform === platform)
            : [...this.marketBestItemsSnapshot];

        if (snapshotsToProcess.length === 0) {
            this.sharedService.warningAlert('No Market Best items to undo');
            return;
        }

        let completedCount = 0;
        let errorCount = 0;
        const total = snapshotsToProcess.length;

        const onComplete = () => {
            completedCount++;
            if (completedCount === total) {
                // Remove processed snapshots from the master list
                const processedSkuIds = new Set(snapshotsToProcess.map(s => s.skuId));
                this.marketBestItemsSnapshot = this.marketBestItemsSnapshot.filter(
                    s => !processedSkuIds.has(s.skuId)
                );
                this.hasMarketBestItems = this.marketBestItemSkus.size > 0;
                this.saveMarketBestItems();

                // Refresh the checkout cart to remove qty-0 items and recalculate totals
                this.refreshCheckoutCart();

                if (errorCount > 0) {
                    this.sharedService.warningAlert(`Removed ${total - errorCount} of ${total} Market Best items. ${errorCount} failed.`);
                } else {
                    this.sharedService.successAlert(`${total} Market Best item${total > 1 ? 's' : ''} removed successfully!`);
                }
            }
        };

        snapshotsToProcess.forEach(snapshot => {
            const section = this.supplierSections.find(s => s.id === snapshot.platform);
            if (!section) {
                errorCount++;
                onComplete();
                return;
            }

            const targetItem = section.items.find(i => String(i.sku_id) === snapshot.skuId);
            if (!targetItem) {
                this.marketBestItemSkus.delete(snapshot.skuId);
                if (snapshot.altSkuId) this.marketBestItemSkus.delete(snapshot.altSkuId);
                onComplete();
                return;
            }

            // Restore previous quantity
            targetItem.selectedQuantity = snapshot.previousQty;
            this.applyBulkRate(targetItem);
            targetItem.cartError = undefined;

            if (snapshot.platform === 'hyperpure' && targetItem.sku_id && this.hyperpureSupplier) {
                this.hyperpureSupplier.syncCartLocally(String(targetItem.sku_id));
                targetItem.isAddingToCart = true;

                this.priceDiscoveryService.addToCart(
                    Number(targetItem.sku_id), snapshot.previousQty, 'DELIVERY', this.selectedEntityId
                ).pipe(takeUntil(this.destroy$)).subscribe({
                    next: () => {
                        targetItem!.isAddingToCart = false;
                        this.marketBestItemSkus.delete(snapshot.skuId);
                        onComplete();
                    },
                    error: () => {
                        targetItem!.isAddingToCart = false;
                        errorCount++;
                        onComplete();
                    }
                });
            } else if (snapshot.platform === 'deliverit' && targetItem.sku_id && this.deliveritSupplier) {
                this.deliveritSupplier.syncCartLocally(String(targetItem.sku_id));
                targetItem.isAddingToCart = true;

                this.priceDiscoveryService.updateDeliverItCart(
                    String(targetItem.sku_id), snapshot.previousQty, this.selectedEntityId
                ).pipe(takeUntil(this.destroy$)).subscribe({
                    next: () => {
                        targetItem!.isAddingToCart = false;
                        this.marketBestItemSkus.delete(snapshot.skuId);
                        onComplete();
                    },
                    error: () => {
                        targetItem!.isAddingToCart = false;
                        errorCount++;
                        onComplete();
                    }
                });
            } else if (snapshot.platform === 'swiggy' && targetItem.sku_id && this.swiggyAssureSupplier) {
                this.swiggyAssureSupplier.syncQuantity(targetItem, snapshot.previousQty);
                this.marketBestItemSkus.delete(snapshot.skuId);
                onComplete();
            } else if (snapshot.platform === 'dmart' && targetItem.sku_id && this.dmartSupplier) {
                this.dmartSupplier.syncCartLocally(String(targetItem.sku_id));
                targetItem.isAddingToCart = true;

                this.priceDiscoveryService.addToDmartCart(
                    String(targetItem.sku_id), snapshot.previousQty, this.selectedEntityId
                ).subscribe({
                    next: () => {
                        targetItem!.isAddingToCart = false;
                        this.marketBestItemSkus.delete(snapshot.skuId);
                        onComplete();
                    },
                    error: () => {
                        targetItem!.isAddingToCart = false;
                        errorCount++;
                        onComplete();
                    }
                });
            } else {
                this.marketBestItemSkus.delete(snapshot.skuId);
                if (snapshot.altSkuId) this.marketBestItemSkus.delete(snapshot.altSkuId);
                onComplete();
            }
        });
    }

    /**
     * Returns items in the checkout that are tagged as Market Best Items.
     */
    get marketBestItems(): SupplierProduct[] {
        if (!this.selectedCheckoutCart) return [];
        return (this.selectedCheckoutCart.items || []).filter(
            (item: SupplierProduct) =>
                item.selectedQuantity > 0 &&
                this.marketBestItemSkus.has(String(item.sku_id))
        );
    }

    /**
     * Returns checkout cart items excluding Market Best Items.
     * Used in the checkout table to avoid showing duplicates of Market Best items.
     */
    get nonMarketBestCartItems(): SupplierProduct[] {
        if (!this.selectedCheckoutCart) return [];
        return (this.selectedCheckoutCart.items || []).filter(
            (item: SupplierProduct) => !this.marketBestItemSkus.has(String(item.sku_id))
        );
    }

    /**
     * Saves market best item SKUs to localStorage.
     */
    private get marketBestStorageKey(): string {
        return `marketBestItems_${this.selectedEntityId}`;
    }

    private saveMarketBestItems(): void {
        try {
            const data = {
                skus: [...this.marketBestItemSkus],
                snapshot: this.marketBestItemsSnapshot
            };
            localStorage.setItem(this.marketBestStorageKey, JSON.stringify(data));
        } catch (e) {
            console.error('Failed to save market best items:', e);
        }
    }

    /**
     * Loads market best item SKUs from localStorage.
     */
    private loadMarketBestItems(): void {
        try {
            const stored = localStorage.getItem(this.marketBestStorageKey);
            if (stored) {
                const data = JSON.parse(stored);
                this.marketBestItemSkus = new Set(data.skus || []);
                this.marketBestItemsSnapshot = data.snapshot || [];
                this.hasMarketBestItems = this.marketBestItemSkus.size > 0;
            }
        } catch (e) {
            this.marketBestItemSkus = new Set();
            this.marketBestItemsSnapshot = [];
            this.hasMarketBestItems = false;
        }
    }

    /**
     * Clears all market best tracking data.
     */
    private clearMarketBestItems(): void {
        this.marketBestItemSkus = new Set();
        this.marketBestItemsSnapshot = [];
        this.hasMarketBestItems = false;
        this.isAddingBestMatches = false;
        this.isBestMatchPopupOpen = false;
        this.selectAllBestMatches = false;
        this.bestMatchCandidates = [];
        localStorage.removeItem(this.marketBestStorageKey);
    }

    /**
     * Checks if all searches have completed to enable the best match button.
     */
    get allSearchesComplete(): boolean {
        return this.searchItems.length > 0 && this.searchItems.every(item => !item.isLoading);
    }

    /**
     * Returns true if any best match items are found across all searches.
     * Checks both API-recommended best matches and fallback highlighted items.
     */
    get isAnyBestMatchAvailable(): boolean {
        if (!this.allSearchesComplete) return false;

        // Check API reports
        const hasApiBestMatch = this.searchItems.some(item => {
            const best = this.hasSupplierPreferrence ? item.preferredBestMatch : (item.preferredBestMatch || item.bestMatch);
            return best && (best.rate || 0) > 0;
        });
        if (hasApiBestMatch) return true;

        // If preferred suppliers are set, we don't fall back to section best matches
        if (this.hasSupplierPreferrence) return false;

        // Check fallback highlighted items in sections across all search items
        return this.searchItems.some(searchItem =>
            this.supplierSections.some(section =>
                section.items.some(item =>
                    item.searchItemName === searchItem.name && item.isBestMatch && (item.rate || 0) > 0
                )
            )
        );
    }

    // ── Favourite Products ──

    /**
     * Returns filtered favourite products based on the sidebar search term.
     */
    get filteredSidebarFavourites(): PmFavProduct[] {
        if (!this.favSidebarSearchTerm) return this.favProductsList;
        const term = this.favSidebarSearchTerm.toLowerCase();
        return this.favProductsList.filter((fav: PmFavProduct) => {
            const name = fav.productName || '';
            const supplier = fav.supplierDisplayName || fav.supplierName || '';
            return name.toLowerCase().includes(term) || supplier.toLowerCase().includes(term);
        });
    }

    /**
     * Searches for a favourite item from the sidebar by adding it as a new search item.
     * Stays in fav mode (hides market best price banner) and highlights the active fav item.
     */
    searchFavouriteFromSidebar(fav: PmFavProduct): void {
        const name = fav.productName;
        if (!name) return;

        // Enable fav view mode - hides market best price banner
        this.isFavViewMode = true;
        this.activeFavItem = name;

        // Add as search item if not already present
        const exists = this.searchItems.find(item => item.name.toLowerCase() === name.toLowerCase());
        if (!exists) {
            this.newItemSearchText = name;
            this.addNewSearchItem();
        } else {
            this.selectSearchItem(exists);
        }
    }

    /**
     * Loads ALL favourite items via a dedicated backend API.
     * Backend fetches fav items for the entity, groups by supplier (HP, Udaan, Dmart, Deliverit, Local),
     * calls each supplier's search API, and returns consolidated product results.
     * Results are rendered directly in supplier sections without adding to recent searches.
     */
    loadAllFavItems(): void {
        if (!this.hasAuthorizedEntity()) return;
        if (this.isLoadingAllFav) return;

        this.isFavViewMode = true;
        this.isLoadingAllFav = true;
        this.activeFavItem = '';

        // Clear previous fav results from supplier sections
        this.supplierSections.forEach(section => {
            section.items = section.items.filter(item => item.searchItemName !== '__fav__');
            section.totalItems = section.items.length;
        });

        // Set a virtual active search item for fav results display
        this.activeSearchItem = '__fav__';
        this.marketBestPrice = null;

        this.priceDiscoveryService.searchFavouriteItems(this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    this.isLoadingAllFav = false;

                    // Extract the actual response data (API returns { success, response: { ... } })
                    const data = response?.response || response;

                    if (!data) {
                        this.sharedService.warningAlert('No results found for favourite items');
                        return;
                    }

                    // Create a virtual search item to tag results
                    const favSearchItem: SearchItem = { name: '__fav__', isLoading: false };

                    // Process response using existing processResults logic
                    const productImages = data?.product_images || {};
                    this.processResults(data, favSearchItem, productImages);
                },
                (error: any) => {
                    this.isLoadingAllFav = false;
                    console.error('Failed to search favourite items:', error);
                    this.sharedService.errorAlert('Failed to load favourite items. Please try again.');
                }
            );
    }

    /**\n     * Exits fav view mode and restores normal search view with market best price banner.\n     * Cleans up fav-tagged items from supplier sections.\n     */
    exitFavViewMode(): void {
        this.isFavViewMode = false;
        this.isLoadingAllFav = false;
        this.activeFavItem = '';

        // Remove fav-tagged items from supplier sections
        this.supplierSections.forEach(section => {
            section.items = section.items.filter(item => item.searchItemName !== '__fav__');
            section.totalItems = section.items.length;
        });

        // Restore active search item to last real search item
        if (this.searchItems.length > 0) {
            this.activeSearchItem = this.searchItems[this.searchItems.length - 1].name;
            this.refreshMarketBestPriceForActiveSearch();
        } else {
            this.activeSearchItem = '';
            this.marketBestPrice = null;
        }
    }

    /**
     * Removes a favourite product from the sidebar using the existing toggle mechanism.
     */
    removeFavouriteFromSidebar(fav: PmFavProduct): void {
        if (!this.hasAuthorizedEntity()) {
            return;
        }

        const key = buildFavProductKey(fav.supplierName, fav.productSkuId);

        // Optimistic UI removal
        this.favProductKeys.delete(key);
        this.favProductsList = this.favProductsList.filter(
            (f) => buildFavProductKey(f.supplierName, f.productSkuId) !== key
        );

        // If no more favourites, switch back to recent
        if (this.favProductsList.length === 0) {
            this.activeSidebarSection = 'recent';
        }

        const payload: RemoveFavProductPayload = {
            companyId: this.selectedEntityId,
            supplierName: fav.supplierName,
            productSkuId: fav.productSkuId,
            // Pass dmartArticleNumber for D-mart favorites
            ...(fav.supplierName === 'Dmart' ? { dmartArticleNumber: fav.dmartArticleNumber || '' } : {}),
        };

        this.priceDiscoveryService.removeFavouriteProduct(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    console.log(`✅ Removed favourite: ${fav.productName}`);
                },
                (error: any) => {
                    console.error('Failed to remove favourite product:', error);
                    // Rollback
                    this.favProductKeys.add(key);
                    this.loadFavouriteProducts();
                }
            );
    }

    /**
     * Load all favourite products for the current entity from the backend.
     * Populates `favProductKeys` (Set) for O(1) lookup and `favProductsList` (array).
     */
    loadFavouriteProducts(): void {
        if (!this.hasAuthorizedEntity()) return;

        this.isLoadingFavProducts = true;
        this.priceDiscoveryService.getFavouriteProducts(this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    this.isLoadingFavProducts = false;
                    const rawData = response?.data || response || [];

                    if (Array.isArray(rawData)) {
                        // Normalize data to ensure consistent camelCase field names
                        this.favProductsList = rawData.map((item: any) => ({
                            _id: item._id || item.id,
                            companyId: item.companyId || item.company_id,
                            supplierName: item.supplierName || item.supplier_name,
                            supplierDisplayName: item.supplierDisplayName || item.supplier_display_name,
                            productName: item.productName || item.product_name,
                            productSkuId: item.productSkuId || item.product_sku_id,
                            dmartArticleNumber: item.dmartArticleNumber || item.dmart_article_number,
                            supplierId: item.supplierId || item.supplier_id,
                            createdAt: item.createdAt || item.created_at,
                            updatedAt: item.updatedAt || item.updated_at,
                        } as PmFavProduct));
                        this.favProductKeys = new Set(
                            this.favProductsList.map((fav: PmFavProduct) =>
                                buildFavProductKey(fav.supplierName, fav.productSkuId)
                            )
                        );
                        console.log(`✅ Loaded ${this.favProductKeys.size} favourite products`);
                    } else {
                        this.favProductsList = [];
                        this.favProductKeys = new Set();
                    }
                },
                (error: any) => {
                    this.isLoadingFavProducts = false;
                    console.error('Failed to load favourite products:', error);
                }
            );
    }

    /**
     * Check if a specific supplier product is favourited.
     * Uses the composite key (supplierName::productSkuId) for O(1) lookup.
     */
    isProductFavourite(supplierName: string, productSkuId: string | number): boolean {
        if (!productSkuId) return false;
        return this.favProductKeys.has(buildFavProductKey(supplierName, String(productSkuId)));
    }

    /**
     * Resolve the canonical supplier name for the favourite system.
     * Maps section IDs to the enum values used in the DB.
     */
    private resolveSupplierNameForFav(sectionId: string): string {
        const id = (sectionId || '').toLowerCase();
        if (id === 'hyperpure' || id === 'hp') return 'Hyperpure';
        if (id === 'udaan') return 'Udaan';
        if (id === 'dmart') return 'Dmart';
        if (id === 'deliverit') return 'Deliverit';
        return 'Local Supplier';
    }

    /**
     * Resolve a display name for local suppliers.
     * For platform suppliers this is empty; for locals it's the section name.
     */
    private resolveSupplierDisplayName(sectionId: string, section?: SupplierSection): string {
        const canonical = this.resolveSupplierNameForFav(sectionId);
        if (canonical !== 'Local Supplier') return '';
        if (section?.name) return section.name;
        return this.resolveSupplierName(sectionId) || sectionId;
    }

    /**
     * Resolve the SKU stored in favourites for a supplier product.
     * Deliverit must persist sku_id from the price-discovery status response,
     * not product_number.
     */
    private resolveProductSkuIdForFav(supplierName: string, item: SupplierProduct | any): string {
        if (supplierName === 'Hyperpure' && item?.product_number) {
            return String(item.product_number);
        }
        if (supplierName === 'Deliverit') {
            return String(item?.sku_id || item?.skuId || '');
        }
        return String(item?.sku_id || item?.item_name || '');
    }

    /**
     * Helper to determine the favourite state of a product in a specific supplier section.
     * Used in templates: [class.filled]="isSupplierProductFav(section.id, item)"
     */
    isSupplierProductFav(sectionId: string, item: SupplierProduct): boolean {
        const supplierName = this.resolveSupplierNameForFav(sectionId);
        if (supplierName === 'Deliverit') {
            const candidates = [item?.sku_id, item?.sku, item?.product_number]
                .filter(Boolean)
                .map(value => String(value));
            return candidates.some(id => this.favProductKeys.has(buildFavProductKey(supplierName, id)));
        }
        const productId = this.resolveProductSkuIdForFav(supplierName, item);
        if (!productId) return false;
        return this.favProductKeys.has(buildFavProductKey(supplierName, productId));
    }

    /**
     * Toggle favourite status on a supplier product.
     *
     * Heart icon click handler for each product card.
     * Implements optimistic UI update + API call with rollback on failure.
     * Prevents rapid duplicate clicks via `savingFavProduct` set.
     *
     * @param item     The SupplierProduct item
     * @param section  The SupplierSection the item belongs to
     * @param event    Optional DOM event (to stopPropagation)
     */
    toggleProductFavourite(item: SupplierProduct, section: SupplierSection, event?: Event): void {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        if (this.isDemoPreviewSupplier(section?.id || item?.platform)) {
            return;
        }

        if (!this.hasAuthorizedEntity()) {
            this.sharedService.errorAlert('Please select an entity first');
            return;
        }

        const sectionId = section.id || '';
        const supplierName = this.resolveSupplierNameForFav(sectionId);
        const productSkuId = this.resolveProductSkuIdForFav(supplierName, item);
        const productName = item.item_name || '';

        if (!productSkuId) {
            console.warn('Cannot favourite product without a SKU ID');
            return;
        }

        const key = buildFavProductKey(supplierName, productSkuId);

        // Debounce: ignore if a toggle is already in-flight for this key
        if (this.savingFavProduct.has(key)) {
            return;
        }

        const isFav = this.favProductKeys.has(key);

        // ─── Optimistic UI update ───
        if (isFav) {
            this.favProductKeys.delete(key);
            this.favProductsList = this.favProductsList.filter(
                (f) => buildFavProductKey(f.supplierName, f.productSkuId) !== key
            );
        } else {
            this.favProductKeys.add(key);
        }

        this.savingFavProduct.add(key);

        // ─── API call ───
        if (isFav) {
            // Remove
            const payload: RemoveFavProductPayload = {
                companyId: this.selectedEntityId,
                supplierName,
                productSkuId,
                // Pass dmartArticleNumber for D-mart favorites
                ...(supplierName === 'Dmart' ? { dmartArticleNumber: item.article_number || item.dmartArticleNumber || '' } : {}),
            };

            this.priceDiscoveryService.removeFavouriteProduct(payload)
                .pipe(takeUntil(this.destroy$))
                .subscribe(
                    () => {
                        this.savingFavProduct.delete(key);
                        console.log(`✅ Removed favourite product: ${productName} (${supplierName})`);
                    },
                    (error: any) => {
                        this.savingFavProduct.delete(key);
                        console.error('Failed to remove favourite product:', error);
                        // ─── Rollback ───
                        this.favProductKeys.add(key);
                        this.loadFavouriteProducts();
                        this.sharedService.errorAlert('Failed to remove from favourites');
                    }
                );
        } else {
            // Add
            const payload: AddFavProductPayload = {
                companyId: this.selectedEntityId,
                supplierName,
                supplierDisplayName: this.resolveSupplierDisplayName(sectionId, section),
                productName,
                productSkuId,
                // Pass supplierId for Local Supplier to enable rate card lookup
                ...(supplierName === 'Local Supplier' && item.supplierId ? { supplierId: item.supplierId } : {}),
                // Pass dmartArticleNumber for D-mart favorites
                ...(supplierName === 'Dmart' ? { dmartArticleNumber: item.article_number || item.dmartArticleNumber || '' } : {}),
            };

            this.priceDiscoveryService.addFavouriteProduct(payload)
                .pipe(takeUntil(this.destroy$))
                .subscribe(
                    (response: any) => {
                        this.savingFavProduct.delete(key);
                        console.log(`✅ Added favourite product: ${productName} (${supplierName})`);
                        // Normalize the data - use payload values since API may return snake_case
                        const favProduct: PmFavProduct = {
                            _id: response?.data?._id || response?.data?.id,
                            companyId: payload.companyId,
                            supplierName: payload.supplierName as PmFavProduct['supplierName'],
                            supplierDisplayName: payload.supplierDisplayName,
                            productName: payload.productName,
                            productSkuId: payload.productSkuId,
                            dmartArticleNumber: item.article_number || item.dmartArticleNumber,
                            supplierId: payload.supplierId,
                            createdAt: response?.data?.createdAt || response?.data?.created_at,
                            updatedAt: response?.data?.updatedAt || response?.data?.updated_at,
                        };
                        this.favProductsList.push(favProduct);
                    },
                    (error: any) => {
                        this.savingFavProduct.delete(key);
                        console.error('Failed to add favourite product:', error);
                        // ─── Rollback ───
                        this.favProductKeys.delete(key);
                        if (error?.status === 409) {
                            this.loadFavouriteProducts();
                        } else {
                            this.sharedService.errorAlert('Failed to add to favourites');
                        }
                    }
                );
        }
    }

    /**
     * Toggles the Supplier Settings Popup Mode
     */
    toggleSettingsPopup(): void {
        console.log('🔧 [Settings Popup] Toggling. Current State:', this.isSettingsPopupOpen);
        this.isSettingsPopupOpen = !this.isSettingsPopupOpen;
        if (this.isSettingsPopupOpen) {
            this.tempSupplierOrder = [...this.supplierSections];
            console.log('   -> Supplier Order Setup:', this.tempSupplierOrder.map(s => s.name));
        }
    }

    /**
     * Reorders the suppliers array upon drop hook
     */
    onSupplierDrop(event: CdkDragDrop<any[]>): void {
        if (event.previousIndex !== event.currentIndex) {
            moveItemInArray(this.tempSupplierOrder, event.previousIndex, event.currentIndex);
            this.saveSupplierOrder(true);
            console.log("changed");
        }
    }

    /**
     * Saves the current loaded temp sequence setup order to persistent store index
     */
    saveSupplierOrder(isSilent = false): void {
        const supplierPreferrence = this.tempSupplierOrder.map(s => ({ name: s.id, autoFill: true }));

        this.priceDiscoveryService.updatePurchaseManagerConfig(this.selectedEntityId, supplierPreferrence)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    this.supplierSections = [...this.tempSupplierOrder];

                    if (!isSilent) {
                        this.isSettingsPopupOpen = false;
                        this.sharedService.successAlert('Supplier preferences saved successfully!');
                    }
                },
                () => {
                    if (!isSilent) {
                        this.sharedService.errorAlert('Failed to save supplier order');
                    }
                }
            );
    }

    // ── Inventory PR ──

    toggleInventoryPrRow(item: any): void {
        item.expanded = !item.expanded;
    }

    toggleInventoryPrModal(): void {
        this.showInventoryPrModal = !this.showInventoryPrModal;
    }

    addInventoryPrToSearch(item: any): void {
        const name = item.name || item.itemName || item.item_name || '';
        const qty = item.qty || item.quantity || '';
        const unit = item.unit || item.uom || '';
        const searchString = `${name} ${qty}${unit}`.trim();

        if (searchString) {
            if (!this.searchItems.find(i => i.name.toLowerCase() === searchString.toLowerCase())) {
                this.searchItems.push({ name: searchString, isLoading: true });
                this.pendingSearchTags.push(searchString);
            }
        }
    }

    handleInventoryPrSearch(items: any[]): void {
        items.forEach((item: any) => this.addInventoryPrToSearch(item));
        this.showInventoryPrModal = false;
        this.searchPendingItems();
    }

    // ── HP Recent Purchases ──

    /**
     * Toggles the visibility of the HP Recent Purchases modal and loads items if opening.
     */
    toggleHpRecentPurchasesModal(): void {
        this.showHpRecentPurchasesModal = !this.showHpRecentPurchasesModal;
        if (this.showHpRecentPurchasesModal) {
            this.searchHpRecentPurchaseTerm = '';
            this.pmAnalytics.trackRecentPurchaseView(0);
            this.loadHpRecentPurchaseItems();
        }
    }

    /**
     * Loads HP Recent Purchases items (live + missing) for the modal.
     */
    loadHpRecentPurchaseItems(): void {
        if (!this.hasAuthorizedEntity()) {
            this.isHpRecentPurchasesLoading = false;
            return;
        }

        this.isHpRecentPurchasesLoading = true;
        this.priceDiscoveryService.getHpRecentPurchases({ days: 60 }, this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response: any) => {
                    this.hpRecentPurchaseLiveItems = applyHyperpureInclusiveTaxToItems(response?.liveItems || []);
                    this.hpRecentPurchaseMissingItems = applyHyperpureInclusiveTaxToItems(response?.missingItems || []);
                    this.hpRecentPurchaseTotalCount = response?.totalCount || 0;
                    this.hpRecentPurchaseLiveCount = response?.liveCount || 0;
                    this.hpRecentPurchaseMissingCount = response?.missingCount || 0;
                    this.syncHpRecentPurchaseQuantities();
                    this.isHpRecentPurchasesLoading = false;
                },
                (error: any) => {
                    console.error('Failed to load Recent Purchases', error);
                    this.hpRecentPurchaseLiveItems = [];
                    this.hpRecentPurchaseMissingItems = [];
                    this.isHpRecentPurchasesLoading = false;
                }
            );
    }

    /**
     * Syncs the quantities of HP Recent Purchase items with the current Hyperpure cart.
     */
    syncHpRecentPurchaseQuantities(): void {
        if (!this.hyperpureSupplier || !this.hyperpureSupplier.apiCartItems) return;

        // Build a map of sku_id -> quantity from the supplier's cart
        const cartMap = new Map<string, number>();
        this.hyperpureSupplier.apiCartItems.forEach((cartItem: any) => {
            cartMap.set(String(cartItem.sku_id), cartItem.quantity || cartItem.selectedQuantity || 0);
        });

        if (this.hpRecentPurchaseLiveItems) {
            this.hpRecentPurchaseLiveItems.forEach((item: any) => {
                const itemId = String(item.skuId || item.productId || item.product_id || item.sku_id || item.id);
                item.selectedQuantity = cartMap.get(itemId) || 0;
            });
        }
    }

    /**
     * Returns filtered live items based on search term.
     */
    get filteredHpLiveItems(): any[] {
        if (!this.searchHpRecentPurchaseTerm) return this.hpRecentPurchaseLiveItems;
        const term = this.searchHpRecentPurchaseTerm.toLowerCase();
        return this.hpRecentPurchaseLiveItems.filter((item: any) => {
            const name = item.itemName || item.item_name || item.name || '';
            return name.toLowerCase().includes(term);
        });
    }

    /**
     * Returns filtered missing items based on search term.
     */
    get filteredHpMissingItems(): any[] {
        if (!this.searchHpRecentPurchaseTerm) return this.hpRecentPurchaseMissingItems;
        const term = this.searchHpRecentPurchaseTerm.toLowerCase();
        return this.hpRecentPurchaseMissingItems.filter((item: any) => {
            const name = item.itemName || item.item_name || item.name || '';
            return name.toLowerCase().includes(term);
        });
    }

    /**
     * Adds an HP item to the Hyperpure cart.
     */
    addHpToCart(item: any): void {
        if (item.rate) {
            const previousQuantity = item.selectedQuantity || 0;

            // Setup item properties required by HyperpureSupplierComponent
            item.sku_id = item.skuId || item.productId || item.product_id || item.sku_id || item.id;
            item.item_name = item.itemName || item.item_name || item.name;
            item.platform = 'hyperpure';

            if (item.maximumOrderQty && (previousQuantity + 1) > item.maximumOrderQty) {
                this.sharedService.warningAlert(`Maximum order quantity is ${item.maximumOrderQty}`);
                return;
            }

            if (previousQuantity === 0 && item.minimumOrderQty && item.minimumOrderQty > 1) {
                item.selectedQuantity = item.minimumOrderQty;
            } else {
                item.selectedQuantity = previousQuantity + 1;
            }

            item.cartError = undefined;
            if (this.hyperpureSupplier) {
                this.hyperpureSupplier.addToCart(item, 'Hyperpure', previousQuantity);
            }
        }
    }

    /**
     * Updates the quantity of an HP item in the Hyperpure cart.
     */
    updateHpQuantity(item: any, change: number): void {
        const previousQuantity = item.selectedQuantity || 0;

        // Setup item properties required by HyperpureSupplierComponent
        item.sku_id = item.skuId || item.productId || item.product_id || item.sku_id || item.id;
        item.item_name = item.itemName || item.item_name || item.name;
        item.platform = 'hyperpure';

        // Enforce maximum order quantity
        if (change > 0 && item.maximumOrderQty && (previousQuantity + change) > item.maximumOrderQty) {
            this.sharedService.warningAlert(`Maximum order quantity is ${item.maximumOrderQty}`);
            return;
        }

        const minQty = this.resolveMinimumOrderQty(item);
        if (change < 0 && (previousQuantity + change < 1 || (minQty > 1 && previousQuantity <= minQty))) {
            item.selectedQuantity = 0;
        } else {
            item.selectedQuantity = previousQuantity + change;
            if (item.selectedQuantity < 0) item.selectedQuantity = 0;
        }
        this.syncDuplicateCartQuantity(item);

        item.cartError = undefined;
        if (this.hyperpureSupplier) {
            this.hyperpureSupplier.updateQuantity(item, change, previousQuantity);
        }
    }

    /**
     * Navigates to price discovery for a specific HP recent purchase item.
     */
    searchHpRecentPurchaseItem(item: any): void {
        const name = item.item_name || item.itemName || item.name;
        if (name) {
            this.pmAnalytics.trackRecentPurchaseItemSelect(
                name,
                item.sku_id?.toString() || item.product_number?.toString()
            );
            this.showHpRecentPurchasesModal = false;

            // Check if already in list
            const existingItem = this.searchItems.find(
                i => i.name.toLowerCase() === name.toLowerCase()
            );

            if (existingItem) {
                this.selectSearchItem(existingItem);
            } else {
                this.pendingSearchTags.push(name);
                this.searchPendingItems();
            }
        }
    }

}
